// Single source of truth for the /features hub and every /features/[slug] page.
// Edit here; both pages and the sitemap will pick the change up.
//
// TWO ARRAYS, AND THE DIFFERENCE IS THE WHOLE RULE
// ------------------------------------------------
// `ALL_FEATURES` is the catalogue. `FEATURES` is the subset this site — the
// GLOBAL product — is allowed to render, and it is what every page, the
// sitemap and llms-full.txt import. A feature carrying a `market` tag is
// scoped to one jurisdiction's law and is withheld here entirely.
//
// Withheld, not badged. Until 2026-09-05 the tag drove an "India" badge and
// nothing else, so eleven capabilities whose plan keys sit in the backend's
// `INDIA_ONLY_FEATURE_KEYS` (`app/seeds/subscription_plans.py`) were sold on
// the global site, in its sitemap, and in the file answer engines read —
// while every endpoint behind them is mounted `require_india_edition` and
// returns 404 to a global org at any price. A badge does not make an
// unbuyable feature true; it makes it footnoted. This is the same
// truth-in-advertising defect the backend closed as WORKPLAN item 26, and the
// same containment `/library` and `/glossary` already got (§B1 of
// GLOBAL_READINESS_AUDIT_2026-08-31, then a hard 404 in
// `lib/edition/indiaOnlyRoutes.ts`).
//
// The tag is default-deny: `FEATURES` keeps only entries with NO market tag,
// so a jurisdiction-scoped feature added later is excluded the day it lands
// rather than the day someone remembers. Nothing is deleted — the entries are
// correct on the India site, which shares this file in spirit — and restoring
// one is deleting its `market` line.
//
// If you add a page or a data file that lists capabilities, import `FEATURES`.
// `ALL_FEATURES` exists for `generateStaticParams` and for the guard in
// `src/lib/copy/noUnbackedClaims.test.ts`, which fails the build if anything
// under `src/app` or `src/components` imports it.

export type FeatureStatus = "live" | "beta" | "soon";

export type Group =
  | "vio"
  | "nexus"
  | "work"
  | "clients"
  | "litigation"
  | "drafting"
  | "review"
  | "deals"
  | "research"
  | "compliance"
  | "privacy"
  | "tax"
  | "vault"
  | "team"
  | "workflows"
  | "ai-controls"
  | "platform"
  | "free-tools";

export type Feature = {
  slug: string;
  title: string;
  /** One-sentence pitch (hub card + meta description). */
  oneLine: string;
  /** Two-paragraph long description shown on the feature page. */
  long: readonly string[];
  group: Group;
  status: FeatureStatus;
  /**
   * Set only for capabilities scoped to one jurisdiction's law — omit for
   * anything globally applicable.
   *
   * A tag WITHHOLDS the feature from this site: it is dropped from
   * `FEATURES`, so it never reaches the hub, a detail page, the sitemap,
   * llms-full.txt, or any count derived from them. See the header. Tag on
   * evidence, not on vocabulary: the test is whether a global org can buy and
   * call it, which the backend answers in `INDIA_ONLY_FEATURE_KEYS`
   * (`app/seeds/subscription_plans.py`) and `core/nav_manifest.py`.
   */
  market?: "IN";
  /**
   * Plan availability, rendered in the detail page's hero above the sign-up
   * CTA. Set it whenever the feature is not on every published global plan
   * (`global_*` in the backend's `app/seeds/subscription_plans.py`) — a
   * prospect must read it before subscribing, not after.
   */
  planNote?: string;
  /** "How it works" — 3 ordered steps. */
  how: ReadonlyArray<{ title: string; body: string }>;
  /** Bullet list of concrete capabilities. */
  capabilities: ReadonlyArray<string>;
  /** Brief FAQ — keep to 2–3 items. */
  faq: ReadonlyArray<{ q: string; a: string }>;
};

export const GROUPS: Record<Group, { label: string; sub: string; spotlight?: boolean }> = {
  vio: {
    label: "Vio — AI Copilot",
    sub: "Talks to a single document. Drafts, redlines, explains, cites.",
    spotlight: true,
  },
  nexus: {
    label: "Nexus — Institutional Intelligence",
    sub: "Talks across your entire portfolio. Search, rollups, graphs, insights.",
    spotlight: true,
  },
  work: {
    label: "Work — Tasks, Meetings & Matters",
    sub: "What a person decided to do, what was said in the room, and the record it lands on.",
  },
  clients: {
    label: "Clients & Matters",
    sub: "Clients as records, intake, messages, and a portal the client signs in to.",
  },
  litigation: {
    label: "Litigation & Case Intelligence",
    sub: "The matter's facts, issues and contradictions, court orders, bundles and hearings.",
  },
  drafting: {
    label: "Drafting & Redlining",
    sub: "Author, redline, and produce clean output.",
  },
  review: {
    label: "Review & Risk Analysis",
    sub: "Score, flag, and audit every clause.",
  },
  deals: {
    label: "Deals & Procurement",
    sub: "M&A diligence, due-diligence rooms and vendor onboarding — upload-driven.",
  },
  research: {
    label: "Court Research",
    sub: "Case law, citations, and jurisdiction filters.",
  },
  compliance: {
    label: "Compliance & Deadlines",
    sub: "Obligations tracked with evidence, and readiness assessed against your own policies.",
  },
  privacy: {
    label: "Privacy & Data Protection",
    sub: "Run a privacy programme under the data protection laws your organisation is subject to.",
  },
  tax: {
    label: "Tax Intelligence",
    sub: "Income Tax, TDS, GST, IFRS reasoning over your filings.",
  },
  vault: {
    label: "Vault & Document Management",
    sub: "Encrypted storage with sharing, versioning, and permissions.",
  },
  team: {
    label: "Team & Enterprise",
    sub: "Multi-seat collaboration, roles, access policies, reports and audit trails.",
  },
  workflows: {
    label: "Workflow Automation",
    sub: "No-code agents that chain actions across modules.",
  },
  "ai-controls": {
    label: "AI Controls & Guardrails",
    sub: "Your data, your control. AI you can audit and cap.",
  },
  platform: {
    label: "Platform & Deployment",
    sub: "Cloud, private cloud, or air-gapped on-prem.",
  },
  "free-tools": {
    label: "Free Tools",
    sub: "Stand-alone utilities open to anyone.",
  },
};

/**
 * The plan note shared by every feature the backend switches off across the
 * whole published global price list.
 *
 * `app/seeds/subscription_plans.py` builds the `global_starter`, `global_pro`,
 * `global_business` and `global_team` (Enterprise) matrices from Pro's, so a
 * key that is `False` on Pro is `False` on all four — white label, SAML SSO,
 * SCIM, custom agents, firm preferences, org analytics, the Intelligence API,
 * on-prem and custom model training. The organisation model's open-ceiling
 * short-circuit deliberately excludes them (they stay plan-bound), so the
 * endpoints behind them answer 402 on every published plan unless a super
 * admin grants the key to one organisation.
 *
 * Reviewed against the backend seed on 2026-10-08.
 */
const NOT_ON_PLAN =
  "this is not switched on in any of our four published plans — Starter, Pro, " +
  "Business and Enterprise all have it off — so a subscription will not unlock " +
  "it. It has to be enabled for your organisation by arrangement. Ask us before " +
  "you plan around it.";

/**
 * The whole catalogue, jurisdiction-scoped entries included.
 *
 * Do NOT import this to render, list, count or link anything — use `FEATURES`.
 * Its only legitimate consumers are `generateStaticParams` (which needs the
 * withheld slugs so their routes resolve to a deliberate 404 rather than a
 * build error) and the claims guard.
 */
export const ALL_FEATURES: ReadonlyArray<Feature> = [
  // ── Vio (the AI copilot) ───────────────────────────────────────
  {
    slug: "vio-ask",
    title: "Vio — Ask any document",
    oneLine: "Conversational Q&A over a single contract or matter, with the sources it drew on.",
    long: [
      "Vio is LexVio's AI copilot. It sits inside a document and answers questions in plain English — \"what's the liability cap?\", \"is there an arbitration clause?\", \"what's the termination notice?\". Answers come back with the sources Vio drew on, as numbered references you can open.",
      "Vio answers in a thread that remembers what you already asked, and you can put any clause in front of it directly — paste or select the text and ask it to explain, simplify, or redraft just that passage.",
    ],
    group: "vio",
    status: "live",
    how: [
      { title: "1. Open a document", body: "Pull up a contract from the vault or paste fresh text into the scanner. Vio attaches to whatever you're viewing." },
      { title: "2. Ask anything", body: "Type a natural question. Vio retrieves the relevant passages from the document, with the applicable-law context for the contract's governing law, and answers from them." },
      { title: "3. Check the references", body: "Answers carry numbered source references. Where the answer quotes your own document, the citation preview verifies the quotation back to the exact page — see Citation verification." },
    ],
    capabilities: [
      "Numbered source references on answers, with a preview card per reference",
      "Quotations from your own documents can be verified back to the page they came from",
      "Follow-up questions — Vio keeps the thread and its history",
      "Highlight + ask — put any clause in front of Vio and ask it to explain that passage",
      "Simplify mode — restate a clause in plain English while preserving its legal effect",
      "Generate counter-clauses or alternative wordings inline",
      "Four answer personas — senior associate, partner / GC, boutique drafter, plain English",
      "Export a thread to PDF or DOCX as a structured report",
      "Knows the work beside the document — \"what's due today\", \"what did the client agree to\" are answered from your own tasks and meeting minutes through read-only tools, and Vio can open the task list or the meeting for you",
      "Never files a task or edits a meeting itself — a task is a draft you confirm, a follow-up email is a draft you send",
      "An answer keeps writing if you navigate away — come back and it is there. Stop ends it on purpose; a stopped answer offers Continue and a failed one offers Retry",
    ],
    faq: [
      { q: "Does Vio see my other documents?", a: "Vio is scoped to the document (or documents) you attach to the thread. For portfolio-wide queries, use Nexus." },
      { q: "Can Vio create a task or change a meeting for me?", a: "No. Vio can read your tasks and meetings, and it can turn a sentence into a task draft or a follow-up email draft — but the draft is shown to you with a button, and nothing is filed or sent until you press it. Its tools over the work modules are read-only by design." },
      { q: "Can I rely on a citation Vio gives me?", a: "Check it. Every citation can be sent to the citation-verification endpoint, which returns verified, ambiguous, or unresolved. Quotations from your own vault are verified by finding the words in your document. LexVio holds no case-law or statute corpus for the jurisdictions this product serves, so a case or statute citation is never returned as verified — check it at the official source. See the Citation verification page for exactly what each verdict means." },
      { q: "What model powers Vio?", a: "Anthropic's Claude family, called through our hardened wrapper that adds prompt-injection sanitisation, token caps, and per-org cost guardrails. We do not train models on your content — there is no training programme to opt into." },
    ],
  },
  {
    slug: "vio-draft",
    title: "Vio — Draft & redline",
    oneLine: "Generate replacement language and export a redlined Word file.",
    long: [
      "Vio can rewrite a clause for you, argue a harder position, or produce a redlined Word document ready to send. Drafts are grounded in the governing law the contract resolves to — its applicable-law context is supplied to the model with the request — and where no governing law can be decided, in jurisdiction-neutral commercial drafting.",
      "A redline session runs over a whole contract and produces per-clause suggestions with a rationale and a severity. You can also fan a contract out into three variants at once — conservative, balanced, and aggressive — and compare the positions side by side before you pick one.",
    ],
    group: "vio",
    status: "live",
    how: [
      { title: "1. Start a redline session", body: "Point Vio at a contract. It produces per-clause suggestions — original text, suggested text, rationale, severity." },
      { title: "2. Set the posture", body: "Pick a negotiation stance and a goal for the session, or generate all three variants (conservative / balanced / aggressive) and compare." },
      { title: "3. Export", body: "Download a .docx redline, or apply the accepted suggestions to the working version inside LexVio." },
    ],
    capabilities: [
      "Per-clause suggestions with original text, suggested text, rationale, and severity",
      "Three-variant fan-out on one contract — conservative, balanced, aggressive",
      "Negotiation-stance presets and a session goal that steer how hard the draft pushes",
      "Fallback positions drawn from your fallback-clause library are fed into the draft",
      "Reference to the governing law where applicable — check any statute it names at the official source",
      "Word export with native tracked changes when the original upload was a .docx",
    ],
    faq: [
      { q: "Do I get native Word tracked changes?", a: "When the contract was uploaded as a .docx, yes — the export writes real w:ins / w:del markup with author and date, so it opens in Word's own tracked-changes UI. When the source was a PDF or a scan there is no original Word file to mark up, so the export instead renders deletions struck through and insertions in green. It is the same content, but it is styled text rather than Word revisions." },
      { q: "Can I lock the firm style?", a: "Firm Preferences captures your house-style fragment and injects it into contract analysis. It is not switched on in any of our published plans today — talk to us before you count on it." },
      { q: "Where is counter-offer drafting?", a: "It is a separate surface: the Negotiation assistant drafts counter-offers and negotiation emails against a specific position, rather than rewriting a clause in place." },
    ],
  },

  // ── Nexus (institutional intelligence) ────────────────────────
  {
    slug: "nexus-search",
    title: "Nexus — Search across your portfolio",
    oneLine: "Meaning-based search across every document in your vault, plus your playbooks and knowledge base.",
    long: [
      "Nexus is LexVio's portfolio engine. Where Vio talks to one document, Nexus talks across your whole vault. Ask \"which of our MSAs cap liability at twelve months?\" and get ranked passages with snippets, drawn from your contracts, your playbook entries and your knowledge base in one pass.",
      "Contract text is retrieved by meaning — passages are embedded at ingest and matched on semantic distance rather than on the words you happened to type. Playbook and knowledge-base entries are matched on text. Everything is scoped to your organisation.",
    ],
    group: "nexus",
    status: "live",
    how: [
      { title: "1. Type a query", body: "A plain-English question, not a keyword string — retrieval over your contracts runs on meaning, so paraphrases find the clause." },
      { title: "2. Skim the hits", body: "Ranked snippets across contracts, playbooks and knowledge entries, each tagged with its source and clause type where known." },
      { title: "3. Open the source", body: "Click a hit to open the underlying document at the passage it came from." },
    ],
    capabilities: [
      "Semantic retrieval over contract passages, embedded at ingest",
      "Text matching across playbook entries and the knowledge base in the same query",
      "Clause-type tagging on retrieved contract passages",
      "Organisation-scoped — retrieval never crosses tenants",
      "Shared LexVio content is jurisdiction-scoped — your own documents are never filtered, and shared reference content appears only where it is tagged for your jurisdiction",
    ],
    faq: [
      { q: "Is this keyword search?", a: "Not over your contracts. Contract passages are retrieved by semantic similarity against an embedding of your query, which is why a paraphrase still finds the clause — and why an exact-string search is not the right tool here. Playbook and knowledge entries are matched on text. There is no BM25 index and no keyword path over contract bodies." },
      { q: "Does it search inside scanned PDFs?", a: "Yes — they go through OCR at ingest time and become first-class searchable text." },
      { q: "Can I export the result list?", a: "Not today. Nexus returns results to the app; there is no CSV or PDF export of a Nexus result set." },
    ],
  },
  {
    slug: "nexus-ask",
    title: "Nexus — Ask across your portfolio",
    oneLine: "Get a synthesised answer with citations from every document at once.",
    long: [
      "Nexus Ask is Nexus Search plus synthesis. Ask \"what's our standard liability cap across vendor contracts signed in 2026?\" and get a structured answer that aggregates across documents, with citations to each source.",
      "It's the right tool when you want a number, a pattern, or a synthesised summary across many documents — not when you want to find a single document (use Search for that).",
    ],
    group: "nexus",
    status: "live",
    how: [
      { title: "1. Pose a portfolio question", body: "Something that requires reading many documents — \"what's our typical termination notice for MSAs?\"" },
      { title: "2. Nexus gathers", body: "The engine pulls relevant clauses across the vault, deduplicates, and ranks by relevance." },
      { title: "3. Synthesised answer", body: "A structured response with the aggregated finding, counter-examples, and a cited list of the documents it drew from." },
    ],
    capabilities: [
      "Aggregate questions — \"what % of our MSAs cap liability at <12 months?\"",
      "Counterparty rollups — group results by who you signed with",
      "Citation list — every answer links to source documents",
      "Organisation-scoped — retrieval never crosses tenants",
      "Knows the product it lives in — \"what's on my plate today\", \"did the client meeting get minutes\", \"what state is the ABC agreement in\" are answered from your own rows, with a link to the screen",
      "Read-only tools over the work modules — your tasks, your meetings and their minutes, a contract's lifecycle, your blog posts — and never a write",
    ],
    faq: [
      { q: "How is this different from Vio?", a: "Vio reads one document. Nexus Ask reads your whole vault. Pick Vio for clause-level questions on the doc in front of you; Nexus for patterns across many." },
      { q: "Can Nexus file a task or edit a meeting?", a: "No. Its tools over tasks, meetings, the contract lifecycle and the blog are lookups — they read the rows you own and cite them as links. Nothing Nexus does writes to any of those modules." },
      { q: "What happens when the vault does not contain the answer?", a: "Nexus refuses rather than improvising. The answer carries a grounded flag, and where nothing relevant is in your corpus it returns an explicit refusal instead of a synthesised guess." },
      { q: "Can I export the synthesised answer?", a: "Not from Nexus. Vio copilot threads export to PDF or DOCX as a structured report; a Nexus answer is returned to the app only." },
    ],
  },
  {
    slug: "nexus-clause-coverage",
    title: "Nexus — Clause coverage map",
    oneLine: "How many of your contracts carry each clause type, and which ones are missing it.",
    long: [
      "A coverage rollup across your portfolio that answers: \"how many of our contracts have an arbitration clause, and which ones don't?\" For each clause type it returns the number of contracts expected to carry it, the number that do, the number that don't, and a coverage percentage.",
      "Each row also carries a sample of the contract IDs that are missing that clause, so triage starts from a concrete list rather than a number. For the full missing-clause list on one contract, open that contract's own missing-clause view.",
    ],
    group: "nexus",
    status: "live",
    how: [
      { title: "1. Run the coverage query", body: "Nexus reads the clause tags applied to your contracts at ingest and rolls them up by clause type." },
      { title: "2. Read the rollup", body: "Per clause type: expected, present, missing, and coverage percentage across the scope." },
      { title: "3. Open the gaps", body: "Each row carries a sample of the contracts missing that clause. Open one to see its full missing-clause list." },
    ],
    capabilities: [
      "Per-clause-type rollup — expected, present, missing, coverage percentage",
      "A sample of the contracts missing each clause type, to start triage from",
      "Per-contract missing-clause list, with dismiss, on any individual contract",
      "Clause types come from tagging applied automatically at ingest",
    ],
    faq: [
      { q: "Is this a document-by-clause grid?", a: "Not today. It is a one-dimensional rollup per clause type with a sample of the missing contracts — enough to find the gaps, not enough to render a full matrix of every document against every clause." },
      { q: "Does it flag non-standard clauses as well as missing ones?", a: "Coverage has two states: present or missing. Whether a clause that is present is unusual is a different question, answered by clause benchmarking and by the risk report's per-clause verdict." },
      { q: "Can I see coverage change over time?", a: "No. There is no time series on clause coverage. The Legal Health Score is the trend-tracked measure." },
    ],
  },
  {
    slug: "nexus-negotiation-rollup",
    title: "Nexus — Negotiation rollups",
    oneLine: "Which of your redline positions actually get accepted, by clause type and by counterparty.",
    long: [
      "Every redline suggestion you accept, reject or leave pending is a data point about how your positions land. Nexus rolls those decisions up across the portfolio so you can see which clause types you win on, which you concede, and which counterparties push back hardest.",
      "Three views: by clause type, by counterparty, and a top-friction list of the positions that generate the most argument. Useful before a negotiation, to know where you usually end up conceding.",
    ],
    group: "nexus",
    status: "beta",
    how: [
      { title: "1. Redline as usual", body: "Accept and reject suggestions inside redline sessions. Every decision is recorded against its clause type and counterparty." },
      { title: "2. Pick a view", body: "By clause type, by counterparty, or top friction." },
      { title: "3. Read the rates", body: "Accepted, rejected and pending counts with percentages for each row." },
    ],
    capabilities: [
      "Accept / reject / pending counts and rates on your redline positions",
      "Rolled up by clause type, by counterparty, or as a top-friction list",
      "Counterparty memory built automatically from the redline pipeline",
      "Single-clause benchmarking against a market-reference library, separately",
    ],
    faq: [
      { q: "Does it show the distribution of my liability caps?", a: "No. This rolls up redline outcomes — what you accepted and rejected — not the numeric values of your commercial terms. There is no mean, median or percentile view of caps or payment terms across the portfolio." },
      { q: "Does it tell me my walkaway number?", a: "Not from history. A walkaway position can be drafted for a specific contract in the negotiation assistant, from that contract and the BATNA you give it. Nothing derives a threshold from past outcomes." },
    ],
  },
  {
    slug: "nexus-insights",
    title: "Nexus — Insights feed",
    oneLine: "Proactive alerts when something in your portfolio changes or is at risk.",
    long: [
      "Nexus runs background analyses on your portfolio and surfaces findings as an Insights feed. Examples: \"3 contracts expire next 90 days with no renewal review scheduled\", \"audit rights rejected by 4 vendors in 30 days\", \"this counterparty's standard MSA has shifted toward harsher liability over the last 6 months\". A card is written only when it can point at the evidence behind it; with no real signal, the feed stays empty rather than padded.",
      "Each insight carries a severity (info, warn, risk), a confidence label, and the source documents behind it. You can open the sources or dismiss it; there is no snooze.",
    ],
    group: "nexus",
    status: "beta",
    how: [
      { title: "1. Work normally", body: "The scan reads your recent redline sessions, contracts, open flags and counterparties — there is nothing to configure." },
      { title: "2. Background scan", body: "Nexus runs the scan on a schedule. Findings land in the Insights tab as patterns, risks, opportunities, compliance or negotiation cards." },
      { title: "3. Triage", body: "Open the source document, or dismiss the insight as not relevant." },
    ],
    capabilities: [
      "Background scans that run on a schedule and write into the insights feed",
      "Severity ladder — info, warn, risk",
      "Confidence label on every insight — low, medium, high",
      "Per-insight evidence — every finding lists the documents behind it",
      "Dismiss an insight you have dealt with",
      "A separate proactive-flags inbox raises advisory flags from a daily sweep",
    ],
    faq: [
      { q: "Can I snooze an insight instead of dismissing it?", a: "Not yet. Dismiss is the only triage action; there is no snooze and no per-insight task assignment." },
      { q: "Do insights come to me by email or WhatsApp?", a: "No. Insights are written to the feed and read in the app — there is no insight digest on any channel today." },
    ],
  },
  {
    slug: "nexus-graph",
    title: "Nexus — Knowledge graph",
    oneLine: "Two graphs over your portfolio — a clause/counterparty knowledge graph, and the document chain from MSA to SOW to amendment.",
    long: [
      "The legal knowledge graph holds contract, counterparty, matter and clause nodes joined by typed edges — applies_to, pairs_with, references, superseded_by, precedent_for, fallback_for, conflicts_with, triggers_regulation, escalates_to, fails_with_counterparty. You query it one hop at a time: ask for a node's neighbours and get everything it connects to, with the edge type on each.",
      "Alongside it, the contract-relationship layer records the structural chain between documents — parent_of, sow_of, amends, supersedes, related_to — and returns a two-level neighbourhood tree. That is the one to reach for when you need \"what is the contractual chain on this transaction?\"",
    ],
    group: "nexus",
    status: "beta",
    how: [
      { title: "1. Pick a node", body: "A clause, a counterparty, a matter, or a contract." },
      { title: "2. Read its neighbours", body: "Everything connected to it, each edge labelled with its relationship type." },
      { title: "3. Follow the document chain", body: "For structure — MSA to SOW to amendment — use the contract-relationship tree, which returns two levels around the contract you start from." },
    ],
    capabilities: [
      "Contract, counterparty, matter and clause nodes with ten typed edge kinds",
      "One-hop neighbour queries over the knowledge graph",
      "Contract-relationship chain — parent_of, sow_of, amends, supersedes, related_to",
      "Two-level neighbourhood tree around any contract",
      "Knowledge-graph edges populated from redline sessions on a nightly job",
    ],
    faq: [
      { q: "Are the edges extracted automatically when I upload a document?", a: "No. Knowledge-graph edges are populated from completed redline sessions by a nightly job (or on demand for a specific session), and contract relationships are created by you. Uploading a contract does not by itself add it to either graph." },
      { q: "Can I export the graph as an image?", a: "No. Both surfaces return data, not a rendered diagram — there is no PNG or PDF export of a graph view." },
      { q: "Are there \"signed by\" and \"governed by\" edges?", a: "No. Those two edge types do not exist. The ten that do are listed above." },
    ],
  },

  // ── Work — Tasks, Meetings & Matters ──────────────────────────
  // Verified against the backend on 2026-09-18: app/services/meetings.py,
  // app/services/meeting_transcription.py, app/api/v1/meetings/{routes,
  // export,ai}.py, app/workers/main.py (reap_stuck_meetings_task),
  // app/services/todo.py, app/services/task_reminders.py,
  // app/api/v1/todo/{routes,ai}.py, app/db/models/{meeting,task}.py.
  // Both routers are mounted bare on /api/v1 and /mobile/v1 — no edition
  // gate, no plan gate — so no `market` tag here.
  {
    slug: "meeting-intelligence",
    title: "Meeting Intelligence",
    oneLine: "Record or upload a meeting, get a transcript and minutes, turn the action items into tasks — with the speakers labelled, never identified.",
    long: [
      "Record a meeting in the app or upload a recording afterwards — up to 300 MB, about five hours of audio. A live recording goes up in chunks of roughly 45 seconds and each is transcribed as it arrives, so the transcript is on screen while the meeting is still running. Pause and resume are part of the record: the transcript shows where the pause fell and how long it lasted. When you stop, the minutes are generated once from the whole transcript — a summary, the decisions, the discussion, action items, unresolved questions, commitments and next steps.",
      "Speakers are labelled Speaker 1, Speaker 2 and so on. Those labels are inferred from the words by a model, never identified from a voice, and every screen says so; you can put a person's name on a label, and it stays marked as inferred. Action items become tasks in the same task list the rest of the product uses, on your click — nothing is filed silently. Minutes export to PDF or Word, and a meeting linked to a matter writes its record onto that matter's timeline: meeting started, minutes generated, three tasks created.",
    ],
    group: "work",
    status: "live",
    how: [
      { title: "1. Record or upload", body: "Start a recording on the phone or the desk, or upload a file after the fact. A meeting left paused for four hours, or recording with no audio for six, is closed by the system and finalised with what it has." },
      { title: "2. Transcript, then minutes", body: "Chunks are transcribed as they arrive and the screen polls for the next one. On stop, the transcript is labelled by speaker and one model call produces the minutes. A meeting that fails says so, with a reason and a retry — it does not spin." },
      { title: "3. Act on it", body: "Name the inferred speakers, correct the minutes, create tasks from the action items, export to PDF or Word, or ask Vio to draft the follow-up email — which it shows you and never sends." },
    ],
    capabilities: [
      "Record in the app or upload a recording, up to 300 MB (about five hours)",
      "Live transcription in ~45-second chunks, polled onto the screen while the meeting runs",
      "Pause and resume, with the pause and its length drawn into the transcript and noted on the export",
      "Speaker labels — Speaker 1, Speaker 2 — inferred by a model and renameable; never voice identification",
      "Minutes generated once from the transcript: summary, decisions, discussion, action items, unresolved questions, commitments, next steps",
      "Minutes can be corrected by hand; the record then says who edited them and when",
      "Action items filed as tasks in the shared task list, once each, on your click",
      "Export to PDF or Word, dated in your own timezone, with every speaker label marked inferred",
      "Linked to a matter, so the meeting, its minutes, its tasks and its exports appear on the matter timeline",
      "Search a phrase across every transcript and every set of minutes in the organisation",
      "Abandoned recordings closed by the system — four hours paused or six hours silent — and finalised with what was recorded",
      "Transcription runs on OpenAI's speech model, disclosed in the privacy policy; unavailable in an air-gapped deployment, where the Record button is disabled",
    ],
    faq: [
      { q: "Does it identify who is speaking?", a: "No. The transcript is split into turns and a model labels them Speaker 1, Speaker 2 and so on from the words alone — there is no voice identification. You can put a name on a label, and the export still marks that label as inferred, because the name is your assertion rather than the system's." },
      { q: "Can I edit the minutes?", a: "Yes. Each section can be corrected after generation, and the meeting then records who edited it and when — the provenance line reads AI-generated, edited, rather than pretending the model wrote the final text. You can also regenerate the minutes from the transcript, and an action item that already became a task keeps its task through a regeneration." },
      { q: "Does it join my video calls?", a: "No. It records from the device's microphone or takes an uploaded file. There is no calendar integration and no bot that joins a call." },
      { q: "Is the audio spend in my AI usage figure?", a: "No. Transcription is priced per minute of audio, not per token, so it is not in the token log. The record of it is the meeting itself — the recorded duration and the transcription model used." },
    ],
  },
  {
    slug: "tasks",
    title: "Tasks",
    oneLine: "One task list for the whole organisation — due dates in your own timezone, matter and contract links, snooze, reminders, and tasks the system files for you.",
    long: [
      "A task is what a person decided to do by a date. There is one task list across the organisation, and it is the same list everywhere: the Todo screen, the matter page, the phone's home screen and Vio all read it. Each task carries a title, an assignee, a priority (high, medium, low), a category, a due date that is either all-day or timed, and links to the matter, contract or obligation it belongs to. Statuses are open, in progress, done and cancelled. Snooze hides a task until a time you pick; it never moves the due date.",
      "\"Today\" is the caller's day, not the server's: an all-day task is compared by date in your own timezone, a timed task by the instant, and the same rule decides the dashboard counters, the phone's home screen and the reminders. Each morning at 08:00 in your zone you get one digest of the tasks due that day, and an overdue task nudges you once. Some tasks the system files on its own — a workflow step, an approval step, a contract's non-renewal notice deadline — and it files each one once, however many times the sweep runs.",
    ],
    group: "work",
    status: "live",
    how: [
      { title: "1. Add a task — or say it", body: "Type it, or tell Vio \"remind me to review the ABC agreement tomorrow morning\". Vio returns a draft with the assignee and matter resolved to real records; you confirm it. A name that matches two people comes back as a choice, and a matter is never guessed." },
      { title: "2. Work the list", body: "Views for my tasks, the team, a matter, a contract or a category; filters for overdue, today and upcoming. Complete, reopen, reassign, snooze until later today, tomorrow or next week, add a note to the thread." },
      { title: "3. Be reminded", body: "A digest at 08:00 in your timezone and an overdue nudge, on the notification channels you have switched on. Change the due date and the reminders re-arm." },
    ],
    capabilities: [
      "One list across the organisation, read by the desk, the phone, the matter page and Vio",
      "Due date all-day or timed, assignee, priority, category, and links to a matter, contract or obligation",
      "\"Today\" resolved in the user's own timezone — the same rule for the list, the counters and the reminders",
      "Statuses: open, in progress, done, cancelled. Snoozed is not a status — it hides the task without moving the date",
      "Daily 08:00 digest and an overdue nudge, sent once per task and due date",
      "System-filed tasks for a workflow step, an approval step or a contract's notice deadline — each filed once",
      "Vio turns a sentence into a task draft you confirm; the assignee and matter are resolved server-side and a matter is never guessed",
      "An append-only note thread under each task",
      "Every change is an audit row scoped to the task's matter, which is what puts it on the matter timeline",
      "The matter page shows the same list filtered by matter — there is no second, matter-side list",
    ],
    faq: [
      { q: "Is a task the same as an obligation?", a: "No. An obligation is what a contract binds the organisation to — it is extracted from the document and lives in the obligation register with its own reminders and evidence. A task is what a person decided to do. A task can point at an obligation, and the notice-deadline task the system files points at its contract, but completing the task does not discharge the obligation and the register is not rebuilt from tasks." },
      { q: "Does Vio create tasks on its own?", a: "No. Vio returns a draft — title, date, assignee and matter where it could resolve them, candidates where it could not — and nothing is written until you confirm. The confirmed task records that it came from Vio, with the model and prompt version that produced the draft." },
      { q: "Can I share a task list with a client, or sync it to a calendar?", a: "No. Tasks are internal to the organisation; there is no external share and no calendar sync." },
    ],
  },

  {
    // Added 2026-10-08. Verified against g-backend: services/command_center.py
    // (one statement per section; the reg-change section is absent for a
    // global org because its router is India-only), services/pending_actions.py,
    // api/v1/work.py, api/v1/command_center.py — mounted bare, no plan gate.
    slug: "command-center",
    title: "Command Center & My Pending Actions",
    oneLine: "One screen for what needs you: tasks, deadlines, hearings, approvals, drafts awaiting review, client activity and AI jobs in flight.",
    long: [
      "The Command Center gathers the organisation's live work into short sections: your tasks and obligations due, non-renewal notice deadlines and hearings in the next fourteen days, approvals waiting on your role, drafts awaiting review, what clients did in the portal, open evidence requests, contradictions to review, court orders, contracts needing action, renewals, open matters and AI jobs still running. Each item links to the page where you act on it.",
      "My Pending Actions is the same view narrowed to you: approvals and workflow gates your role may decide, workflow steps assigned to you, and the contract requests and intake requests you own. Both use the task list's own rule for today and overdue, in your timezone, so a count here agrees with the list it links to.",
    ],
    group: "work",
    status: "live",
    how: [
      { title: "1. Open it", body: "Command Center sits with the dashboard on the desk; My Pending Actions sits beside your tasks." },
      { title: "2. Read the sections", body: "Each section shows a capped list and says when there is more, rather than running a second count." },
      { title: "3. Click through", body: "Every item links to the task, draft, hearing, approval or request it describes." },
    ],
    capabilities: [
      "Sections from tasks due to AI jobs in flight, each one query and each capped",
      "Hearings, tasks, obligations and notice deadlines in the next 14 days",
      "Approvals and workflow gates the caller's role may decide",
      "Drafts awaiting review, client portal activity, open evidence requests and contradictions",
      "\"Today\" and \"overdue\" computed in the user's own timezone, with the same rule as the task list",
      "My Pending Actions: steps assigned to you, and the contract and intake requests you own",
      "The same sections read by the phone's home screen",
    ],
    faq: [
      { q: "Is this a separate task list?", a: "No. It reads the existing tasks, approvals, drafts and requests; nothing is stored for it, so there is nothing to keep in step." },
      { q: "Is it on every plan?", a: "Yes. It carries no plan gate; what it shows depends on which modules your organisation uses." },
    ],
  },
  {
    // Added 2026-10-08. Verified: services/messaging.py, api/v1/messages.py,
    // api/client_portal_messages.py, services/communications.py (migration 239).
    slug: "messages",
    title: "Messages & communication history",
    oneLine: "Internal threads for the team, client threads the client reads in the portal, and one history across the channels the product records.",
    long: [
      "Messages has two kinds of thread. An internal thread belongs to its members and nobody else can open it. A client thread is the firm's correspondence with a client: every user in the organisation can read it, and the client's contact reaches it through the client portal while a live share covers that client and matter. Revoke the share and the thread closes on the contact's next request.",
      "Messages are text, written by people — nothing is sent by the AI. Communication History is a single newest-first view across messages, in-app notifications, meetings, portal activity and the other channels the product records, filterable by matter or client, and it says which channels a filter could not reach. A notification shows as delivered or read, never as \"sent\" when it was only scheduled.",
    ],
    group: "clients",
    status: "live",
    how: [
      { title: "1. Start a thread", body: "Internal with named colleagues, or a client thread tied to a client and, optionally, a matter." },
      { title: "2. Reply", body: "New messages notify the other members in-app. A client contact sees the thread at their next portal sign-in — they are not emailed." },
      { title: "3. Read the history", body: "Communication History merges the recorded channels for a matter or a client, newest first." },
    ],
    capabilities: [
      "Internal threads visible to their members only",
      "Client threads readable by the whole firm and by the client's contact through the portal",
      "Portal access follows the live client share — a revoked share closes the thread",
      "Text only; never written or sent by AI",
      "Audit rows name ids and counts, never a subject or a body, and land on the matter timeline",
      "Communication History across messages, notifications, meetings and portal events",
    ],
    faq: [
      { q: "Is the client emailed when I post a message?", a: "No. The client sees the thread when they next sign in to the portal." },
      { q: "Can I attach a file?", a: "No — messages are text. A client sends a document through the portal's evidence request instead." },
    ],
  },

  // ── Clients & Matters ─────────────────────────────────────────
  {
    // Added 2026-10-08. Verified: services/clients.py, core/client_names.py,
    // db/models/client.py (migration 224).
    slug: "clients",
    title: "Clients & contacts",
    oneLine: "A client is a record with contacts, not a name typed on a matter — and duplicates merge without losing anything.",
    long: [
      "Every matter can point at a client record, and each client holds its contacts. When a matter is created with only a client name, it is linked to the existing client of that name, or a new one is made. The duplicate check ignores legal-form suffixes, so \"Acme Corp Ltd\" and \"Acme Corp\" are recognised as one client.",
      "Merging two clients never deletes one: the merged record keeps its id and points at the survivor, and every lookup follows that pointer. Matters, documents, tasks, workflow runs and saved views can all be filtered by client. A contact flagged for portal access is the one a share or a client message can reach.",
    ],
    group: "clients",
    status: "live",
    how: [
      { title: "1. Add a client", body: "From the client directory, or let a new matter create one from its client name." },
      { title: "2. Add contacts", body: "Name, email and whether the contact may use the client portal." },
      { title: "3. Merge duplicates", body: "Merge two records; the duplicate points at the survivor and nothing is deleted." },
    ],
    capabilities: [
      "Client records with contacts, linked from matters",
      "Duplicate detection that ignores legal-form suffixes",
      "Merge without deletion — the old record forwards to the survivor",
      "Filter matters, the vault, tasks, workflow runs and saved views by client",
      "Portal access set per contact",
    ],
    faq: [
      { q: "What happens to matters on a merged client?", a: "They resolve to the surviving client, because every lookup follows the merge pointer." },
      { q: "Does every contact get portal access?", a: "No. Portal access is a switch on the contact, and a share can only go to a contact who has it." },
    ],
  },
  {
    // Added 2026-10-08. Verified: api/client_portal.py, api/client_portal_full.py,
    // services/client_portal.py (migrations 225, 230, 232).
    slug: "client-portal",
    title: "Client portal",
    oneLine: "Share a matter, a workflow, a document or a draft with the client; they sign in with a one-time link, upload evidence, answer questions and approve drafts.",
    long: [
      "A share gives one client contact a defined scope on one thing — a matter, a workflow run, a contract, a case analysis or a bundle. The contact signs in with a magic link sent to the email address on file, valid for fifteen minutes and usable once; the landing link alone is not a credential. Every portal request re-reads the shares, so revoking one takes effect immediately.",
      "Inside, the client sees progress as labels, statuses and due dates — never internal notes or who is assigned. They can upload documents against an evidence request, answer the questions a workflow asks of them, and approve or ask for changes on a draft you sent them, with the decision recorded against the exact text they were shown. An answer from the client is an unverified fact until a lawyer accepts it, and internal documents such as a hearing brief can never be sent.",
    ],
    group: "clients",
    status: "live",
    how: [
      { title: "1. Share", body: "Pick the contact and what they may do: view progress, upload, answer questions, approve a draft." },
      { title: "2. Client signs in", body: "A one-use, fifteen-minute link to the contact's email address on file opens a portal session." },
      { title: "3. Accept what comes back", body: "Uploads land on the matter as evidence; answers wait for a person to accept them before any due date changes." },
    ],
    capabilities: [
      "Scoped shares of a matter, workflow run, contract, case analysis or bundle",
      "Magic-link sign-in to the contact's address on file — fifteen minutes, one use",
      "Revocation takes effect on the next request; moving a matter to another client revokes its shares",
      "Client uploads stored as evidence on the matter",
      "Client answers recorded as unverified facts until a lawyer accepts them",
      "Draft approval recorded against the text the client was shown; a changed draft must be re-sent",
      "Progress shown without internal notes, assignees or AI-written explanations",
      "Every portal read and write audited",
    ],
    faq: [
      { q: "Can a client see everything on the matter?", a: "No. They see what the share's scope allows — the documents shared, the progress of shared work, the drafts sent to them. Internal workflows such as hearing preparation are never shared." },
      { q: "Is the downloaded file watermarked?", a: "Not in the portal yet. Downloads use short-lived links." },
    ],
  },
  {
    // Added 2026-10-08. Verified: services/intake_forms.py, api/v1/intake_forms.py
    // (migration 240), services/legal_intake.py::capture.
    slug: "intake-forms",
    title: "Intake forms",
    oneLine: "Build an intake form, publish it on a public link, and every submission becomes a triaged intake request.",
    long: [
      "Design a form with the questions you need and publish it. A public form gets a link built on a random token, not your organisation's name; unpublishing closes it and re-publishing brings the same link back. An internal form is filled by signed-in colleagues and has no public door.",
      "Every submission goes through the same capture path as a typed intake request — triaged, audited and queued — and is marked internal or external depending on who filed it. Answers are stored by field key, checked against the form as it stands, and unknown fields are dropped.",
    ],
    group: "clients",
    status: "live",
    how: [
      { title: "1. Build the form", body: "Add questions; a field's key never changes once given." },
      { title: "2. Publish", body: "Share the public link, or keep the form internal." },
      { title: "3. Work the submissions", body: "Each submission arrives as an intake request, triaged and ready to route or promote into a matter." },
    ],
    capabilities: [
      "Public forms on a token link, rate-limited for anonymous callers",
      "Internal forms for colleagues",
      "Submissions captured as intake requests with triage",
      "Internal / external requester marked on every request",
      "Answers validated against the current form; unknown keys dropped",
    ],
    faq: [
      { q: "Can a form collect files?", a: "No. An anonymous upload would be a document with nobody behind it. A client who must send a file uses the portal's evidence request." },
      { q: "Does the link reveal my firm's name?", a: "The link is built on a random token rather than your organisation's name." },
    ],
  },
  {
    // Added 2026-10-08. Verified: services/legal_intake.py (capture, triage,
    // question_for / next_question, POST /intake/{id}/answers).
    slug: "legal-intake",
    title: "Legal intake",
    oneLine: "A front door for legal requests — captured, triaged by AI, routed to a person, and promoted into a matter.",
    long: [
      "Requests come in from the business through a single intake rather than by email and corridor conversation. Each one is triaged on arrival: what kind of work it is, how urgent, whether it is legal work at all. The triage recommends; a person applies. A request triaged as not-legal is recorded as rejected with a visible rationale rather than silently dropped, so a human can reverse it.",
      "Promotion is what makes this more than a ticket queue: a request becomes a matter, and the matter is where the documents, redlines and obligations for that piece of work then live. When a request is missing details, triage asks for them one question at a time, and each answer re-runs the triage.",
    ],
    group: "clients",
    status: "live",
    how: [
      { title: "1. Capture the request", body: "Typed in, or filed through an intake form — recorded with its channel and triaged on arrival." },
      { title: "2. Route it", body: "Assign to a person. Answer the missing-detail questions and the triage re-runs." },
      { title: "3. Promote or close", body: "Promote it into a matter, or close it with a reason. Either way there is a record." },
    ],
    capabilities: [
      "AI triage on arrival — type, urgency, and whether it is legal work",
      "Triage recommends, a human applies",
      "Missing details asked one question at a time; each answer re-runs triage",
      "Rejection recorded with a rationale rather than a deletion",
      "Internal and external requests told apart, with a view of colleagues' requests",
      "Promotion into a matter, which becomes the container for the work",
    ],
    faq: [
      { q: "Does the triage decide on its own?", a: "No. It records a recommendation and a rationale; a person applies it, and can reverse it." },
      { q: "Can people outside the firm submit a request?", a: "Yes, through an intake form published on a public link. Every submission becomes an intake request marked external. See Intake forms." },
    ],
  },

  // ── Litigation & Case Intelligence ────────────────────────────
  // Added 2026-10-08. Every template here is in workflow_catalogue/litigation.py,
  // which is edition-neutral. The dispute templates (disputes.py — legal
  // notice, cheque bounce, consumer complaint and five more) are all
  // `edition="india"`, absent from a global org's catalogue, and deliberately
  // NOT described on this site.
  {
    slug: "case-intelligence-studio",
    title: "Case Intelligence Studio",
    oneLine: "Read the matter's documents once into verified facts, then get issues, claims, contradictions, evidence gaps and a chronology — every row yours to edit.",
    long: [
      "Each case document is read once into facts. A fact taken from a document counts as verified only when its quote is actually found in the text, and it carries the page it came from; a quote that is not there is dropped and counted. A WhatsApp chat export becomes one fact per message with no model call. A model's inference is labelled as inference, and a date never gains a day the document did not state.",
      "A case analysis is a version, not a document: each run reads the verified facts and proposes issues, claims, contradictions between facts, and evidence gaps, with a diff against the last version. A person owns those rows — an edited row is never overwritten, a row the next run no longer mentions is flagged stale rather than deleted, and a contradiction you reviewed keeps its verdict. The chronology, claims matrix and evidence centre are views over the same facts. When new evidence arrives on a matter with an analysis, a fresh version is scheduled and tells you what changed.",
    ],
    group: "litigation",
    status: "live",
    how: [
      { title: "1. Turn the matter on", body: "Documents are read on arrival only for matters where case analysis is switched on — new litigation, arbitration and investigation matters default to on, because reading costs model calls." },
      { title: "2. Run an analysis", body: "One run reads the verified facts and proposes issues, claims, contradictions and gaps. Anything it cites that is not a verified fact of the matter is dropped." },
      { title: "3. Review and ask", body: "Edit or withdraw rows, mark contradictions reviewed, and send an evidence request to the client through the portal." },
    ],
    capabilities: [
      "Facts extracted once per document text, with verified quotes and page references",
      "WhatsApp chat exports and .eml headers parsed deterministically",
      "Case analysis versions with a diff: issues, claims, contradictions, evidence gaps",
      "Person-owned rows — edits are never overwritten, stale rows are flagged, not deleted",
      "Chronology (month-precision dates stay months; undated facts listed separately), claims matrix and evidence centre",
      "Evidence requests to the client, with suggested answers a person accepts or dismisses",
      "Re-analysis scheduled automatically when evidence arrives on an analysed matter",
      "Identity documents are never read",
    ],
    faq: [
      { q: "Does it tell me who will win?", a: "No. It organises facts, issues and gaps. There is no outcome prediction in the case analysis." },
      { q: "Can the AI overwrite my edits on the next run?", a: "No. A row a person edited is never overwritten by a later run." },
    ],
  },
  {
    slug: "court-order-analysis",
    title: "Court order analysis & hearing preparation",
    oneLine: "Read a court order into its directions and next hearing; deadlines are computed only from dates the order states, otherwise marked \"requires review\".",
    long: [
      "Run order analysis on a court order or pleading and it lists the directions, the next hearing and the dates in it. No deadline comes from the model. A date written in the order is parsed from the verified quote by code, and a numeric date that could be read day-first or month-first is not guessed. A period such as \"within four weeks\" is computed only from an anchor the order itself states — its own date or a named date. Anything else — from service, from receipt, \"forthwith\" — is marked \"Deadline requires review\".",
      "A direction becomes a task only when a person confirms it, and the next hearing is added to the matter's hearings on confirm. Hearing preparation is a workflow bound to that hearing: it can generate a hearing brief over the matter's facts, with each sentence linked to its support, and compile the documents since the last hearing. The brief states no outcome and invents no authority.",
    ],
    group: "litigation",
    status: "live",
    how: [
      { title: "1. Upload the order", body: "Store it on the matter as a court order and ask for order analysis." },
      { title: "2. Check the directions", body: "Each direction shows its quote and either a computed deadline with its basis or \"requires review\"." },
      { title: "3. Confirm", body: "Turn a direction into a task and record the next hearing. Nothing is filed until you confirm." },
    ],
    capabilities: [
      "Directions, next hearing and dates extracted from court orders and pleadings",
      "Deadlines computed by code from anchors the order states, with calendar counting",
      "\"Requires review\" for any period with no stated anchor, and for a date that cannot be read one way only",
      "Direction → task on confirmation, with no due date when under review",
      "Next hearing added to the matter on confirm",
      "Hearing preparation workflow and a hearing brief with sentence-level support",
    ],
    faq: [
      { q: "Will it compute a limitation period?", a: "Not for a period it cannot anchor to a date the order states. Those are flagged for a person to decide." },
      { q: "Does the hearing brief predict the outcome?", a: "No. It sets out the file; it states no outcome and invents no authority." },
    ],
  },
  {
    slug: "matter-journey",
    title: "Matter Journey & filing bundle",
    oneLine: "A matter's stages from opening the file to the bundle, finished by the work itself — and a filing bundle PDF stamped with annexure numbers.",
    long: [
      "The Matter Journey is a workflow with stages in order: open the file, read it, understand it, review contradictions, triage gaps, ask the client, research, draft, client review, lawyer review, bundle and follow-up. A stage finishes when its condition holds — the last contradiction reviewed, the last document read — so the journey reflects the work rather than ticks. Stages that do not apply, such as asking the client when nothing was asked, are skipped.",
      "The filing bundle is built from the matter: evidence, correspondence and court orders by default, in the order you set or by the document's own date. Missing required items stop the build unless you force it, which marks them \"[to be annexed]\". The PDF stamps \"ANNEXURE A-n\" and \"Page n of N\".",
    ],
    group: "litigation",
    status: "live",
    how: [
      { title: "1. Start the journey", body: "One journey per matter; it opens at the first stage." },
      { title: "2. Do the work", body: "Stages finish on their own as the reading, review, drafting and approvals happen." },
      { title: "3. Export the bundle", body: "Order the documents, check what is missing, and export the stamped PDF." },
    ],
    capabilities: [
      "Twelve ordered stages, each finished by its condition",
      "Stages that do not apply are skipped, with the reason",
      "Research stage offers one research step per accepted question, finished when an authority is linked to its issue",
      "Matter bundle in a set order or by the document's own date",
      "Annexure stamps and page numbers on the PDF",
      "Missing items stop the build unless forced",
    ],
    faq: [
      { q: "Can I tick a stage done by hand?", a: "Stages finish when their condition holds; client steps can only be completed for the client with an override and a note." },
      { q: "Are text documents included in the bundle?", a: "Yes, rendered as text annexures in a Latin-script font." },
    ],
  },
  {
    slug: "fact-tagged-drafting",
    title: "Drafting from the matter",
    oneLine: "Draft from the matter's verified facts, with every factual sentence linked to its source, a deterministic check, and a lawyer → client → approved review chain.",
    long: [
      "The drafting context is a list of the matter's verified facts, issues, claims, evidence, orders and research links. Untick what should not be used; nothing unticked and nothing unverified reaches the model. Each factual sentence in the draft is linked to the facts it rests on, and what the matter lacks appears as a bracketed placeholder, not a guess.",
      "The check is code, not a model: near-miss party names, case numbers, dates, amounts, addresses and courts not in the facts, sentences with no support, citations, annexures against the bundle, and relief with no claim. The review chain runs from AI-generated to checked, lawyer review, client review, approved and final. Approval records who, when and the text's fingerprint, and an edit after approval must be re-approved. Client review happens in the client portal.",
    ],
    group: "litigation",
    status: "live",
    how: [
      { title: "1. Choose the context", body: "Untick facts or documents you do not want used." },
      { title: "2. Generate and check", body: "Every factual sentence is linked to its facts; run the check against the current text." },
      { title: "3. Review", body: "Submit to lawyer review, send to the client if required, approve and finalise." },
    ],
    capabilities: [
      "Only verified or confirmed facts reach the prompt",
      "Sentence-level support links to facts",
      "Placeholders for what the matter lacks",
      "Deterministic draft check — names, dates, amounts, courts, citations, annexures, relief",
      "Review states: AI-generated, checked, lawyer review, client review, approved, final",
      "Approval tied to the text; an edit after it requires re-approval",
      "Organisations can require client review before approval",
    ],
    faq: [
      { q: "Can a draft be approved without the check?", a: "No. Submitting needs a check of the current text." },
      { q: "Does the client's decision approve the draft?", a: "No. The client approves or asks for changes; the lawyer still approves." },
    ],
  },
  {
    // research_links on a global org: `authority` links (a citation a person
    // types) only. Judgment / statute links are 422 `corpus_not_available`,
    // and find / current-law answer `available: false` — there is no corpus
    // on this product. Corpus treatment edges never reach a global org's graph.
    slug: "research-links",
    title: "Research links & the matter graph",
    oneLine: "Link the authorities you rely on to the matter's issues and claims, with a stance — and see the whole matter as a graph built from its own records.",
    long: [
      "A research link ties an authority you enter — a case, a statute section or another citation, typed with its reference and, if you like, a link to its source — to an issue, claim, defence, clause, draft or workflow step, with a stance: supports, contrary or context. Every link starts unverified, and nothing about it is invented for you. This product holds no case-law or statute corpus, so authority search and current-law notes are not offered; the authorities are the ones you and your team bring.",
      "The matter graph is computed from the matter's rows — client, parties, documents, facts, issues, claims, orders, directions, obligations, tasks and the authorities you linked — and a model never writes an edge. Every edge names the row it came from, and you can ask for the path between any two nodes.",
    ],
    group: "litigation",
    status: "live",
    how: [
      { title: "1. Link", body: "Type the authority and attach it to an issue or claim with a stance." },
      { title: "2. Use it", body: "Linked authorities appear in the drafting context, and linking one to an issue finishes that issue's research step on the Matter Journey." },
      { title: "3. Explore", body: "Open the matter graph, or ask for the path between two nodes." },
    ],
    capabilities: [
      "Typed authorities linked to issues, claims, defences, clauses, drafts and workflow steps",
      "Stances: supports, contrary, context",
      "Every link starts unverified — nothing is presented as checked that was not",
      "Source links accepted as http(s) only",
      "Matter graph built from rows, never from model output, each edge naming its source row",
      "Path between two nodes, scoped to your organisation",
    ],
    faq: [
      { q: "Can LexVio find authorities for me?", a: "Not on this product. There is no case-law or statute corpus here to search, and we would rather say so than return a near-match dressed as research. Typed authorities work on every plan." },
      { q: "Are my colleagues' research notes on the graph?", a: "Only what is linked to the matter. Research a colleague keeps privately shows as private, with no link to it." },
    ],
  },

  // ── Drafting & Redlining ──────────────────────────────────────
  {
    slug: "fix-suggestions",
    title: "Fix suggestions",
    oneLine: "AI-generated replacement language for every flagged clause.",
    long: [
      "When Vio flags a risky clause it doesn't just complain — it proposes a specific replacement. The new language is grounded in the contract's governing law where one can be decided, and is one click away from being applied.",
      "Suggestions are tuned to your firm's style if you've captured one. Otherwise they default to clear, modern commercial drafting.",
    ],
    group: "drafting",
    status: "live",
    how: [
      { title: "1. Risk surfaces", body: "Vio flags a clause as red or amber during scan." },
      { title: "2. Tap to see the fix", body: "Inline panel shows the proposed replacement plus the reasoning." },
      { title: "3. Apply or send", body: "One click to apply to the working document. Or send to a colleague for review first." },
    ],
    capabilities: [
      "Reasoning shown next to every fix",
      "Apply, modify, or reject — your control",
      "Suggestions carry a severity, so you can triage the ones that matter",
      "Fallback positions from your clause ladder feed the draft where you have one",
    ],
    faq: [
      { q: "Are fixes guaranteed to be enforceable?", a: "No — fix suggestions are a starting point. For high-stakes deals, have an advocate review the redrafted clause before signing." },
    ],
  },
  {
    slug: "ai-redlining",
    title: "AI redlining",
    oneLine: "Produce a redlined Word document ready to send to counterparties.",
    long: [
      "Work through a redline session, accept the suggestions you want, and export a .docx. When the contract was uploaded as a Word file, the export writes real Word revisions — w:ins and w:del markup with an author and a date — so it opens in Word's own tracked-changes review pane.",
      "When the source was a PDF or a scan there is no original Word document to mark up, so the export renders the same changes as styled text instead: deletions struck through, insertions in green. Same content, different mechanism, and worth knowing which one you will get before you send it out.",
    ],
    group: "drafting",
    status: "live",
    how: [
      { title: "1. Accept the fixes", body: "Review the session's suggestions and accept the ones you want applied." },
      { title: "2. Generate the redline", body: "Vio writes the accepted changes into a .docx — Word revisions from a .docx source, styled text from a PDF or scan source." },
      { title: "3. Send or compare", body: "Share via a vault link or download. When a revised version comes back, run a clause-level comparison against the version you sent." },
    ],
    capabilities: [
      "Native Word tracked changes (w:ins / w:del with author and date) when the source is a .docx",
      "Struck-through / green styled output when the source was a PDF or scan",
      "Accept and reject decisions recorded per suggestion, and rolled up in Nexus",
      "Clause-level comparison of any two contract versions when a revised draft comes back",
      "Rounds by instruction — \"make the indemnity mutual\" adds a round of suggestions on just that, appended to the session",
      "Edit a pending suggestion; the model's original wording is kept beside yours",
      "An escalated suggestion — outside your approved range or high exposure — needs an explicit confirmation to accept",
      "Suggestions show your playbook position where one exists, and comment threads per suggestion",
    ],
    faq: [
      { q: "Will it preserve the original Word formatting?", a: "From a .docx source the redline is written into the original document's XML, so the surrounding formatting is the document's own. From a PDF or scan there is no original Word file, so the export is a newly generated document and the counterparty's layout is not reproduced." },
      { q: "Can I redline a whole folder in one run?", a: "No. A redline session runs against one contract. Bulk upload analyses many contracts at once, but it does not generate redlines." },
    ],
  },
  {
    slug: "contract-templates",
    title: "Contract template library",
    // 2026-10-08: the LexVio-maintained starting drafts are shared content
    // tagged `IN` (migration 186: SAFE post-money, ESOP with Indian
    // perquisite tax), so `shared_scalar_clause` shows a global org none of
    // them. What a global org has is its own templates and AI generation.
    oneLine: "Your firm's own templates, versioned — and AI-generated drafts grounded in your house style, your precedents and the governing law, with a verifier checklist.",
    long: [
      "Keep your firm's templates in one place, scoped to your organisation. Each carries version snapshots with change notes, a unified diff between versions and restore. Templates are parameterised: fill the placeholders — parties, consideration, term — and export to Word, or hand the draft to Vio for review.",
      "Generate a new template from a brief. Intake runs first: what the brief states is drafted exactly, and what it leaves open becomes a placeholder and a drafting note rather than a choice made for you. The draft is grounded in your house style, your own clauses and precedents, and the applicable-law context for the governing law you name — or a jurisdiction-neutral context when you name none. A verifier checklist runs on every generated draft. LexVio does not ship a library of its own starting drafts on this product.",
    ],
    group: "drafting",
    status: "live",
    how: [
      { title: "1. Start from yours, or a brief", body: "Open one of your firm's templates, or describe the document you need and let the drafting studio generate it." },
      { title: "2. Fill the placeholders", body: "Parties, addresses, consideration, governing law, term. Anything the brief left open stays a visible placeholder." },
      { title: "3. Review with Vio", body: "Output drops into Vio. Treat it like any other contract: review, redline, share." },
    ],
    capabilities: [
      "Firm templates scoped to your organisation, with version snapshots, change notes, diff and restore",
      "Parameterised placeholders",
      "One-click hand-off to Vio for AI review",
      "AI-generated templates grounded in your house style, your own clauses and precedents, and the applicable-law block contract analysis uses",
      "Brief intake first: what the brief states is drafted exactly; what it leaves open becomes a placeholder and a drafting note, never a choice made for you",
      "A verifier checklist on every generated draft — undefined terms, dangling cross-references, literal amounts, missing sections",
      "Long-form drafting section by section, with the Definitions drafted first and cross-references resolved at the end",
      "Fill the placeholders and export the template to Word",
    ],
    faq: [
      { q: "Can I add my own templates?", a: "Yes — admins can upload firm-specific templates, scoped to your organisation. Templates carry version snapshots with change notes, a unified diff between versions, and restore." },
      { q: "Will an AI-generated template choose a governing law for me?", a: "No. If the brief does not name one, the draft keeps a placeholder, and the verifier flags a draft that names a law anyway." },
    ],
  },

  {
    // Added 2026-10-08. Verified: services/draft_brief.py (migrations 219,
    // 236) — on a global org nothing about law, place or money is inferred;
    // the one inference (rupees from the words) is India-only — and
    // services/draft_sessions.py (migration 220).
    slug: "voice-drafting-brief",
    title: "New Contract — speak a brief, get a draft",
    oneLine: "Say what you need; LexVio turns it into a drafting brief, asks for what is missing, and drafts only when the brief is complete.",
    long: [
      "Dictate or type a request — \"an NDA with Acme for two years, mutual\". One model call extracts the fields with a confidence each, and everything after that is rules: names are resolved against your clients and counterparties, a matter is never guessed, and nothing about law or money is assumed. A currency, a governing law or a place you did not say is left blank and asked for.",
      "The brief lists what each document type requires and asks one question per missing field. Ambiguities are found by rule, not by the model: a place with no forum asks whether you mean the courts, the arbitration seat or both, with the consequence of each, and a date with no year is queried. The brief names the client you picked. Drafting runs through the drafting studio's own prompt and style pass, with a bracketed placeholder for anything still missing.",
    ],
    group: "drafting",
    status: "live",
    how: [
      { title: "1. Speak or type", body: "Start a new contract, pick the client, and optionally attach an intake request." },
      { title: "2. Answer the questions", body: "One question per missing field, in order, with the ambiguities spelt out." },
      { title: "3. Draft", body: "When nothing is missing, generate the draft and continue in the document editor." },
    ],
    capabilities: [
      "Speech or text becomes a structured brief — never a document directly",
      "Names resolved against your own records; a matter is never guessed",
      "No assumed governing law, place or currency",
      "Rule-based ambiguity questions — forum, side, a date with no year",
      "Drafting blocked until the brief is complete, unless you force it",
      "Model and prompt version recorded for the extraction and the draft",
    ],
    faq: [
      { q: "Will it fill in a governing law by default?", a: "No. A governing law you did not state is asked for, not assumed, and the draft keeps a placeholder until you answer." },
      { q: "Which languages can I dictate in?", a: "English, through the same dictation used elsewhere in the app." },
    ],
  },
  {
    slug: "draft-sessions",
    title: "Document editor — spoken changes",
    oneLine: "Tell the editor what to change; it proposes edits against the exact text, you accept or reject, and a snapshot becomes a new version.",
    long: [
      "In a draft session you give an instruction — \"make the indemnity mutual\" — and the model proposes changes. Each proposal's original text must be found verbatim in the working document, or it is dropped rather than applied somewhere it does not belong. You edit, accept or reject each one; a high-materiality change or a deletion asks for explicit confirmation.",
      "A snapshot renders the working text to Word and saves it as the next version of the contract, through the same checks as an upload. The change log is the history, and a failure anywhere leaves the working text untouched. Key dates are extracted into a contract timeline alongside the lifecycle dates.",
    ],
    group: "drafting",
    status: "live",
    how: [
      { title: "1. Open a session", body: "Start from a contract or a generated draft." },
      { title: "2. Instruct", body: "Speak or type the change; review the proposals against the original text." },
      { title: "3. Snapshot", body: "Save a new version when you choose — a version is a deliberate act." },
    ],
    capabilities: [
      "Proposals verified as verbatim substrings of the working text",
      "Accept, edit or reject; confirmation for high-materiality changes and deletions",
      "Snapshot to Word as a new contract version",
      "Change log as the history",
      "Extracted contract timeline merged with lifecycle dates",
    ],
    faq: [
      { q: "Does every accepted change create a version?", a: "No. Versions are created only when you take a snapshot." },
      { q: "Can a proposal change text it did not quote?", a: "No. A proposal whose original text cannot be located is dropped." },
    ],
  },
  {
    // Added 2026-10-08. Verified: services/clause_governance.py,
    // api/v1/clause_library.py (migration 238).
    slug: "clause-library",
    title: "Clause Library",
    oneLine: "Your firm's standard clauses and fallback positions with versions and approval — only the approved version ever reaches the AI.",
    long: [
      "The Clause Library holds your clause templates, drafting rules, negotiation tactics and fallback-ladder rungs. Editing an approved clause writes the next version as a pending revision; the approved version stays in force until an admin accepts the change. A clause that was never approved is edited in place and goes back to draft.",
      "Approval, rejection and retirement are for company admins, and not the revision's own author — unless they are the organisation's only admin. Restore makes an old version the new one. Redline positions, negotiation playbooks, template grounding and the fallback resolver read only clauses that are approved and inside their effective window. The audit names ids, version numbers and field names, never the clause text.",
    ],
    group: "drafting",
    status: "live",
    how: [
      { title: "1. Add or edit", body: "Open the Clause Library from Contracts on the desk." },
      { title: "2. Approve", body: "An admin other than the author accepts or rejects the pending revision." },
      { title: "3. In force", body: "The approved version, within its effective dates, is what AI drafting and redlining read." },
    ],
    capabilities: [
      "Versioned clauses, drafting rules, tactics and fallback rungs",
      "Pending revisions that leave the approved version in force",
      "Four-eyes approval, with a single-admin exception",
      "Effective-from and effective-to dates",
      "Restore any earlier version",
      "Only in-force clauses reach redlining, negotiation and drafting",
    ],
    faq: [
      { q: "Can a draft clause leak into a redline?", a: "No. Drafts, pending revisions, retired clauses and clauses outside their window are never read by the AI." },
      { q: "Where do saved answers go?", a: "Takeaways and compliance notes are knowledge rather than standards; they are versioned and kept with your saved answers, not in the Clause Library." },
    ],
  },

  // ── Review & Risk Analysis ────────────────────────────────────
  {
    slug: "contract-scanner",
    title: "Contract scanner",
    oneLine: "Three-tier extraction cascade so PDF, DOCX and scanned contracts all work — one accept list for every upload door, and a report page that says which step the analysis is on.",
    long: [
      "The entry point to every LexVio analysis. Drop a PDF, DOCX or TXT, a JPEG or PNG photograph, or paste text. LexVio runs a three-tier extraction cascade: LlamaParse for structured layouts, PyMuPDF for clean PDFs, and Tesseract OCR for scanned or photographed documents. The accept list is exactly those five types and it is one list — the desk upload, the phone's chunked upload and camera, the Vio attachment and every member of a bulk ZIP go through the same check. A legacy .doc file is refused with a message saying to save it as .docx or PDF, because there is no .doc parser in the stack and pretending otherwise would decode it as text.",
      "Whatever you throw at it lands on the other side as clean structured clauses with section numbers preserved. While the analysis runs, the report page says where it is — parsing, jurisdiction, indexing, analysing, finalising — by name and never as a percentage: the steps are wildly unequal in length (indexing takes seconds, analysing takes minutes) and a number invented from their count would be a progress bar that lies. A failed analysis leaves the step it broke on in place, so the row says what went wrong.",
    ],
    group: "review",
    status: "live",
    how: [
      { title: "1. Upload or paste", body: "PDF, DOCX, TXT, JPEG or PNG, or raw text. Scans are fine. Legacy .doc is refused with a message, on every door." },
      { title: "2. Extraction cascade", body: "LlamaParse first, then PyMuPDF, then OCR — whichever produces clean structured output for your document. A photograph goes straight to the OCR tier." },
      { title: "3. Parse into clauses", body: "Section-numbered clauses indexed and ready for risk scoring, search, and Vio Q&A. The report page names the step the analysis is on until it is ready." },
    ],
    capabilities: [
      "One accept list — PDF, DOCX, TXT, JPEG, PNG — shared by the desk upload, the phone upload and camera, Vio attachments and bulk ZIPs",
      "Legacy .doc refused with a message that says what to do instead",
      "Three-tier extractor cascade — fallbacks for poor PDFs",
      "Tesseract OCR for scanned or photographed documents",
      "Section-number preservation",
      "Handles photographed contracts from phones",
      "Analysis progress by named step — parsing, jurisdiction, indexing, analysing, finalising — never a percentage",
    ],
    faq: [
      { q: "What's the largest document it handles?", a: "200K input token limit per scan run (≈400-500 pages depending on density). Larger contracts can be split or bulk-uploaded as a portfolio." },
      { q: "Why is there no progress bar?", a: "Because the pipeline cannot measure one. It knows which step it is on, not how far through it is, and the steps differ in length by orders of magnitude. The report page shows the five steps and marks the current one; a percentage would be a number we made up." },
    ],
  },
  {
    slug: "clause-risk-scoring",
    title: "Clause-level risk scoring",
    oneLine: "Red, amber, or green for every clause, with an explanation and confidence score.",
    long: [
      "Every clause in your contract gets a per-clause risk verdict: red (block / renegotiate), amber (worth a second look), or green (standard). Each verdict includes a plain-English explanation, a confidence score (low / medium / high), and a reference to the relevant provision of the governing law where one applies.",
      "Risk scoring runs over the parsed clauses produced by the scanner. There is no separately trained scoring model — LexVio trains no model. The analysis prompt is assembled with the applicable-law context for the governing law the document resolves to, and where no governing law can be decided, with a jurisdiction-neutral commercial-contract context rather than another country's law.",
    ],
    group: "review",
    status: "live",
    how: [
      { title: "1. Scan a contract", body: "Document parses into clauses. Risk model runs over each clause." },
      { title: "2. Verdict + reasoning", body: "Red/amber/green tag with a sentence-level explanation in plain English." },
      { title: "3. Drill in", body: "Click a clause to see the reasoning, the relevant statute, and the fix suggestion." },
    ],
    capabilities: [
      "Per-clause red/amber/green tags",
      "Plain-English explanations on every verdict",
      "A verdict and an evidence span, so you can see which words drove the finding",
      "A confidence score from 0 to 100 on each verdict",
      "Quoted clause text is checked back against the source document — anything the model could not find there is withheld and reported separately",
      "References to the governing law where relevant — check a cited provision at the official source",
      "Filter by risk level for triage on long contracts",
    ],
    faq: [
      { q: "What if I disagree with a verdict?", a: "Record the disagreement — clause feedback is captured against the clause. It is not used to train or tune a model, because nothing in the product trains on your content." },
      { q: "Does it catch implied risks (not just clause text)?", a: "It catches a subset — missing clauses, contradictions, definitional gaps. It is a reading of the contract, not a tax or regulatory review of your business." },
    ],
  },
  {
    // Rewritten 2026-10-08 against services/health_score.py and
    // api/v1/health_score.py. The previous copy (per-contract score, clarity /
    // liability / IP sub-scores, a monthly email digest, "weighted blend with
    // multipliers") described nothing in the code. On a global org the tax
    // category is omitted, and a category with no input is "not assessed"
    // rather than a free 100 — so in practice the score is driven by the
    // organisation's analysed agreements.
    slug: "legal-health-score",
    title: "Legal Health Score",
    oneLine: "One organisation-wide number, 0-100, from the parts of your legal position the product can actually read — and \"not assessed\" for the parts it cannot.",
    long: [
      "A single score from 0 to 100 for your organisation, higher is better. Each category starts at 100 and is debited for the risk factors found in it, and the score is the mean of the categories that had something to read. The categories are contracts, compliance and litigation risk; on this product the contracts category — your analysed agreements — is the one with inputs for most organisations.",
      "A category with nothing to read is listed as not assessed and left out of the mean, rather than scored a perfect 100 it did not earn; an organisation with nothing to assess gets no score at all, with a sentence saying why. Snapshots are kept with the change from the previous one, so you can see whether the position is improving.",
    ],
    group: "review",
    status: "live",
    how: [
      { title: "1. Work normally", body: "The score reads what is already in the product — you do not fill anything in." },
      { title: "2. Read the categories", body: "Each assessed category shows its own score and risk factors; a category with no input says \"not assessed\"." },
      { title: "3. Read the history", body: "Snapshots are kept with the change from the previous one. Recompute on demand rather than waiting for the next snapshot." },
    ],
    capabilities: [
      "0-100 organisation-wide score, the mean of the assessed categories",
      "Risk factors listed per category",
      "Categories with no input marked not assessed — never a free 100",
      "No score at all, with the reason, when there is nothing to assess",
      "Snapshots with the change from the previous one",
      "Recompute on demand",
    ],
    faq: [
      { q: "Why is a category \"not assessed\"?", a: "Because nothing in your organisation feeds it yet. Scoring an empty category 100 would lift your overall number for a dimension nobody looked at." },
      { q: "Will it email me when the score moves?", a: "No. There is no score digest email. Read the history in the app." },
      { q: "Is this the same as a contract's risk score?", a: "No. A contract's 0-100 risk score is per document, computed in the analysis pipeline. The Health Score is organisation-wide." },
    ],
  },
  {
    slug: "bulk-analysis",
    title: "Bulk contract analysis",
    oneLine: "Upload a ZIP of contracts and run the full analysis across all of them in one job.",
    long: [
      "For due diligence, vendor audits, lease portfolios, and any other situation where you need the same analysis across a large set of documents. Upload a ZIP and every member goes through the same pipeline a single upload would: virus scan, extraction, clause parsing, risk scoring.",
      "Because they go through the normal ingest path, the documents land in your vault and become searchable in Nexus like anything else you upload. Progress is polled while the job runs, and per-file failures are counted so you can see what did not parse.",
    ],
    group: "review",
    status: "live",
    how: [
      { title: "1. Upload a ZIP", body: "Up to 200 files and 500 MB per archive. Each member is virus-scanned and guarded against zip-bomb and path-traversal tricks." },
      { title: "2. The job runs", body: "Three documents are processed concurrently through the full analysis pipeline, under your organisation's daily AI cost cap." },
      { title: "3. Check progress", body: "Poll the job for a completion percentage and per-file success and failure counts. Cancel a running job at any point." },
    ],
    capabilities: [
      "ZIP upload — up to 200 files, 500 MB per archive",
      "Full risk analysis per document, not a shallow pass",
      "Per-member virus scan, zip-bomb and zip-slip guards",
      "Processed documents land in the vault and become searchable in Nexus",
      "Progress polling with processed / failed counts, and cancel",
    ],
    faq: [
      { q: "Is there a size cap?", a: "200 files and 500 MB per ZIP, with a 100 MB limit on any single member and a 2 GB uncompressed ceiling for the archive." },
      { q: "Can I run a custom question across the batch?", a: "Not today. A bulk job runs the standard contract analysis on each document; there is no per-job custom prompt. For a one-off question across many documents, use Nexus Ask." },
      { q: "Can I download the results as a spreadsheet?", a: "No. Job results are read through the API and the app; there is no results export endpoint." },
      { q: "What happens to documents that fail to parse?", a: "They are counted as failures on the job so you can see how many did not make it. There is no re-process or skip queue — re-upload the ones that failed." },
    ],
  },
  {
    slug: "legal-simulators",
    title: "Legal simulators",
    oneLine: "What-if engines for litigation and contracts — outcome odds, damages, settlement, and AI negotiation roleplay.",
    long: [
      "A suite of scenario engines that turn the court-research corpus and the risk pipeline into forward-looking projections. Model how a matter is likely to land before you file, quantify a damages claim against comparable awards, weigh settling against pushing to trial, or rehearse a negotiation against an AI counterparty that pushes back.",
      "Every projection is grounded — outcome and damages simulators cite the comparable cases behind the numbers. Each result carries a clear disclaimer: these are statistical projections, not predictions or legal advice.",
    ],
    group: "review",
    // Withheld 2026-09-05. Every simulator router is mounted
    // `require_india_edition` (`app/api/v1/router.py`), and each of the ten
    // simulator keys is in `INDIA_ONLY_FEATURE_KEYS`. The backend's own
    // manifest says why: "Every simulator encodes Indian law or Indian
    // precedent — s.138 NI Act, GST/TDS, court outcome"
    // (`app/core/nav_manifest.py`, key `simulators`, editions=INDIA_ONLY).
    market: "IN",
    status: "live",
    how: [
      { title: "1. Pick a simulator", body: "Court outcome, damages quantum, settlement, cite-chain, counterfactual redline, drafting critic, negotiation, or a custom template." },
      { title: "2. Describe the scenario", body: "Fill the domain form — matter facts and claim amount, a contract plus proposed edits, cheque + return-memo details, or a counterparty persona and your objectives." },
      { title: "3. Read the projection", body: "Probability bands, percentile ranges, fee and timeline estimates, or a coached multi-turn negotiation — with the supporting precedents or statutory basis attached." },
    ],
    capabilities: [
      "Court Outcome — win-probability % with confidence interval, outcome distribution, days-to-judgment bands, and per-judge bench profiles",
      "Damages Quantum — award percentiles (p25–p99) from won cases with your claim's position and outlier cases",
      "Settlement — settlement-value band, fee breakdown, timeline, and a settle-vs-push recommendation",
      "Cite-Chain — counter-citations to your argument plus a 'still good law?' status on every case you cite",
      "Counterfactual — re-run a contract's risk score against proposed edits and see the clause-level diff",
      "Drafting Critic + Negotiation roleplay + a build-your-own custom simulator with reusable org templates",
    ],
    faq: [
      { q: "Are these predictions I can rely on in court?", a: "No. Every simulator returns a statistical projection with a clear disclaimer — a decision-support tool, not legal advice or a guaranteed outcome. Treat the cited precedents as the substance and the numbers as a guide." },
      { q: "Where do the numbers come from?", a: "From the case-law corpus and the contract-risk pipeline — outcome and damages run over comparable cases from the jurisdictions covered." },
    ],
  },

  {
    // Backed by `app/jurisdiction/services/decision.py` (the resolution order
    // and the fail-closed unknown), its call from contract-pipeline step 3b
    // (`app/core/pipeline/contract.py`), the `jurisdiction_decisions` table,
    // and `POST /api/v1/jurisdictions/resolve` +
    // `/jurisdictions/decisions/{id}/override`. The router carries no edition
    // gate and no feature gate, so this is live for every account here.
    slug: "governing-law-detection",
    title: "Governing law detection",
    oneLine: "Reads the governing-law clause before deciding which law a contract is analysed under — and treats \"unknown\" as an answer.",
    long: [
      "Analysing a contract under the wrong law produces advice that is confident and wrong. So before anything else runs, LexVio reads the whole document for a governing-law clause and matches it deterministically — \"governed by English law\", \"the laws of the State of Delaware\", \"the laws of Singapore\". Sub-national is respected wherever contract law is sub-national: Delaware, New York and California are three different answers, and so are Ontario and Quebec.",
      "The order is an explicit choice first, then the clause, then a model's classification, then your organisation's default. A model never outranks a clause that states the answer in words. When nothing settles it the jurisdiction is unknown, and unknown is a decision rather than a gap — the contract is analysed as a general commercial contract instead of being quietly filed under some other country's law. Every decision is recorded with the signals behind it, including the model's suggestion on the occasions the clause overruled it.",
    ],
    group: "review",
    status: "live",
    how: [
      { title: "1. Upload the contract", body: "Detection runs inside the analysis pass. Nothing to configure, and no per-document setting to forget." },
      { title: "2. The clause decides", body: "A deterministic pass over the full text, not an extract. A governing-law clause on page 30 outranks a classifier that read the first few pages." },
      { title: "3. See it, and change it", body: "The decision, its confidence and the signals behind it are stored against the contract. Override it and the override is stored too — as the strongest signal there is." },
    ],
    capabilities: [
      "Deterministic governing-law clause matching across the whole document, not a leading extract",
      "Sub-national resolution where contract law is sub-national — Delaware, New York, California, Ontario",
      "A model's classification is one signal and never the decider",
      "Unknown is a real answer: general commercial-contract treatment, never a fallback to another country's law",
      "Every decision recorded with its signals, including the ones that were overruled",
      "Human override, recorded alongside the automatic decision",
    ],
    faq: [
      { q: "What happens when a contract names no governing law?", a: "Your organisation's default applies if you have set one; otherwise the jurisdiction stays unknown and the contract is analysed as a general commercial contract. It is never assigned to a jurisdiction on a guess." },
      { q: "Does an AI decide this?", a: "Only where the deterministic signals are absent or disagree, and even then its answer ranks below a governing-law clause. A clause that names the law in words does not need a model to read it." },
    ],
  },

  {
    // Added 2026-10-08. Verified: services/contract_health.py, negotiation
    // playbooks (migration 228, `negotiation` key — on every global_* plan),
    // services/clm_obligations.py, the renewal pack, services/clm_survival.py.
    slug: "contract-health",
    title: "Contract health cards & negotiation playbook",
    oneLine: "One card per clause type with the quote behind each finding, a playbook for the negotiation, and a renewal pack built from the record.",
    long: [
      "Health cards turn a contract's analysis into one card per clause type, each finding shown with its quote and the weakest verdict. A finding that was contradicted is shown but does not count, and a checklist item the analysis never assessed is \"not assessed\" rather than a debit. The score sits under the cards with its basis.",
      "The negotiation playbook for a contract seeds from your approved clause positions and fallback ladder, then adds model suggestions over the contract's own clauses; your edits survive a rebuild and a deleted item is not re-seeded. Redline rounds read the playbook, and a suggestion that conflicts with a do-not-accept item escalates. The obligation tracker shows which obligations still lack their required evidence. The renewal pack aggregates the record and rejects a summary that states a number the facts do not hold. On termination, confidentiality, IP, indemnity and data-protection clauses are flagged as surviving obligations, with no survival period computed for you.",
    ],
    group: "review",
    status: "live",
    how: [
      { title: "1. Analyse the contract", body: "Health cards are read from the analysis and its checklist." },
      { title: "2. Prepare the negotiation", body: "Build the playbook from your positions; edit or delete items." },
      { title: "3. Through renewal or termination", body: "Use the renewal pack to decide; termination flags the clauses that survive." },
    ],
    capabilities: [
      "Health cards per clause type with quotes and verdicts",
      "Contradicted findings shown but not counted; unassessed items not debited",
      "Negotiation playbook seeded from your approved positions",
      "Playbook conflicts escalate redline suggestions",
      "Obligation tracker: required evidence, dependencies, clause references",
      "Renewal pack checked against the record's own numbers",
      "Survival flags on termination, without an invented survival period",
    ],
    faq: [
      { q: "Does the playbook decide what we accept?", a: "No. It records positions; a person accepts or rejects each suggestion." },
      { q: "Is the health score the same as the Legal Health Score?", a: "No. The cards show one contract's score with its basis underneath; the Legal Health Score is organisation-wide." },
    ],
  },
  {
    // Added 2026-10-08. `deep_audit` is on every global_* plan (inherited from
    // Pro, not in INDIA_ONLY_FEATURE_KEYS); AI Administration can switch the
    // OpenAI and Gemini readers off (`multi_agent_vendors`).
    slug: "deep-audit",
    title: "Deep Audit — three models, cross-checked",
    oneLine: "Run a contract past Anthropic, OpenAI and Google models in parallel, then reconcile the three readings into one report.",
    long: [
      "Switch a single upload into deep mode and the analysis does not go to one model. Anthropic, OpenAI and Google models each read the contract in parallel, and a reconciler pass merges the three readings into one report — so a finding that only one model saw is visible as exactly that, rather than presented with the same confidence as one all three agreed on.",
      "It costs roughly three times the tokens of a standard analysis and takes longer, which is why it is a per-upload choice rather than the default. Use it on the contracts where being wrong is expensive. An admin can switch the second-opinion vendors off for the whole organisation in AI Administration.",
    ],
    group: "review",
    status: "live",
    how: [
      { title: "1. Choose deep mode on upload", body: "A per-upload setting, not an account-wide one. Standard analysis stays the default." },
      { title: "2. Three readings in parallel", body: "Each vendor's model analyses the contract independently." },
      { title: "3. One reconciled report", body: "A merging pass produces a single cross-checked report rather than three you have to compare yourself." },
    ],
    capabilities: [
      "Three model vendors reading the same contract independently",
      "A reconciliation pass that merges them into one report",
      "Per-upload choice, so the cost is spent where it matters",
      "Roughly three times the token cost of a standard analysis",
      "All three vendors disclosed in the privacy policy's sub-processor list, and all three hard-disabled in air-gapped deployments",
      "Second-opinion vendors can be switched off by an admin",
    ],
    faq: [
      { q: "Is it more accurate?", a: "It is more cross-checked, which is not the same claim. Three independent readings surface disagreements a single pass would have hidden; we are not going to put an accuracy percentage on it, because we have not measured one." },
      { q: "Does the extra spend show in my usage?", a: "Yes. Each vendor's call on this path writes a cost row with the model that answered, so a deep run shows as roughly three times a standard one in the usage view." },
    ],
  },

  // ── Deals & Procurement ───────────────────────────────────────
  {
    slug: "tender-intelligence",
    title: "Tender Intelligence",
    oneLine: "Upload a tender or RFP and get an eligibility fit score, penalty-clause flags, the BOQ, and a bid-profitability simulation.",
    long: [
      "Drop in a tender, RFP, or bid document and LexVio extracts the reference number, issuing authority, value, deadlines, and every eligibility criterion, then scores how well your organisation fits — turnover, registrations, certifications — into a 0-100 eligibility verdict (eligible, eligible-with-gaps, or ineligible).",
      "Beyond eligibility, it decomposes the technical, financial, and bid conditions, flags one-sided liquidated-damages and unlimited-liability clauses, pulls out the bill of quantities, and runs a profitability simulation so you can decide whether to bid and at what margin. Every deadline syncs into the Compliance Calendar as a tracked obligation.",
    ],
    group: "deals",
    // Withheld 2026-09-05. `/api/v1/tenders` is mounted behind BOTH
    // `require_feature("tender_intelligence")` and `require_india_edition`,
    // and the key is in `INDIA_ONLY_FEATURE_KEYS` — so it is neither sold on a
    // global plan nor reachable if it were. Manifest note: "Indian public
    // procurement — EMD and GST fields". The copy here had been rewritten to
    // read jurisdiction-neutrally, which made the claim worse rather than
    // better: it promised global procurement over an Indian-tender engine.
    market: "IN",
    status: "live",
    how: [
      { title: "1. Upload the tender", body: "PDF, DOCX, or text. LexVio parses it and extracts criteria, deadlines, risks, and the BOQ." },
      { title: "2. See your fit", body: "A deterministic eligibility score against your org profile, with each criterion marked met or not — plus the penalty clauses worth worrying about." },
      { title: "3. Model the bid", body: "Run the profitability simulator for margin, break-even, and probability-of-loss, then push every deadline into your calendar." },
    ],
    capabilities: [
      "Eligibility fit score (0-100) against your org profile, with a per-criterion met / not-met breakdown",
      "Decomposes technical, financial, and bid conditions plus the mandatory-document submission checklist",
      "40+ risk flags — one-sided LD clauses, unlimited liability, ambiguous scope — and penalty-clause extraction",
      "Bill-of-quantities line items pulled out for cost modelling",
      "Profitability simulator: base-case margin, working-capital cost of payment delays, tax payables, and break-even bid",
      "Monte-Carlo run (2,000 iterations) with a probability-of-loss readout and ±10% sensitivity",
      "Auto-creates obligations for the submission deadline, pre-bid meeting, and timeline milestones",
      "Re-analyse when your org profile changes; export the analysis to PDF or DOCX",
    ],
    faq: [
      { q: "Does it find tenders for me?", a: "No — Tender Intelligence is upload-driven analysis. External tender discovery feeds, portal logins, and competitor win-probability data are out of scope for now." },
      { q: "How is the eligibility score calculated?", a: "Deterministically. Extracted criteria (turnover, experience, certifications, licences) are matched against the profile your org stores, so the verdict is explainable line by line rather than a black-box guess." },
    ],
  },
  {
    slug: "m-and-a-intelligence",
    title: "M&A Deal Intelligence",
    oneLine: "Run a deal-format diligence pass over a data room — reps & warranties, indemnity caps, CPs, MAC clauses, and a jurisdiction-specific regulatory red-flag report.",
    long: [
      "Point LexVio at a due-diligence room of deal documents — SPA, SHA, disclosure schedules, ancillaries — and it runs a transaction-grade pass: it classifies each document, extracts the representations and warranties by category, reads indemnity caps and baskets, tracks conditions precedent, and pulls out material-adverse-change clauses with their carve-outs.",
      "It then cross-checks the disclosure schedules against the SPA warranties to surface gaps, and produces a jurisdiction-specific regulatory red-flag report — company law, foreign-investment rules, stamp and transfer taxes, antitrust thresholds, data protection, and cross-border tax exposure — closing with a checklist whose items sync into Obligations.",
    ],
    group: "deals",
    status: "live",
    how: [
      { title: "1. Assemble the room", body: "Create a due-diligence room and upload the deal documents — SPA, SHA, disclosure schedules, escrow, ancillaries." },
      { title: "2. Run the deal analysis", body: "LexVio classifies each document and extracts reps & warranties, indemnities, conditions precedent, and MAC clauses." },
      { title: "3. Work the findings", body: "Review the disclosure cross-check and red-flag report, then push closing-checklist items into Obligations." },
    ],
    capabilities: [
      "Classifies each document — SPA, SHA, disclosure schedule, asset purchase, escrow, ancillary",
      "Reps & warranties by category (title, financials, tax, litigation, IP, employment, compliance, environmental) with qualifiers, survival, and severity",
      "Indemnity caps, baskets/deductibles, survival periods, and flagged concerns",
      "Conditions precedent with responsible party, status (open / satisfied / waived), and due date",
      "Material-adverse-change clauses with scope and carve-outs",
      "Disclosure-schedule cross-check against SPA warranties to surface gaps and inconsistencies",
      "Jurisdiction-specific regulatory red flags — company law, foreign-investment rules, stamp/transfer taxes, antitrust thresholds, data protection, cross-border tax — each with severity and remediation",
      "Closing checklist (CPs, deliverables, filings, consents) syncable to Obligations, plus export",
    ],
    faq: [
      { q: "Does it value the target?", a: "No — it's a legal-diligence engine, not a financial model. Valuation, cap-table, and transaction-structure modelling are out of scope; it analyses the documents in the room." },
      { q: "How does this relate to Due Diligence Rooms?", a: "Deal Intelligence is the M&A layer on top of a due-diligence room: the room bundles and aggregates the contracts, and this runs the SPA/SHA-aware analysis over them." },
    ],
  },
  {
    slug: "procurement-hub",
    title: "Procurement Hub",
    oneLine: "Onboard vendors with automatic risk-tiering and a compliance checklist that routes for approval and tracks every vendor end to end.",
    long: [
      "Procurement Hub turns vendor onboarding into a governed workflow. Enter a vendor's name, category, jurisdiction, and spend, and LexVio infers a risk tier on the spot from those three facts — data, hosting or payments exposure and a large spend push a vendor up — then generates the compliance checklist that tier demands. Change the category, spend or jurisdiction and the tier is recomputed.",
      "From there the vendor moves through a tracked lifecycle, with approval routing to the right reviewers (CISO for Tier 1, Finance above a spend threshold), per-check status, evidence capture, and links to the contracts the vendor is party to.",
    ],
    group: "deals",
    status: "live",
    how: [
      { title: "1. Add the vendor", body: "Name, category, jurisdiction, and spend estimate — LexVio predicts the risk tier as you type." },
      { title: "2. Auto-checklist + routing", body: "A tier-appropriate compliance checklist is generated and routed to the right approvers; the vendor enters review." },
      { title: "3. Track to active", body: "Reviewers mark each check passed, failed, or waived; the vendor transitions to active and links to its contracts." },
    ],
    capabilities: [
      "Deterministic Tier 1/2/3 inference from jurisdiction, spend, and category (data, hosting, payments → higher risk), recomputed when those facts change",
      "Auto-checklist per tier: tax ID, data processing agreement, SOC 2, ISO 27001, sanctions screening, anti-bribery, insurance, prior litigation, financial health",
      "Vendor lifecycle: prospect → in-review → approved → active → on-hold / rejected / offboarded",
      "Approval routing — CISO for Tier 1, Finance above the spend threshold",
      "Per-check status (pending / passed / failed / waived / N-A) with severity, due date, evidence, and checked-by",
      "Links vendors to existing contracts with a role and note, and tracks the contract count",
      "KPI dashboard: total vendors, Tier-1 exposure, in-review, pending checks, active, and blocked",
      "Filter by status, risk tier, or category and search by name",
    ],
    faq: [
      { q: "Does it score vendor risk automatically?", a: "It infers a risk tier deterministically from jurisdiction, spend, and category, and drives the checklist from that. It is not a black-box risk score — the tier and its required checks are explainable." },
      { q: "Who has to approve a vendor?", a: "Routing depends on the tier and spend — Tier 1 vendors route to your CISO and spend above the threshold routes to Finance, so high-risk relationships can't go active without the right sign-off." },
    ],
  },
  {
    slug: "insurance-analyzer",
    title: "Insurance Policy Analyzer",
    oneLine: "Upload a policy and see what isn't covered — exclusions, sub-limits, deductibles, and the claims-notice window that voids the claim if you miss it.",
    long: [
      "Insurance wordings hide the things that matter in the exclusions and conditions. Upload a policy and LexVio extracts the metadata (insurer, policy number, sum insured, premium, type), then lays out the coverage heads against the exclusions, the per-peril sub-limits, and the deductibles — so the gaps are visible at a glance.",
      "Critically, it surfaces the claims-notification window — miss it and the claim can be void — alongside conditions precedent, warranties whose breach suspends cover, coverage-gap flags, and the renewal date, which syncs into the Compliance Calendar so a lapse never sneaks up on you.",
    ],
    group: "deals",
    // Withheld 2026-09-05. `/api/v1/insurance` carries
    // `require_feature("insurance_legal")` + `require_india_edition`, and the
    // key is in `INDIA_ONLY_FEATURE_KEYS`. Manifest note: "IRDAI-regulated
    // policies, INR sums insured".
    market: "IN",
    status: "live",
    how: [
      { title: "1. Upload the policy", body: "PDF, DOCX, or text. LexVio parses the wording and extracts the structured terms." },
      { title: "2. See what's not covered", body: "Exclusions, sub-limits, deductibles, conditions, and the claims-notice window, all pulled out and ranked by severity." },
      { title: "3. Never miss a date", body: "Renewal and premium dates sync into your calendar as obligations with reminders." },
    ],
    capabilities: [
      "Metadata extraction: insurer, policy number, sum insured, premium, and type (property, liability, marine, health, motor, fire, engineering, cyber, D&O, PI, GPA, credit)",
      "Coverage heads versus exclusions, side by side",
      "Per-peril sub-limits and deductibles / excess",
      "Conditions precedent and warranties whose breach suspends cover",
      "Claims-notification window highlighted — the deadline that voids a claim if missed",
      "Coverage-gap flags and risk flags (e.g. average clause) with severity",
      "Renewal date and key policy dates syncable to Obligations",
      "Re-analyse when the wording changes; export to PDF, DOCX, or text",
    ],
    faq: [
      { q: "Does it track insurance-regulator circulars or rates?", a: "No — it analyses the policy you upload. Regulator circular ingestion, live rate data, and claims-history scoring are out of scope for now." },
      { q: "Can it tell me if I'm under-insured?", a: "It flags coverage gaps it can see in the wording (for example, a missing business-interruption head). It does not recommend products or benchmark cover against the market." },
    ],
  },

  {
    // Added 2026-10-08. Verified: services/dd_rooms.py, api/v1 dd_rooms router
    // (mounted with no edition gate and no feature gate).
    slug: "due-diligence-rooms",
    title: "Due-diligence rooms",
    oneLine: "Bundle a set of contracts into a room, see the aggregate immediately, then run a synthesis across all of them.",
    long: [
      "A room is a named bundle of contracts you are looking at together — a target's contract set, a vendor portfolio, a lease book. Adding contracts is idempotent, so building a room up over several sessions does not duplicate anything.",
      "Two levels of answer. The aggregate view is computed from the analyses already done and involves no AI at all, so it is immediate and free: counts, risk distribution, what is in the set. The synthesis run is a model pass across the room that produces a written cross-contract read, and every run is kept, so you can see what you asked and what came back, and export it.",
    ],
    group: "deals",
    status: "live",
    how: [
      { title: "1. Create the room", body: "Name it, give it a status, add contracts. Adding the same contract twice is a no-op." },
      { title: "2. Read the aggregate", body: "Computed from existing analyses — no model call, no wait, no cost." },
      { title: "3. Run the synthesis", body: "A cross-contract analysis over the room. Runs are listed, re-readable and exportable." },
    ],
    capabilities: [
      "Rooms bundling any set of contracts, with idempotent adds and removes",
      "Instant aggregate view computed without a model call",
      "Cross-contract synthesis runs, kept as history rather than overwritten",
      "Export a run",
      "The room is also what M&A Deal Intelligence runs its transaction-grade pass over",
    ],
    faq: [
      { q: "How is this different from M&A Deal Intelligence?", a: "The room is the container and the general cross-contract read. M&A Deal Intelligence is the SPA- and SHA-aware analysis that runs over a room — reps and warranties, indemnity caps, conditions precedent, MAC clauses and a red-flag report under the law the documents state." },
      { q: "Is this a data room I can invite the other side into?", a: "No. It is an internal working bundle. Sharing individual documents outside the organisation is done with share links or the client portal, which have their own controls." },
    ],
  },

  // ── Court Research ────────────────────────────────────────────
  {
    slug: "court-research-corpus",
    title: "Court research corpus",
    oneLine: "Citation-verified judgments and rulings from courts and tribunals across major commercial jurisdictions.",
    long: [
      "A search-first index of court and tribunal output. Judgments are tagged by court, jurisdiction, doctrine, and clause type they bear on. Full-text search with clause-type filters lets you find the precedent that bears on a specific drafting decision.",
      "The corpus is updated continuously from the court websites we monitor. We publish the corpus cutoff and last-refresh date inside the search UI.",
    ],
    group: "research",
    // Withheld 2026-09-05, and the worst of the set. All seven `/research`
    // routers are mounted `require_india_edition`, with the reason written
    // above the mount: "The corpus behind these is Indian: `judgments` holds
    // Indian court decisions and `statute_sections` the India Code." `ai_research`
    // is in `INDIA_ONLY_FEATURE_KEYS`. So "across major commercial
    // jurisdictions" described a single-jurisdiction corpus that 404s here —
    // an overstatement of coverage on top of a surface that does not exist.
    market: "IN",
    status: "live",
    how: [
      { title: "1. Search", body: "Natural-language query or clause-type filter. Hybrid semantic + keyword retrieval." },
      { title: "2. Filter", body: "Court, year, jurisdiction, doctrine, clause type — narrow down the set." },
      { title: "3. Open + cite", body: "Read the judgment with relevant passages highlighted. Drop into a draft as a footnoted citation." },
    ],
    capabilities: [
      "Multi-court coverage across major commercial jurisdictions",
      "Clause-type tags for drafting-led search",
      "Hybrid retrieval — semantic + keyword + clause-type",
      "Direct insertion into Vio drafts as cited footnote",
    ],
    faq: [
      { q: "Is every judgment in here?", a: "No corpus is complete; we publish the cutoff date inside the search UI. Our coverage prioritises commercial and corporate law jurisdictions." },
    ],
  },
  {
    slug: "citation-graph",
    title: "Citation graph",
    oneLine: "See how cases cite each other — trace a doctrine forward and back.",
    long: [
      "Every judgment is a node; every citation is an edge. The graph lets you see which earlier cases were cited, which later cases cited this one, and how a doctrine evolved across courts and decades.",
      "Useful for advocates building an argument from precedent, and for partners checking whether a cited case has been distinguished or overruled in a later decision.",
    ],
    group: "research",
    // Withheld 2026-09-05. Same `/research` mount and same corpus as above;
    // the manifest carries it as `citation_intelligence`, editions=INDIA_ONLY,
    // and its note draws the line this site now follows: "Distinct from
    // POST /copilot/citations/preview, which serves both editions because the
    // vault resolver reads the org's own documents." That one is sold here,
    // as `citation-verification` below.
    market: "IN",
    status: "live",
    how: [
      { title: "1. Anchor a case", body: "Open any judgment in the research corpus." },
      { title: "2. Expand the graph", body: "Backward to earlier cases this one cited; forward to later cases that cite this one." },
      { title: "3. Spot warnings", body: "Cases marked as distinguished, overruled, or per-incuriam are flagged in the graph." },
    ],
    capabilities: [
      "Forward + backward citation chains",
      "Distinguished / overruled flags",
      "Per-doctrine subgraph filters",
      "PNG / PDF export for argument briefs",
    ],
    faq: [
      { q: "How are overruling flags determined?", a: "Combination of automated NLP extraction and editorial review for high-traffic cases. Reach out if you find a missing flag." },
    ],
  },

  // ── Compliance & Deadlines ────────────────────────────────────
  {
    slug: "regulator-monitoring",
    title: "Regulator monitoring",
    oneLine: "Regulatory monitoring across your obligations, with alerts ahead of every deadline.",
    long: [
      "We monitor the regulators that matter most to your organisation: corporate and securities regulators, financial-services regulators, company registries, and tax authorities — configured to the regulators relevant to your entity and jurisdiction.",
      "New circulars and amendments are summarised in plain English and tagged against your active obligations.",
    ],
    group: "compliance",
    // Withheld 2026-09-05. The alerts it describes are `/api/v1/compliance`,
    // mounted `require_india_edition`; the regulators behind it are the ones
    // `sebi_dashboard` names in `INDIA_ONLY_FEATURE_KEYS` ("SEBI / MCA
    // filings"). "Corporate and securities regulators, financial-services
    // regulators, company registries, and tax authorities" is that list with
    // the names taken off.
    market: "IN",
    status: "live",
    how: [
      { title: "1. Configure your entity", body: "Tell us your entity type, listed status, and key business segments. We tailor the monitoring scope." },
      { title: "2. Continuous ingest", body: "We pull new circulars and notifications as they're published." },
      { title: "3. Plain-English digest", body: "Each new item arrives summarised with a 'does this affect you?' line." },
    ],
    capabilities: [
      "Regulator coverage across corporate/securities, financial services, company registry, and tax",
      "Plain-English summaries on every new circular",
      "Tagged against your obligations — relevant items rise to the top",
      "Searchable archive of past circulars",
    ],
    faq: [
      { q: "How fresh is the feed?", a: "We poll the regulators' official channels continuously. Most items land within a working day of publication." },
    ],
  },
  {
    slug: "filing-calendar",
    title: "Filing calendar",
    oneLine: "Every deadline that applies to your entity in one calendar, with advance alerts.",
    long: [
      "A unified calendar of every filing deadline relevant to your entity — tax returns, statutory filings, quarterly disclosures, and more.",
      "Alerts fire 30, 14, and 7 days in advance by default — configurable per deadline. Channels: email and in-app.",
    ],
    group: "compliance",
    // Withheld 2026-09-05. `compliance_calendar_router` is mounted
    // `require_india_edition` and `compliance_calendar` is in
    // `INDIA_ONLY_FEATURE_KEYS` — "the 53-event India statutory calendar".
    // The site's own `/calendar` page is already a hard 404 here for the same
    // reason (`lib/edition/indiaOnlyRoutes.ts`).
    market: "IN",
    status: "live",
    how: [
      { title: "1. Pick your scope", body: "Entity type + active registrations (tax, listed status, cross-border exposure) drive which deadlines apply." },
      { title: "2. See them all", body: "Calendar view with deadlines colour-coded by regulator." },
      { title: "3. Get the nudge", body: "Email + in-app reminders at configurable windows." },
    ],
    capabilities: [
      "All-in-one calendar across tax, statutory, and regulatory filings",
      "Configurable lead times (30/14/7/1 day)",
      "Email + in-app channels",
      "Per-team assignment — \"who's responsible for this one?\"",
      "Calendar export — iCal / Google Calendar sync",
    ],
    faq: [
      { q: "What if a deadline shifts?", a: "We track government-published holiday calendars and regulator notifications for your applicable jurisdiction. If a deadline moves, the calendar moves with it and a notification fires." },
    ],
  },
  {
    slug: "regulatory-change-feed",
    title: "Regulatory change feed",
    oneLine: "New circulars summarised and filterable by Act, regulator, and effective date.",
    long: [
      "A real-time chronological feed of new circulars, notifications, and amendments. Each item carries a short plain-English summary, a tag set, and an 'affects you?' verdict based on your configured profile.",
      "Use it for staying current. Use Insights for the proactive scan of how the change impacts your specific obligations and contracts.",
    ],
    group: "compliance",
    // Withheld 2026-09-05. `reg_change_router` carries
    // `require_feature("reg_change_impact")` + `require_india_edition`. The
    // key was added to `INDIA_ONLY_FEATURE_KEYS` for exactly this reason — the
    // backend's comment records that "every global plan advertised
    // 'Regulatory Change Impact' as included" while the feed behind it is the
    // India reg-feed. This is that same claim, on the marketing side.
    market: "IN",
    status: "live",
    how: [
      { title: "1. Subscribe", body: "Pick which regulators and topic tags you want to see." },
      { title: "2. Daily digest", body: "Email or in-app digest of new items, ranked by 'affects you?' confidence." },
      { title: "3. Drill in", body: "Each item links to the full circular, the LexVio summary, and the related contracts in your vault." },
    ],
    capabilities: [
      "Filter by regulator, Act, topic, effective date",
      "Plain-English summary on every item",
      "Daily / weekly digest options",
      "Slack + Teams + email delivery",
    ],
    faq: [
      { q: "Does it cover state/regional notifications?", a: "Select state and regional circulars are covered; broader state/regional compliance is on the roadmap." },
    ],
  },
  {
    // Added 2026-10-08. Verified: obligations router (no edition gate),
    // workers/main.py::send_obligation_reminders_task (daily, idempotent per
    // obligation / due date / lead time, through services/notify.py),
    // services/clm_obligations.py, services/proactive_analysis.py.
    slug: "obligations",
    title: "Obligation register",
    oneLine: "Pull the obligations out of a contract, then actually track them — with reminders that fire, evidence, and a renewal pipeline.",
    long: [
      "AI extraction reads a contract and produces the obligations it creates: what has to be done, by when, and of what type. From there it is a working register — filter it, update status, mark things complete with who completed them and when, add obligations manually that no contract created.",
      "A daily job sends reminders on each obligation's own lead times, on the notification channels you have switched on, with a log that stops the same reminder going twice. You can attach evidence to an obligation, so a completed item carries proof rather than a tick. The non-renewal notice deadline and the renewal decision live on the contract record itself: the deadline is derived from the expiry date and notice period, flagged at 30, 7 and 1 days, and at 30 days the system files one task to decide.",
    ],
    group: "compliance",
    status: "live",
    how: [
      { title: "1. Extract from the contract", body: "Obligations are pulled out of a contract's text, and can be re-extracted later if the contract changes." },
      { title: "2. Work the register", body: "Filter, update, assign a status, complete with attribution, or add obligations by hand." },
      { title: "3. Get reminded, and prove it", body: "Reminders fire on each obligation's lead times. Attach evidence when you complete one." },
    ],
    capabilities: [
      "AI extraction of obligations from a contract, re-runnable, with the clause it came from kept only when its quote is found in the text",
      "A reminder job that actually runs, with an idempotency log so a reminder is not sent twice",
      "Evidence attachments, with the evidence each obligation requires listed and tracked",
      "Dependencies between obligations, checked for cycles",
      "Dashboard, renewal pipeline, and export of the register",
      "M&A closing-checklist items and audit-readiness remediation sync into the same register",
      "Notice deadlines on the contract record — expiry date minus notice period, flagged at 30, 7 and 1 days",
    ],
    faq: [
      { q: "Can I add an obligation that no contract created?", a: "Yes. Manual obligations sit in the same register with the same reminders and evidence." },
      { q: "Is an obligation the same as a task?", a: "No. An obligation is what the contract binds you to, extracted from its text and tracked here with evidence. A task is what a person decided to do by a date. A task can point at an obligation; completing a task never discharges an obligation." },
    ],
  },
  {
    // Added 2026-10-08. services/audit_frameworks.py::frameworks_for_org —
    // a global org sees SOC 2 only; the DPDP and GST frameworks are in
    // INDIA_ONLY_FRAMEWORK_KEYS and 404 for it.
    slug: "audit-readiness",
    title: "Audit readiness",
    oneLine: "Assess yourself against the SOC 2 control set — judged against the policies you upload, not a questionnaire.",
    long: [
      "LexVio assesses your organisation control by control against the policy documents you have in the product. Each control comes back as met, partial, gap or unknown, with a recommendation. Unknown is a real verdict and it is used when the evidence does not support a judgement either way.",
      "The gaps become work: one call turns them into dated obligations in your register, so remediation is tracked rather than noted in a report nobody reopens. Runs are kept, so you can show an auditor what you assessed, when, and what you did about it. Everything is judged against your own documents — no external data, no benchmark, no scraping.",
    ],
    group: "compliance",
    status: "live",
    how: [
      { title: "1. Pick the framework", body: "SOC 2 is the framework offered on this product." },
      { title: "2. Assess against your evidence", body: "Controls are judged against the policy documents you hold in the product." },
      { title: "3. Turn gaps into obligations", body: "One call creates dated remediation obligations from the gaps, which then behave like any other tracked obligation." },
    ],
    capabilities: [
      "SOC 2 control set",
      "Per-control verdict — met, partial, gap, unknown — each with a recommendation",
      "Assessed against documents you uploaded, not against a self-report questionnaire",
      "Readiness report exportable as PDF, DOCX or text",
      "Gap-to-obligation remediation with due dates",
      "Run history, so an assessment is auditable rather than transient",
    ],
    faq: [
      { q: "Is this a SOC 2 audit?", a: "No, and it must not be represented as one. It is a readiness assessment against the control set, done by reading your policies. A SOC 2 report comes from an accredited auditor and nothing here substitutes for that." },
      { q: "Why would a control come back \"unknown\"?", a: "Because your documents do not say enough either way. Forcing that into a pass or a fail would be the failure mode this whole product is built to avoid." },
    ],
  },

  // ── Privacy & Data Protection ─────────────────────────────────
  {
    // Rewritten 2026-10-08 as the Privacy & Data Protection module
    // (g-backend migration 257, docs/PRIVACY_REGIMES_PLAN_2026-10-07.md).
    // The slug is kept because next.config.ts redirects the old
    // `/features/dpdp-readiness` path here. The previous copy described a
    // 15-question GDPR self-assessment with template artefacts that this
    // product does not have.
    //
    // Backed by: api/v1/router.py (`/privacy-program` routers gated by
    // `require_feature("privacy_compliance")` and by no edition; the public
    // request portal is anonymous and token-addressed);
    // services/privacy_packs/registry.py (PACK_MODULES — fifteen packs, of
    // which `in_dpdp` is India-only and never offered to a global org, so a
    // global org can hold the other fourteen; COUNTRY_DEFAULTS picks the
    // starting law from the org's country, and no country means no guess);
    // every pack's `content_status="drafted"`; the DIFC pack carries
    // summaries and links only. Plan gate from subscription_plans.py:
    // `privacy_compliance` True on global_pro / global_business / global_team,
    // False on global_starter.
    slug: "privacy-readiness",
    title: "Privacy & Data Protection",
    oneLine: "Run your privacy programme under the data protection laws your organisation is subject to — fourteen law packs, from GDPR and UK GDPR to CCPA, PIPEDA and POPIA, on one engine.",
    planNote: "Included on Pro, Business and Enterprise. It is not included on Starter.",
    long: [
      "The Privacy & Data Protection module is a working programme, not a questionnaire. Your organisation's country picks the law it starts with, and an admin can hold several at once: EU GDPR, UK GDPR, the California CCPA/CPRA, New York's SHIELD Act, the Delaware Personal Data Privacy Act, Canada's PIPEDA, Australia's Privacy Act, New Zealand's Privacy Act 2020, Singapore's PDPA, Hong Kong's PDPO, South Africa's POPIA, Kenya's Data Protection Act, Nigeria's NDPA and the DIFC Data Protection Law. Where no country is set, the module asks which law applies rather than guessing one. Screens use the law's own vocabulary, and neutral words where the laws you hold differ.",
      "Every law pack is drafted: its content has not yet been verified against the official text by counsel, and the product says so on every screen that shows it. The DIFC pack carries short summaries and links only, because the DIFC's terms do not allow its text to be reproduced. Treat the module as a way to run and evidence your programme, not as legal advice — a status of compliant needs linked evidence and a person's approval, and is never computed from a score alone.",
    ],
    group: "privacy",
    status: "beta",
    how: [
      { title: "1. Decide what applies", body: "Run the applicability wizard; the obligation register and readiness calendar follow from the laws you hold." },
      { title: "2. Build the records", body: "Systems, personal data, processing activities, purposes, notices, consent, processors and transfers — each a register you own." },
      { title: "3. Run it", body: "Work individual rights requests and incidents against each law's own clocks, delete on schedule, and export audit packs with the evidence linked." },
    ],
    capabilities: [
      "Fourteen law packs on one engine; your country picks the default, and an admin can hold several laws at once",
      "Applicability wizard, obligation register, readiness calendar and gap assessment",
      "System and personal-data inventory, data flow map, lineage, and AI data discovery over files you upload",
      "Processing-activity and purpose registers with change reviews",
      "Notice builder with versions; consent records, withdrawals, and child and guardian consent",
      "Individual rights requests and complaints, with a public request portal and each law's own response period",
      "Breach response with each law's notification clocks, a notification workspace and corrective actions",
      "Retention policies, legal holds and a deletion queue",
      "Processor register with DPA review against each law's required clauses, processor obligations and reassessments",
      "DPIA workbench, risk register, review schedule, and algorithm and AI reviews",
      "Cross-border transfer register, data locations and transfer restrictions",
      "Evidence, audit packs, a regulatory change monitor and a privacy copilot",
      "Every pack marked drafted on screen — not yet verified against the official text by counsel",
    ],
    faq: [
      { q: "Will it tell me we are compliant?", a: "Not from a score. A compliant status needs evidence linked and a person's approval. The law packs are drafted and not yet reviewed by counsel, so have counsel confirm your position before you rely on it." },
      { q: "Which law does it start with?", a: "The one your organisation's country points to — the UK GDPR for a UK organisation, the EU GDPR for Ireland, Germany or the Netherlands, the three US state packs for a US organisation, and so on. An admin can add or remove laws. With no country set, it asks rather than guessing." },
      { q: "Does AI data discovery connect to our databases?", a: "No. Discovery reads files and documents you upload; there are no live database connectors." },
      { q: "Which plans include it?", a: "Pro, Business and Enterprise. Starter does not include it." },
    ],
  },

  // ── Tax Intelligence ──────────────────────────────────────────
  {
    slug: "income-tax-analysis",
    title: "Income tax analysis",
    oneLine: "Section-wise IT Act analysis with CBDT circulars, flagging missed deductions.",
    long: [
      "Upload your return or financials and get a section-wise analysis. We flag missed deductions, notice-prone positions, and computation errors against the current IT Act and CBDT circulars.",
      "Works for individuals (salaried + business income), partnerships, and corporates. New-regime / old-regime switch detection included.",
    ],
    group: "tax",
    market: "IN",
    status: "live",
    how: [
      { title: "1. Upload return + Form 16 / 26AS", body: "PDF or JSON. We parse and reconcile against your TDS." },
      { title: "2. Section-by-section review", body: "Section 80C, 80D, 80G, HRA, LTA, capital gains, and the rest. Each one verified against current limits." },
      { title: "3. Issue list", body: "Missed deductions, computation errors, and notice-prone positions called out with citations." },
    ],
    capabilities: [
      "Salaried, business, and corporate coverage",
      "Old vs new regime comparison",
      "Reconciliation against Form 26AS / AIS",
      "CBDT circular citations on every flag",
    ],
    faq: [
      { q: "Does it file the return?", a: "No — review and analysis only. Filing routes to the IT portal or your CA's filing software." },
    ],
  },
  {
    slug: "tds-detection",
    title: "TDS detection in contracts",
    oneLine: "Auto-flags TDS obligations from contract clauses — section, rate, threshold, due date.",
    long: [
      "When you scan a contract, LexVio checks for TDS-attracting clauses and flags them: applicable section (194C, 194J, 194-IA, 194Q, 195, etc.), rate, threshold, due date, and any non-resident considerations.",
      "Useful at contract signing time (so you build TDS into the commercial terms) and at invoice-processing time (so you don't forget to deduct).",
    ],
    group: "tax",
    market: "IN",
    status: "live",
    how: [
      { title: "1. Scan a contract", body: "TDS check runs alongside the standard risk analysis." },
      { title: "2. See the obligations", body: "Each TDS-attracting clause is flagged with section, rate, threshold." },
      { title: "3. Calendar the dates", body: "Auto-add quarterly TDS deposit deadlines to your filing calendar." },
    ],
    capabilities: [
      "Section detection — 192 to 206AB, including 194-IA / 194-IB / 194Q / 194R / 194S",
      "Rate + threshold lookup",
      "Non-resident / 206AA / 206AB higher-rate cases",
      "Auto-add quarterly deposit dates to calendar",
    ],
    faq: [
      { q: "Does it cover crypto TDS (§194S)?", a: "Yes — §194S detection is in the section set." },
    ],
  },
  {
    slug: "gst-computation",
    title: "GST computation helper",
    oneLine: "GST math, ITC reconciliation against GSTR-2B, and e-invoicing validation.",
    long: [
      "Compute GST correctly across intra-state, inter-state, and zero-rated supply scenarios. Reconcile your input tax credit against GSTR-2B and surface mismatches.",
      "Validates e-invoice JSON against the IRP schema so you can catch errors before submission.",
    ],
    group: "tax",
    market: "IN",
    status: "live",
    how: [
      { title: "1. Upload invoices or transactions", body: "PDF, JSON, or CSV." },
      { title: "2. Compute + classify", body: "CGST + SGST or IGST with HSN/SAC lookup. Place-of-supply detection." },
      { title: "3. Reconcile + validate", body: "Cross-check against GSTR-2B; validate e-invoice JSON against IRP rules." },
    ],
    capabilities: [
      "Forward + reverse GST computation",
      "Place-of-supply inference",
      "ITC reconciliation against GSTR-2B",
      "E-invoice JSON validation",
    ],
    faq: [
      { q: "Does it file GSTR?", a: "No — analysis and reconciliation only. File through the GST portal or your accountant's tooling." },
    ],
  },
  {
    slug: "ifrs-indas",
    title: "IFRS vs IndAS comparator",
    oneLine: "Side-by-side treatment of a clause or transaction under both frameworks.",
    long: [
      "For groups with overseas reporting or for transactions that need dual-framework treatment. Pick a clause or a transaction type, and get the side-by-side accounting under IFRS and IndAS with notes on the India-specific carve-outs.",
      "Useful for finance teams negotiating MSAs (revenue recognition impact), and for advocates structuring cross-border arrangements.",
    ],
    group: "tax",
    market: "IN",
    status: "beta",
    how: [
      { title: "1. Pick the scenario", body: "Revenue recognition, lease accounting, financial instruments, business combinations." },
      { title: "2. Pick the clause", body: "Paste the contract clause or transaction structure." },
      { title: "3. Read the comparator", body: "IFRS side, IndAS side, key differences and India-specific notes." },
    ],
    capabilities: [
      "Major standards covered — revenue, leases, financial instruments, business combinations",
      "India-specific carve-out notes",
      "Citation to relevant IndAS paragraph",
    ],
    faq: [
      { q: "Is this a substitute for an auditor's view?", a: "No — it's a starting analysis. Confirm with your auditor before relying on it for financial statements." },
    ],
  },
  {
    slug: "cross-border-tax",
    title: "Cross-border tax",
    oneLine: "DTAA treaty analysis, transfer pricing flags, Form 15CA/CB requirements.",
    long: [
      "Cross-border remittances, royalty payments, fees for technical services, and service exports — all have India tax implications shaped by the relevant DTAA, transfer pricing rules, and Form 15CA/CB requirements.",
      "Upload a cross-border contract and get the tax position summarised — applicable treaty rate, FTS / royalty classification, withholding obligations, and the 15CA/CB workflow.",
    ],
    group: "tax",
    market: "IN",
    status: "beta",
    how: [
      { title: "1. Upload a cross-border contract", body: "Service agreement, royalty agreement, or remittance arrangement." },
      { title: "2. Treaty + section analysis", body: "Applicable DTAA, fall-back §9 / §195 position, transfer pricing considerations." },
      { title: "3. 15CA/CB workflow", body: "Generate the forms with pre-filled data; identify when CB certification is needed." },
    ],
    capabilities: [
      "DTAA treaty rate lookup",
      "FTS / Royalty / Business income classification",
      "Transfer pricing flags",
      "Form 15CA + 15CB generation",
    ],
    faq: [
      { q: "Does it cover BEPS / Pillar Two?", a: "BEPS Pillar Two readiness is on the roadmap; cross-border DTAA basics are live in beta." },
    ],
  },

  // ── Vault & Document Management ───────────────────────────────
  {
    slug: "encrypted-vault",
    title: "Encrypted vault",
    oneLine: "AES-256 encryption at rest, TLS 1.2 or later in transit, stored in AWS ap-south-1 (Mumbai, India).",
    long: [
      // 2026-10-08: matched to /legal/privacy, /legal/dpa, /security and
      // /trust word for word on the facts. The earlier copy said "TLS 1.3",
      // "encrypted backups in a second AWS region", "90-day backup retention"
      // and "region pinning on enterprise plans" — none of which the legal
      // pages state or the backend does (backups are a per-deployment job
      // written to ap-south-1; there is no per-organisation region routing).
      "Uploaded documents, data exports and database backups are stored in Amazon S3 in the AWS ap-south-1 region (Mumbai, India), written with server-side encryption at rest (AES-256; a customer-managed KMS key where the deployment configures one). All traffic is encrypted in transit with TLS 1.2 or later. There is no per-organisation region routing on the hosted service; a self-hosted deployment keeps its data wherever you run it.",
      // Where the data sits has to be stated, and so does the fact that makes
      // it a question for EU/UK buyers: India holds no adequacy decision.
      "Document content is also transmitted to the AI sub-processors listed in our DPA — model inference, embeddings and parsing, located in the United States — to perform the analysis you request. India holds no EU or UK adequacy decision, so EU/UK customers should read the transfer terms in the DPA before uploading personal data.",
    ],
    group: "vault",
    status: "live",
    how: [
      { title: "1. Upload", body: "Drag a file into your vault. Every upload passes the same accept list and virus scan." },
      { title: "2. Encrypted at rest", body: "Objects are written to storage in AWS ap-south-1 with server-side AES-256 encryption." },
      { title: "3. Know where it sits", body: "The storage region is stated rather than implied, and the sub-processors that receive document content are named in the DPA." },
    ],
    capabilities: [
      "AES-256 server-side encryption at rest; TLS 1.2 or later in transit",
      "Documents, exports and backups stored in AWS ap-south-1 (Mumbai, India)",
      "A customer-managed KMS key where the deployment configures one",
      "Self-hosted deployments keep data on your own infrastructure",
      "Sub-processors that receive document content are named in the DPA",
    ],
    faq: [
      { q: "Where exactly does my data live?", a: "Uploaded documents, data exports and database backups are stored in AWS ap-south-1 (Mumbai, India). Document content is also transmitted to the AI sub-processors listed in our DPA (model inference, embeddings, parsing — located in the United States) to perform the analysis you request. India holds no EU or UK adequacy decision, so EU/UK customers should review the transfer terms in the DPA before uploading personal data." },
      { q: "Can I choose a different region?", a: "Not on the hosted service — there is no per-organisation region routing. A self-hosted deployment runs on infrastructure you choose." },
    ],
  },
  {
    slug: "vault-sharing",
    title: "Shared document links",
    oneLine: "Per-document share links with an expiry, a view cap, granular permissions, and recipient-tagged watermarking.",
    long: [
      "Send a document to a counterparty or external advisor without putting it on a file-sharing service. Each link carries an expiry from one day up to a year, an optional maximum view count, and separate switches for whether the recipient may download the file, see the risk report, and see the summary.",
      "Turn watermarking on and every page of a shared PDF is stamped with the recipient's name or email, so a leaked copy points back at the share it came from. Revoke a link at any time and it starts returning gone rather than the document.",
    ],
    group: "vault",
    status: "live",
    how: [
      { title: "1. Pick a document", body: "Share any contract in your vault." },
      { title: "2. Set the controls", body: "Expiry in days, an optional view cap, and per-link permissions for download, risk report and summary." },
      { title: "3. Send or revoke", body: "Send the link. Revoke it whenever you like — the portal then returns gone rather than the file." },
    ],
    capabilities: [
      "Expiry from 1 to 365 days, defaulting to 14",
      "Optional maximum view count per link",
      "Separate permissions for download, risk report and summary",
      "Recipient-tagged diagonal watermark on shared PDFs",
      "Revocation at any time",
      "View count and last-viewed timestamp per link",
    ],
    faq: [
      { q: "Can I put a password on a link?", a: "No. Link secrecy plus the expiry, view cap and revocation are the controls; there is no separate passphrase." },
      { q: "Do I get a full log of who opened it and when?", a: "Not a per-view log. Each link carries a view count and a last-viewed timestamp — enough to see that it was opened and roughly when, not a record of each individual access with an IP address." },
      { q: "Does the watermark work on non-PDF files?", a: "No. Watermarking applies to PDFs; other file types are served unmodified." },
    ],
  },
  {
    // Rewritten 2026-09-18 to what the backend does: versions are uploaded
    // onto a chain (`POST /contracts/{id}/versions`, app/api/v1/contracts/
    // lifecycle.py; `link_new_version` in app/services/contract_lifecycle.py)
    // and compared clause-by-clause (app/api/v1/contracts/compare.py). The
    // previous copy — autosave versioning, one-click restore, comments per
    // version — described nothing in the codebase.
    slug: "version-history",
    title: "Version history",
    oneLine: "Chain successive drafts of a contract together, and compare any two clause by clause.",
    long: [
      "When a counterparty returns a revised draft, upload it as a new version of the contract you sent. The versions chain together with a number each, so the negotiation history sits in one place instead of as five files called final_v3_REVISED.",
      "Comparison is clause-level and semantic rather than a character diff: it reads both documents and reports what was added, removed and modified as a list of clause changes. That is the right shape for contracts, where a re-worded indemnity matters and a re-flowed paragraph does not.",
    ],
    group: "vault",
    status: "live",
    how: [
      { title: "1. Upload the new draft", body: "Link it to the contract it supersedes, and it becomes the next version in the chain." },
      { title: "2. List the versions", body: "Read the chain for a contract, or across the vault." },
      { title: "3. Compare", body: "Pick any two contracts — versions of the same document or not — and get a clause-level list of what changed." },
    ],
    capabilities: [
      "Numbered version chains linking successive drafts of a contract",
      "A new version is uploaded onto the chain and passes every check the first upload did — accept list, size, storage quota, antivirus — before the same analysis pipeline runs on it",
      "The new version hangs off the latest in the chain whichever version you uploaded against, inherits its matter, and lands in negotiation when the parent was not yet signed",
      "Clause-level semantic comparison of any two contracts",
      "Added, removed and modified counts plus the per-clause detail",
      "Redline templates carry their own version history with change notes, restore, and a unified text diff",
    ],
    faq: [
      { q: "Does it version automatically every time something changes?", a: "No. A version is created when a new draft is uploaded and linked to the one it supersedes — there is no autosave that snapshots a document as you work on it." },
      { q: "Can I roll a contract back to an earlier version?", a: "Not in the vault. Earlier versions remain available to read and compare, but there is no restore action on a contract. Redline templates do have restore." },
      { q: "Are comments preserved per version?", a: "There are no document comments to preserve — see the collaboration page for what does and does not exist there." },
    ],
  },
  {
    // Added 2026-10-08. Verified: services/documents.py::store_document,
    // core/documents/{categories,policy}.py (migration 221; India-only
    // subcategories are a 422 for a global org), saved views,
    // purge_expired_documents_task, contracts/bulk.py `analyse`.
    slug: "documents",
    title: "Documents & the Matter File",
    oneLine: "Every upload is a document with a category — agreement, evidence, court order, correspondence, identity — and the category decides what runs. Nothing is analysed unless you ask.",
    long: [
      "A document's category decides its processing: whether it is indexed for Vio, whether any of it may reach an AI vendor, and what analysis a person may ask for later. Only an agreement is offered contract review, and even an agreement can be stored without one — upload-only, on single, chunked and bulk ZIP uploads — so no scan is used until you press Analyse. Identity documents are never sent to an AI vendor: no chunks, no embeddings, no workflows.",
      "Every upload passes the same accept list and virus scan and is recorded on its matter's timeline, which is how a matter becomes its own file. Retention is a number your organisation sets per category; when it passes, the document is purged and the purge is audited on the matter. There is no product default for identity retention, because a number would be a legal statement. Saved views are filters, not folders.",
    ],
    group: "vault",
    status: "live",
    how: [
      { title: "1. Upload with a category", body: "Pick the matter and the category. Agreements can be stored upload-only." },
      { title: "2. Ask for analysis when you need it", body: "Run contract review on an agreement, or order analysis on a court order, at the point you need it." },
      { title: "3. Keep it tidy", body: "Save filtered views; set retention per category and let the purge run." },
    ],
    capabilities: [
      "Closed categories: agreement, evidence, court order, correspondence, identity and more",
      "Store-only uploads — no forced analysis, no scan used until you ask",
      "Upload-only on single uploads, the phone's chunked upload and bulk ZIP",
      "Identity documents never reach an AI vendor",
      "One upload path with the same accept list and virus scan everywhere",
      "Matter File: every upload on the matter's timeline",
      "Organisation-set retention per category, with audited purge",
      "Saved views as filters over the vault",
    ],
    faq: [
      { q: "Is every document analysed on upload?", a: "No. Only an agreement uploaded for review is analysed. Everything else is stored, and analysis runs when a person asks." },
      { q: "Are there folders?", a: "No. The matter is the container; saved views are filters." },
    ],
  },
  {
    // Added 2026-10-08. Verified: services/clm_overview.py,
    // services/contract_requests.py, services/clm_triage.py,
    // workflow_catalogue/clm_lifecycle.py (migrations 228, 237) — all
    // edition-neutral; no money threshold is a default.
    slug: "clm-dashboard",
    title: "CLM dashboard & Contract Request Center",
    oneLine: "A contracts dashboard where every figure states its basis, and a request desk where triage is decided by your rules, not a model.",
    long: [
      "The CLM dashboard covers your agreements: status breakdown, expiries, analysis and risk, review workload and cycle time. Each block carries a sentence saying how it was computed. A figure that cannot be computed is shown as empty with the reason; cycle time needs at least three contracts, because a median of two is an anecdote. Expiry tiles use the same rule as the vault filter they link to.",
      "The Contract Request Center takes a request for an NDA, a DPA, a vendor agreement, an amendment, a renewal or a termination, asking only the questions each type needs. Triage evaluates your Deal Desk rules: who must review and which signatory band applies, each naming its rule; with no rules set, legal review is always required and privacy review is added when the purpose names personal data. No money threshold is assumed. Values are kept in the currency typed, never converted. A submitted request can start the contract lifecycle workflow.",
    ],
    group: "vault",
    status: "live",
    how: [
      { title: "1. Raise a request", body: "Pick the type; answer its form; attach documents." },
      { title: "2. Triage by rule", body: "Your rules decide the reviews and signatory; an owner is assigned and gets a task." },
      { title: "3. Run the lifecycle", body: "Start the workflow; watch the contract on the CLM dashboard." },
    ],
    capabilities: [
      "Dashboard figures each with a basis sentence; empty when not computable",
      "Cycle time median and p90 over 365 days, from three contracts up",
      "Request types with their own forms; invalid fields named",
      "Rule-based triage — reviews and signatory band name their rule",
      "No default money threshold; no currency conversion",
      "Contract lifecycle workflow with role-gated steps, from request to archive",
    ],
    faq: [
      { q: "Does AI decide who approves a request?", a: "No. Your rules decide; a model never chooses the reviewers." },
      { q: "Can a request skip straight to a contract?", a: "A request moves only through its defined states; starting the workflow needs a submitted request." },
    ],
  },
  {
    // Verified 2026-09-18 against app/db/models/contract.py
    // (CONTRACT_STATUSES, RENEWAL_DECISIONS), app/services/contract_lifecycle.py
    // (TRANSITIONS, derive_auto_renews, recompute_notice_deadline,
    // apply_metadata_patch, record_renewal_decision), app/services/
    // proactive_analysis.py (the sweep: _EXPIRING_WINDOW_DAYS, _NOTICE_WINDOWS,
    // _NOTICE_TASK_WINDOW_DAYS), app/api/v1/contracts/lifecycle.py and
    // app/api/v1/vault/list.py. docs/CLM_GAP_AUDIT_2026-09-14.md is the audit.
    // Mounted bare on both surfaces; org-owned rows, no edition gate.
    slug: "contract-lifecycle",
    title: "Contract lifecycle",
    oneLine: "Every contract has a status, only legal moves between them, a notice deadline derived from the record, and a renewal decision written down.",
    long: [
      "Until September 2026 a contract in the vault was \"active\" from the day it was uploaded and nothing ever changed that. Now every contract carries one of thirteen statuses — draft, in review, in negotiation, pending approval, approved, pending signature, executed, active, expiring, expired, renewed, terminated, archived — and only the moves the lifecycle table allows are accepted; anything else is refused with a 409, whoever asked. The moves are driven by the modules: an approval outcome, the daily sweep that marks a contract expiring 30 days before its end date and expired the day after it, and archiving, which is its own role-gated action rather than a status you can type.",
      "The non-renewal notice deadline is derived, not guessed: expiry date minus the notice period, recomputed whenever either changes. The sweep flags it at 30, 7 and 1 days and, at 30, files one task — decide: renew, renegotiate or terminate — that is never filed twice. Whether a contract auto-renews is read from the renewal clause by a fixed phrase list and is either true or unknown, never false because the clause said nothing. The decision you make is recorded on the contract with who made it and when; it does not move the status, because deciding to terminate is not the termination.",
    ],
    group: "vault",
    status: "live",
    how: [
      { title: "1. Correct the record", body: "Edit the extracted metadata — parties, dates, value, governing law, renewal terms, notice period. The edit is stamped with who and when, an expiry you correct is marked as a person's reading rather than the model's, and the notice deadline is recomputed." },
      { title: "2. Let the modules move it", body: "An approval moves it to approved, or back to negotiation on rejection; the daily sweep moves it to expiring and expired; an explicit status call handles the rest, and refuses a move the table does not allow. Archiving needs the delete role." },
      { title: "3. Upload the next version", body: "The counterparty's returned draft or the signed copy is uploaded onto the chain, passes every check the first upload did, and becomes the next numbered version — a parent that is not yet signed puts it in negotiation." },
    ],
    capabilities: [
      "Thirteen statuses: draft, in review, in negotiation, pending approval, approved, pending signature, executed, active, expiring, expired, renewed, terminated, archived",
      "A transition table — a move it does not list is a 409, for a person, the Deal Desk and the sweep alike",
      "Every status change audited with before and after, scoped to the matter",
      "The daily sweep: expiring 30 days before the end date, expired the day after it",
      "Notice deadline = expiry date − notice period, derived and recomputed, never typed in",
      "Flags at 30, 7 and 1 days before the notice deadline, and one system-filed task at 30 days",
      "Auto-renews read from the renewal clause: true or unknown, never false by absence",
      "A renewal decision — renew, renegotiate, terminate, let lapse — recorded with who and when, separately from the status",
      "Metadata corrections stamped with the editor and the time; a corrected expiry is marked as a person's reading",
      "New versions uploaded onto the chain with the same accept list, size, storage-quota and antivirus checks as the first upload",
      "Archiving is a role-gated action; archived contracts are hidden from the vault unless you ask for them",
      "A contract lifecycle workflow from request and triage through monitoring to archive, with renewal, amendment, breach and termination as child workflows started when needed",
      "Termination flags confidentiality, IP, indemnity and data-protection clauses as surviving obligations",
    ],
    faq: [
      { q: "Can I set a contract to any status I like?", a: "No. The lifecycle table lists the moves that make sense — a draft can go into review, not straight to expired — and anything else is refused. You can archive from most states, and unarchive brings a contract back to active." },
      { q: "Does the product decide whether to renew?", a: "No. It derives the deadline and files the task; the decision is yours and is written on the record with your name. Deciding to terminate does not terminate anything — that status move is a separate, explicit step when it actually happens." },
      { q: "Is there e-signature?", a: "No. Pending signature and executed are statuses you move a contract through; nothing here sends a document out for signing." },
    ],
  },
  {
    slug: "folder-permissions",
    title: "Folder permissions",
    oneLine: "Not built. There are no folders in the vault and no per-folder access control.",
    long: [
      "The vault has no folder concept — no folder table, no folder on a contract, and therefore no per-folder access-control list. This page used to describe one. It was wrong.",
      "What organises documents today is matters (group a deal or a dispute's contracts, research and obligations together) and free-text tags. What controls access is organisation-level role permissions, which are per capability rather than per document set — see Roles and permissions.",
    ],
    group: "vault",
    status: "soon",
    how: [
      { title: "1. Not available yet", body: "There are no folders and no folder ACLs." },
      { title: "2. Group by matter instead", body: "A matter collects the documents, redline sessions, obligations, meetings and tasks for one piece of work." },
      { title: "3. Control access by role", body: "Permissions are granted per capability to a role, across the organisation." },
    ],
    capabilities: [
      "No folders, no per-folder permissions, no inherited ACLs — none of it exists",
      "Matters group related documents and work",
      "Free-text tags on contracts",
      "Saved views — stored filters over the vault, not folders",
      "Role-based permissions applied organisation-wide, not per document set",
    ],
    faq: [
      { q: "How do I restrict one sensitive matter to three people?", a: "You cannot, at document level, today. Permissions are per capability across the organisation rather than per folder or per matter. If that is a requirement, tell us — it is a real gap and worth knowing that it is one." },
    ],
  },
  {
    slug: "bulk-upload",
    title: "Bulk upload + ZIP ingest",
    oneLine: "Upload thousands of documents in one go with automatic parsing and indexing.",
    long: [
      "Migrating from another system, importing a back-catalogue, or onboarding diligence documents? Drop a ZIP and LexVio parses, indexes, and risk-scores every document in parallel.",
      "Progress visible in real time. Errors land in a triage queue you can re-process or skip.",
    ],
    group: "vault",
    status: "live",
    how: [
      { title: "1. Drop the ZIP", body: "Drag onto the vault. Parallel parsing begins." },
      { title: "2. Watch progress", body: "Live progress bar with per-document status." },
      { title: "3. Read the counts", body: "The job reports how many documents processed and how many failed." },
    ],
    capabilities: [
      "ZIP ingestion, three documents processed concurrently",
      "Progress polling with processed and failed counts",
      "Per-member virus scan, zip-bomb and path-traversal guards",
      "Documents land in the vault and become searchable in Nexus",
      "Resumable chunked upload for large files on unreliable connections",
      "Store-only option: agreements in the ZIP are stored without a review and use no scans until you ask; other categories are never analysed on upload",
    ],
    faq: [
      { q: "What's the size cap?", a: "500 MB and 200 files per ZIP, with a 100 MB limit on any single member and a 2 GB uncompressed ceiling." },
      { q: "What happens to files that fail to parse?", a: "They are counted as failures on the job. There is no triage queue and no re-process or skip action — re-upload the ones that failed." },
    ],
  },
  {
    slug: "watermarks",
    title: "Recipient watermarking",
    oneLine: "Stamp a shared PDF with the recipient's identity on every page, so a leaked copy points back at the share.",
    long: [
      "Switch watermarking on for a share link and every page of the PDF the recipient downloads carries a diagonal stamp naming them — their name if you gave one, otherwise their email. Different recipients of the same document get different files.",
      "This is a deterrent and a trace, not digital rights management. The stamp is drawn into the page and can be obscured by someone determined to obscure it, there is no hidden metadata tag behind it, and it applies to PDFs only.",
    ],
    group: "vault",
    status: "beta",
    how: [
      { title: "1. Create a share", body: "Turn the watermark switch on when you create the link." },
      { title: "2. Name the recipient", body: "The name or email you set on the share is what gets stamped." },
      { title: "3. Trace back", body: "A leaked copy carries the recipient it was issued to, so it points at one share." },
    ],
    capabilities: [
      "Diagonal per-page text watermark naming the share recipient",
      "Applied at download time, so each recipient's copy differs",
      "PDF only — other file types are served unmodified",
      "No firm logo, no hidden metadata tag, and no per-share opacity or placement control",
    ],
    faq: [
      { q: "Can I use my firm's logo as the watermark?", a: "No. The watermark is text naming the recipient. There is no image watermark." },
      { q: "Is the tag also embedded invisibly in the file?", a: "No. There is no XMP or metadata embedding — what you see stamped on the page is the whole mechanism. Anyone claiming a forensic trace beyond that would be overselling it." },
      { q: "Is this DRM?", a: "No. It does not prevent copying, printing or forwarding. It makes a leak attributable, which is a different and more modest thing." },
    ],
  },

  // ── Team & Enterprise ─────────────────────────────────────────
  {
    slug: "workspaces",
    title: "Workspaces",
    oneLine: "Not built. The organisation is the isolation boundary; matters are how work is grouped inside it.",
    long: [
      "There is no sub-organisation workspace with its own data boundary. This page described one, and it does not exist — no workspace entity, no per-workspace membership, no isolation between them.",
      "What is real: the organisation is the tenant, and every document, thread, obligation and search is scoped to it — that boundary is enforced at the query and is genuinely tight. Inside the organisation, a matter is the unit of work: it collects the contracts, redline sessions, obligations, hearings, meetings and tasks for one deal or dispute, with an owner and named collaborators. Members can also carry a free-text team label used to filter the member list, which is a label and not an access control.",
    ],
    group: "team",
    status: "soon",
    how: [
      { title: "1. Not available yet", body: "There are no workspaces to create." },
      { title: "2. One organisation is one boundary", body: "Data never crosses organisations. That is enforced on every query, not at the screen." },
      { title: "3. Use matters to group work", body: "A matter gathers everything belonging to one deal or dispute, with an owner and collaborators." },
    ],
    capabilities: [
      "No workspace entity, no per-workspace membership, no per-workspace data isolation",
      "The organisation is the tenant boundary and is enforced in every query",
      "Matters group contracts, redlines, obligations, hearings, meetings and tasks",
      "A free-text team label on a member, for filtering the roster only",
      "Billing and usage are reported per organisation, not per team",
    ],
    faq: [
      { q: "How do I keep two clients' documents apart in one firm account?", a: "By matter, which groups them — not by isolation, which would prevent one from being seen from the other. If you need hard separation between client portfolios, tell us; today the honest answer is separate organisations." },
    ],
  },
  {
    slug: "roles-permissions",
    title: "Roles & permissions",
    oneLine: "Four seeded roles with per-capability view / add / edit / delete permissions, plus custom roles you define.",
    long: [
      "Every organisation starts with four roles — Owner, Company Admin, Manager and Member — each holding permissions expressed per capability rather than per document. A permission is a capability key plus four verbs: view, add, edit and delete. That is what lets you say \"this person may read the tax module but not change anything in it\".",
      "You can create your own roles and set their permission matrix. Company admins can also grant or withhold individual features per member, which is the finer control most teams actually reach for.",
    ],
    group: "team",
    status: "live",
    how: [
      { title: "1. Assign a role", body: "On invitation or later. Owner, Company Admin, Manager and Member are seeded; you can add your own." },
      { title: "2. Set the matrix", body: "Per capability, choose view, add, edit and delete. Deciding on an AI review, for example, requires delete on the relevant capability." },
      { title: "3. Tune per member", body: "A company admin can switch individual features on or off for a specific member, on top of their role." },
    ],
    capabilities: [
      "Four seeded roles — Owner, Company Admin, Manager, Member",
      "Permissions per capability, with view / add / edit / delete verbs",
      "Custom roles with their own permission matrix",
      "Per-member feature assignment on top of the role",
      "Role and permission changes recorded in the append-only audit log",
    ],
    faq: [
      { q: "Is there a guest or read-only external role?", a: "Not as a named role. There is an external-counsel marker on a user that filters the member list, but it is not a permission set. For genuinely external recipients, use a share link with view-only permissions rather than an account." },
      { q: "Can I set permissions per folder or per matter?", a: "No. Permissions attach to capabilities across the organisation, not to a document set. See Folder permissions for the state of that." },
      { q: "Are custom roles an enterprise add-on?", a: "They are on multi-seat plans. Single-seat plans do not need them — one person holds everything." },
    ],
  },
  {
    slug: "realtime-collaboration",
    title: "Collaborative editing & edit proposals",
    oneLine: "Shared editing sessions on a document, and a propose-review-merge flow for changes to it.",
    long: [
      "Two people working on the same draft share an editing session that syncs the document state and records who is currently in it. Alongside that sits the part worth having: an edit proposal is a change one person suggests and another approves, rejects, or the author withdraws — a pull request over a contract clause, with an append-only history of what was proposed and what happened to it.",
      "Be clear about what is not here. There are no comment threads on a clause, no @mentions, and no live cursors showing where a colleague is typing. Notifications exist as a system and are used elsewhere in the product, but nothing routes a collaboration event into them.",
    ],
    group: "team",
    status: "beta",
    how: [
      { title: "1. Open a shared session", body: "The session syncs the document and records the users currently active in it." },
      { title: "2. Propose a change", body: "Rather than editing over someone, propose the change with a note on why." },
      { title: "3. Approve, reject or withdraw", body: "A reviewer approves and it merges, or rejects with a comment. Either way the decision is on the record." },
    ],
    capabilities: [
      "Shared editing sessions with a list of currently active users",
      "Edit proposals — propose, approve and merge, reject with a comment, or withdraw",
      "Append-only edit history of what was proposed and what was decided",
      "Redline templates have their own collaborative editing surface",
      "Comment threads, with replies, on each redline suggestion",
    ],
    faq: [
      { q: "Can I leave a comment on a clause and tag a colleague?", a: "Not on the document itself, and there are no @mentions. A redline suggestion has its own comment thread with replies, and an edit proposal carries a note — the reason attached to a specific proposed change." },
      { q: "Do I see my colleague's cursor moving?", a: "No. A session lists who is active; it does not stream cursor positions or selections." },
    ],
  },
  {
    // Rewritten 2026-09-18 against app/api/v1/matters/{routes,activity}.py,
    // app/db/models/matter.py, app/services/todo.py::create_task and
    // docs/TODO_MODULE_PLAN_2026-09-14.md §1. The previous copy — time
    // tracking, budgets, an exportable archive bundle, Aderant / Clio
    // webhooks — described nothing in the codebase.
    slug: "matter-management",
    title: "Matter management",
    oneLine: "Group contracts, research, obligations, meetings and tasks by matter — with a timeline of everything that happened on it.",
    long: [
      "A matter is an arbitrary collection — a deal, a dispute, a vendor relationship, a regulatory filing — that groups together the client, the documents, obligations, meetings, tasks and research links associated with it.",
      "The matter page has an activity timeline: every audited action scoped to the matter, in time order — a task created or completed, a meeting started, minutes generated, minutes exported, a contract's status changed, a renewal decided — merged with the AI calls made on the matter, each carrying the model and prompt version that produced its output. The timeline is read-only and the log behind it is append-only. Tasks on the matter page are the organisation's one task list filtered by matter; there is deliberately no separate matter-side list of next actions, so a task made anywhere shows up here and a task made here shows up everywhere.",
    ],
    group: "team",
    status: "live",
    how: [
      { title: "1. Create a matter", body: "Give it a name, an owner, a client, tags, and optionally a contract value with its currency." },
      { title: "2. Attach the work", body: "Documents, redline sessions, obligations, hearings, meetings, tasks and research links all hang off the matter." },
      { title: "3. Read the record, then close", body: "The timeline says what happened and who did it. Close the matter and the closing date is stamped, or archive it out of the active list — open tasks on it are cancelled when it is archived." },
    ],
    capabilities: [
      "One matter gathers documents, redline sessions, obligations, hearings, meetings, tasks and research links",
      "An activity timeline of every audited action on the matter — tasks, meetings, minutes, exports, contract status changes, renewal decisions — merged with the AI calls made on it",
      "Each AI entry on the timeline carries the model and prompt version that produced it",
      "The matter's tasks are the organisation's one task list filtered by matter — no second list to keep in step",
      "An assigned owner, a creator, and named collaborators",
      "A client record (not a typed name), tags, and a contract value with its currency",
      "A summary view across everything attached",
      "Close with a stamped closing date, or archive",
    ],
    faq: [
      { q: "Does a matter have its own to-do list?", a: "No, and it used to. The matter-side next-actions list was retired in September 2026 and its rows moved into the one task list. What the matter page shows is that list filtered by matter, so a task filed from a meeting, from Vio, or by the contract sweep appears here without anyone copying it." },
      { q: "Does it track billable time?", a: "No. There are no time entries, no rates and no budgets. A matter records a contract value, not hours." },
      { q: "Can I export the matter's documents?", a: "As a filing bundle: evidence, correspondence and court orders by default, in the order you set or by each document's own date, exported as one PDF stamped with annexure numbers and page numbers. See Matter Journey & filing bundle. Meeting minutes export individually, to PDF or Word." },
    ],
  },
  {
    slug: "audit-log",
    title: "Activity audit log",
    oneLine: "A searchable, append-only record of state-changing actions across your organisation.",
    long: [
      "State-changing actions — uploads, edits, shares, AI runs, billing and admin changes — are recorded with the actor, timestamp, IP and before/after state. Exportable to CSV for internal and compliance audits.",
      "Logs are organisation-scoped and admin-visible.",
    ],
    group: "team",
    status: "live",
    how: [
      { title: "1. Action happens", body: "Any user action is logged with user, timestamp, IP, and action type." },
      { title: "2. Search + filter", body: "Admin → Audit. Filter by user, date, action type, or document." },
      { title: "3. Export", body: "CSV export for internal audit or regulatory request." },
    ],
    capabilities: [
      "State-changing actions captured — uploads, edits, shares, AI runs, admin changes",
      "Append-only, enforced in the database — update is rejected outright and delete only under an explicit purge flag",
      "Actor type recorded — user, system, API key, SCIM, or webhook",
      "Before and after state captured on the change",
      "IP address, user agent and request id on every entry",
      "Filterable by action (exact or prefix), by actor, and by date range",
    ],
    faq: [
      { q: "Is it really append-only, or just not editable in the UI?", a: "Really. Database triggers reject any update to an audit row, and reject deletes unless the transaction explicitly sets a purge flag. It is not an application convention that a bug could bypass." },
      { q: "How long are entries kept?", a: "The retention position recorded in the product is seven years, in line with IT-enabled-services record-keeping. There is no tiering by action type and no purge job currently running." },
      { q: "Can I export to CSV or forward to my SIEM?", a: "Not today. There is no CSV export on the audit log and no audit event on the outbound webhook set. Read and filter it through the admin surface." },
    ],
  },
  {
    slug: "saml-sso",
    title: "SAML SSO",
    oneLine: "Not shipped. Connector configuration and per-IdP setup guides exist; SAML sign-in itself does not.",
    long: [
      "This is the page we most needed to correct. LexVio stores SAML connector configuration — your IdP entity id, sign-on URL, certificate, metadata XML and attribute mappings — with setup guidance for Okta, Entra ID, Google Workspace, OneLogin, JumpCloud, Ping and Auth0. That part is real and it looks convincing.",
      "What is not there is SAML itself: no assertion consumer endpoint, no SAML library, nothing that receives or validates a SAMLResponse, and therefore no just-in-time provisioning and no way to disable password login in favour of the IdP. The connector test checks that your metadata parses; it does not contact your IdP. If SSO is a procurement requirement, treat this as not delivered.",
    ],
    group: "team",
    status: "soon",
    how: [
      { title: "1. Not available yet", body: "There is no SAML sign-in flow to configure your users into." },
      { title: "2. What exists", body: "Storage for connector configuration and attribute mappings, with per-IdP setup guides." },
      { title: "3. What is missing", body: "The assertion endpoint, signature validation, JIT provisioning and SSO enforcement — that is, the feature." },
    ],
    capabilities: [
      "Connector configuration stored per IdP — entity id, sign-on URL, certificate, metadata XML, attribute mappings",
      "Setup guides for Okta, Entra ID, Google Workspace, OneLogin, JumpCloud, Ping and Auth0",
      "A validation check that your metadata parses — it does not contact your IdP",
      "No assertion endpoint, no JIT provisioning, no enforced SSO, no domain-based routing",
    ],
    faq: [
      { q: "Can my users sign in with Okta today?", a: "No. There is nothing to receive the assertion. Password sign-in is the live authentication path." },
      { q: "Can I force SSO and disable passwords?", a: "No. There is an enforcement flag in the data model, and the login path does not read it. Do not rely on it as a control." },
      { q: "What about SCIM?", a: "SCIM user provisioning is genuinely implemented and is independent of SAML — see the SCIM page, including its own plan note." },
    ],
  },
  {
    slug: "scim",
    title: "SCIM provisioning",
    oneLine: "SCIM 2.0 user provisioning from your IdP — create, update, deactivate and delete, with a provisioning log.",
    long: [
      "A standards-compliant SCIM 2.0 user endpoint at the canonical path, with the discovery documents an IdP expects — service provider configuration, resource types and schemas. Your IdP creates, updates, deactivates and deletes users, and every call is written to a provisioning log so you can see exactly what your IdP did and when.",
      "Group support is read-only and virtual: three groups exist, derived by filtering users on their role, so your IdP can read them and map a group to a LexVio role. Your IdP cannot create or modify a group here, and there is no workspace to map a group onto — see the Workspaces page for why.",
    ],
    planNote: "SCIM is fully implemented, but " + NOT_ON_PLAN,
    group: "team",
    status: "live",
    how: [
      { title: "1. Point your IdP at the endpoint", body: "Mint a bearer token in the admin surface and give your IdP the SCIM base URL." },
      { title: "2. Map to a role", body: "IdP group display names map to a LexVio role — company admin or member." },
      { title: "3. Watch the log", body: "Every provisioning call is recorded, so a failed sync is diagnosable rather than mysterious." },
    ],
    capabilities: [
      "SCIM 2.0 user endpoint — list, create, read, replace, patch and delete",
      "Deactivation via the active flag as well as hard delete",
      "Discovery documents — service provider config, resource types, schemas",
      "Read-only virtual groups mapping to a LexVio role",
      "Every provisioning call written to a log",
      "Bearer tokens stored hashed, with a visible prefix, mintable and revocable",
    ],
    faq: [
      { q: "Can my IdP create groups in LexVio?", a: "No. Groups are read-only and derived from user roles — your IdP can read them and use them to set a role, but not create or edit them." },
      { q: "Is there a token rotation button?", a: "Not a single action. Rotation is mint a new token, point the IdP at it, then revoke the old one." },
      { q: "Does it need SAML?", a: "No — SCIM works on its own, which is just as well, because SAML sign-in is not shipped." },
    ],
  },
  {
    slug: "white-label",
    title: "Firm branding",
    oneLine: "Store your logo and two brand colours. Today they reach your transactional email, not your exported reports.",
    long: [
      "You can store a logo, a favicon, a primary colour and an accent colour against your organisation. The one place they are currently applied is transactional email, where your logo replaces ours.",
      "Exported documents are not branded. The redline export, the risk report and the analysis exports all carry LexVio's own header, colours and footer, and none of them read your brand configuration. There is no footer-text field, no per-export toggle, and no way to remove the LexVio attribution from an export. The page used to promise all of that; it should not have.",
    ],
    planNote: "Branding configuration is stored but " + NOT_ON_PLAN,
    group: "team",
    status: "beta",
    how: [
      { title: "1. Upload your brand", body: "Logo, favicon, primary colour, accent colour." },
      { title: "2. It reaches email", body: "Transactional email to your users carries your logo." },
      { title: "3. Exports stay LexVio-branded", body: "PDF and DOCX exports use LexVio's own styling today. That is the gap." },
    ],
    capabilities: [
      "Logo and favicon stored per organisation",
      "Primary and accent colour — two values, not a palette",
      "Applied to transactional email",
      "Not applied to PDF or DOCX exports",
      "No footer text, no per-export-type toggle, no attribution removal",
    ],
    faq: [
      { q: "Can I send a client a risk report with my firm's branding on it?", a: "Not yet. Exports carry LexVio branding regardless of what you have configured. If white-labelled deliverables are the reason you are looking at this, it is not there." },
    ],
  },

  {
    // Added 2026-10-08. Verified: services/access_policy.py,
    // api/v1/org_structure.py (migration 242). A GSTIN on a global org is
    // 422 `gstin_india_only`; `tax_id` is the field. MFA is stored, not
    // enforced — the console's own enforcement list says so.
    slug: "access-policies",
    title: "Departments, branches & access policies",
    oneLine: "Departments and branches as records, and access policies — IP allowlist, allowed platforms, session lifetime — enforced on every request.",
    long: [
      "Departments and branches are rows your organisation owns; people and matters can be placed in a department, and a branch carries its own tax ID, with at most one head office. Access policies are checked on every signed-in request except sign-in and sign-out themselves, so a person can always get in and out.",
      "The checks are an IP allowlist, allowed platforms (web or the phone app), session lifetime counted from sign-in, and a device limit counted at phone sign-in. A policy that would lock out the admin saving it is refused. Multi-factor authentication can be recorded as a requirement but is not enforced — sign-in has no second factor yet, and the console says so.",
    ],
    group: "team",
    status: "live",
    how: [
      { title: "1. Structure", body: "Add departments and branches under organisation settings." },
      { title: "2. Set the policy", body: "Set the allowlist, platforms, session lifetime and device limit under users and permissions." },
      { title: "3. Enforced", body: "Requests outside the policy are refused; sign-in and sign-out stay open." },
    ],
    capabilities: [
      "Departments and branches; at most one head office",
      "A tax ID on each branch",
      "IP allowlist, proxy-aware",
      "Allowed platforms: web and the phone app",
      "Session lifetime from sign-in; phone refresh refused past the cap",
      "Device limit at phone sign-in",
      "Saving a policy that would lock you out is refused",
    ],
    faq: [
      { q: "Is MFA enforced?", a: "No. It can be recorded as a policy, but there is no second factor to enforce yet." },
      { q: "Is the session timeout idle time?", a: "No — it is the age of the session from sign-in." },
    ],
  },
  {
    // Added 2026-10-08. Verified: api/v1/reports.py, services/reports/*
    // (migration 241). India-only blocks (statutory filings, reg-change
    // alerts, corpus research) are absent for a global org, not empty.
    slug: "reports",
    title: "Reports & custom report builder",
    oneLine: "Compliance, client, matter, research and legal-operations reports, plus a custom builder — every figure with its basis, every report as CSV.",
    long: [
      "Five reports are computed when you open them: compliance, clients, matters, research and legal operations, over a period in your own local days. Every KPI carries a sentence saying what it counts, and a median or percentile over too few rows is left empty with the reason rather than shown as a number.",
      "The custom builder lets you choose a source, columns, filters, one group-by and a sort, from a fixed list per source; what you type reaches the query only as a value. Save a report definition to run again. Every report downloads as CSV with the same data you see.",
    ],
    group: "team",
    status: "live",
    how: [
      { title: "1. Pick a report", body: "Open Reports & Analytics on the desk." },
      { title: "2. Set the period", body: "Periods are your local days." },
      { title: "3. Build or export", body: "Save a custom report, or download any report as CSV." },
    ],
    capabilities: [
      "Compliance, client, matter, research and legal-operations reports",
      "Basis sentence on every KPI; too-small samples left empty",
      "Custom builder over a fixed set of sources, columns and filters",
      "Saved report definitions",
      "CSV export",
    ],
    faq: [
      { q: "Can I write my own SQL?", a: "No. The builder offers a fixed list per source so nothing you type becomes part of the query." },
      { q: "Why is a median blank?", a: "Because it was computed over too few rows to mean anything. The report says so rather than showing a number." },
    ],
  },

  // ── Workflow Automation ───────────────────────────────────────
  {
    // Withheld 2026-10-08. The agent library is shared content, and every
    // seeded template is tagged `jurisdictions = ["IN"]` (migration 186:
    // "Everything seeded to date is Indian"), so `GET /agents/templates`
    // returns none of them to a global org through `shared_array_clause`.
    // A page selling 53 agents (GSTR-2A reconciler, ROC deadlines) to a
    // product whose library is empty is the §1A defect this tag exists for.
    // Untag it in the same change that seeds and tags global templates.
    slug: "prebuilt-agents",
    market: "IN",
    title: "Pre-built agents",
    oneLine: "53 ready-made agents — vendor onboarding, NDA quickscan, GSTR-2A reconciler, weekly compliance digest — install and run.",
    long: [
      "A seeded library of 53 agents across contract review, compliance, tax, litigation, client intake, research, finance operations and onboarding. Install one and it becomes your agent: you own the definition, can edit it, and the original stays in the library.",
      "Read the trigger honestly. Agents run when you run them, or when mail arrives at the agent's own inbox address. A schedule can be stored on an agent, and several library agents carry one, but nothing dispatches on it today — see Scheduled runs, which says so plainly.",
    ],
    group: "workflows",
    status: "live",
    how: [
      { title: "1. Browse the library", body: "53 agents by category, filtered to the content that applies to your jurisdiction." },
      { title: "2. Install", body: "The template becomes an agent in your organisation, with its steps as the starting definition." },
      { title: "3. Run it", body: "Run it on demand, or give it an inbox address and forward work to it by email." },
    ],
    capabilities: [
      "53 seeded agents — contract review (10), compliance (10), tax (8), litigation (5), client intake (5), research (5), finance ops (4), onboarding",
      "Named examples that genuinely exist: Vendor Onboarding Check, NDA Quickscan, Weekly Compliance Digest, GSTR-2A Reconciler",
      "Install as your own agent and edit the definition",
      "Per-agent email inbox — forward a document and the agent runs on it",
      "Output steps: email, in-app notification, WhatsApp, or an outbound HTTP call",
      "Per-run record with status, error, output and each step's result",
    ],
    faq: [
      { q: "Do agents run on a schedule?", a: "Not today. A cron expression can be stored on an agent and nothing reads it — the dispatcher does not exist. Manual and email triggers work." },
      { q: "Can output go to Slack or Teams?", a: "No. Email, in-app, WhatsApp and a signed outbound webhook are the delivery steps. Slack appears in the product only as a connection you can store; nothing posts to it, and Teams is not integrated at all." },
      { q: "Does the GSTR-2A reconciler talk to the GST portal?", a: "No. It is an agent that reasons over purchase invoices you supply. There is no GSTN or GSP connection anywhere in the product." },
    ],
  },
  {
    slug: "custom-agents",
    title: "Custom workflow agents",
    oneLine: "Define your own multi-step agent — AI calls, data queries, HTTP calls and notifications, run in sequence.",
    long: [
      "Build an agent from steps that execute in order: an AI call, a database query, an outbound API call, a wait, a condition that stops the run when it does not hold, and delivery steps for email, in-app notification or a signed webhook. Save it and the definition is versioned; test it against sample input and the run is rolled back so nothing is written.",
      "Two limits worth stating rather than discovering. Steps run in sequence — a condition that fails stops the run, it does not branch to an alternative path. And two step types you can save, loop and human approval, are not executed by the engine: they will validate when you save and fail when they run.",
    ],
    planNote: "Building your own agent is gated on a feature key, and " + NOT_ON_PLAN,
    group: "workflows",
    status: "beta",
    how: [
      { title: "1. Pick a trigger", body: "Manual run or an email inbox address. Cron, webhook, contract-upload and compliance-alert triggers can be saved but are not dispatched." },
      { title: "2. Chain steps", body: "AI call, database query, API call, condition, wait, then email, in-app or webhook out. Data passes from step to step." },
      { title: "3. Test, then save", body: "A test run executes the real engine against your sample input and rolls the transaction back. Saving creates a new numbered version." },
    ],
    capabilities: [
      "Executable step types — AI call, database query, API call, condition, wait, email, in-app notification, outbound webhook",
      "Six trigger types definable; manual and email are the two that dispatch",
      "Test run against sample input, rolled back so nothing persists",
      "Numbered versions on every save",
      "Per-run record with each step's result and any error",
      "Publish an agent to the agent marketplace, or install one another organisation on this product published",
    ],
    faq: [
      { q: "Can a step branch to a different path?", a: "No. Steps run in order; a condition that does not hold ends the run rather than routing elsewhere. Success and failure targets can be declared on a step and the engine does not act on them." },
      { q: "Can I roll back to an earlier version?", a: "Versions are kept and numbered, but there is no endpoint to activate an earlier one. Rollback means editing forward." },
      { q: "What about the loop and human-approval steps?", a: "Both have config schemas and neither is implemented in the engine — a run reaching one fails. Use Deal Desk for a real approval gate on a contract." },
    ],
  },
  {
    // Withheld 2026-10-08 with the Intelligence API: a webhook subscription
    // belongs to an Intelligence API key, and the only events live code emits
    // are research events over the Indian corpus (`/research` is mounted
    // `require_india_edition`). On this product there is nothing to subscribe
    // to and no key to subscribe with.
    slug: "webhooks",
    market: "IN",
    title: "Webhooks",
    oneLine: "HMAC-signed outbound webhooks with retries and a delivery log. Six research events fire today.",
    long: [
      "Register an HTTPS endpoint and LexVio POSTs signed JSON to it, with the signature, the event name, a delivery id and the attempt number in the headers. Failures retry on a widening schedule — 30 seconds, 2 minutes, 10 minutes, an hour, six hours, a day — and a subscription that keeps failing is disabled rather than left hammering a dead endpoint. Every attempt is recorded and readable.",
      "Be precise about what actually fires. Twelve event types can be subscribed to; six of them are emitted by live code, all of them research events — a thread created, saved, unsaved or deleted, and a memory created or deleted. The judgment, statute, cause-list, clause-benchmark and compliance-alert events are declared and subscribable and nothing publishes them. The product's own source says that is a promise it should not be making, and we agree.",
    ],
    planNote: "Webhook subscriptions belong to an Intelligence API key, and " + NOT_ON_PLAN,
    group: "workflows",
    status: "beta",
    how: [
      { title: "1. Register an endpoint", body: "An HTTPS URL, checked against server-side request forgery on create and on update. The signing secret is returned once." },
      { title: "2. Pick events", body: "Subscribe to the event types you want. Check the list below for which of them actually fire." },
      { title: "3. Receive and verify", body: "Verify the HMAC-SHA256 signature over the body, then act. Read the delivery log to see every attempt." },
    ],
    capabilities: [
      "HMAC-SHA256 signature over the payload, with event, delivery id and attempt headers",
      "Six retries on a widening schedule out to 24 hours, then auto-disable after sustained failure",
      "Delivery log per subscription showing every attempt",
      "Rotate a subscription's signing secret",
      "Emitted today: research thread created / saved / unsaved / deleted, research memory created / deleted",
      "Declared but never emitted: judgment created and updated, statute amended, cause-list entry, clause-benchmark updated, compliance alert raised",
    ],
    faq: [
      { q: "Can I get a webhook when a contract scan finishes?", a: "No. There is no scan-complete event, and no deadline, document-share or AI-run event either. The six events that fire are research events." },
      { q: "What if my endpoint is down?", a: "We retry six times over 24 hours. After sustained failure the subscription is disabled. There is no manual replay endpoint — re-enable and wait for the next event." },
      { q: "Are these on my normal account?", a: "No. Webhook subscriptions hang off an Intelligence API key, which is subject to the plan note above." },
    ],
  },
  {
    slug: "scheduled-runs",
    title: "Scheduled runs",
    oneLine: "Not shipped. A cron expression can be saved on an agent, and nothing dispatches it.",
    long: [
      "An agent can carry a cron expression. No scheduler reads it. The task that would run an agent on a schedule exists and is registered with the job runner, and no code path ever enqueues it — so a cron-triggered agent never fires.",
      "We would rather correct this page than leave a schedule sitting in an agent looking as though it is doing something. Plenty of scheduled work does run inside LexVio — obligation reminders, the morning task digest, contract expiry and notice-deadline sweeps, health-score snapshots, retention purges — but none of it is a customer-defined agent schedule.",
    ],
    group: "workflows",
    status: "soon",
    how: [
      { title: "1. Not available yet", body: "There is no working agent scheduler." },
      { title: "2. Run agents manually or by email", body: "Run on demand, or forward a document to the agent's own inbox address." },
      { title: "3. Where scheduling does work", body: "Contract obligations and tasks have real reminder jobs, and the contract lifecycle sweep flags expiries and notice deadlines on its own." },
    ],
    capabilities: [
      "A cron expression can be stored on an agent; nothing reads it",
      "No friendly schedule presets",
      "No schedules tied to statutory due dates",
      "Manual and email triggers work today",
      "Per-run records exist for the runs that do happen; there is no re-run action",
    ],
    faq: [
      { q: "If I set a cron on an agent, will it run?", a: "No. It will be saved and never dispatched. That is the whole of this page." },
      { q: "Is there a statutory filing calendar?", a: "Not on this product. Contract-derived deadlines — obligations, expiries, notice deadlines — are tracked and reminded; statutory filing dates are not." },
    ],
  },
  {
    slug: "approval-workflows",
    title: "Approval workflows",
    oneLine: "A multi-step approval chain on a contract, routed by role and value — plus a review gate on AI output.",
    long: [
      "Deal Desk is the real approval feature. A policy defines an ordered ladder of steps, each requiring a role and a minimum number of approvals, with conditions on contract value that you set — no default ladder is seeded on this product, so no money threshold is assumed for you. A contract raises an approval request, steps activate in order, and each decision records who made it, when, and their note.",
      "Separately, AI review is a gate at the other end: hold a class of AI output above a risk threshold until a human approves it, with deciding authority tied to the permission matrix. It is off by default and is the control to reach for when AI work product must not leave the building unreviewed.",
      "One thing to know: an approval step can carry a deadline and nothing enforces it. There is no auto-approve, no auto-reject and no escalation when a step goes past its date — the deadline is shown, not acted on. And approval gates inside an agent workflow do not exist; that step type is declared and unimplemented.",
    ],
    group: "workflows",
    status: "beta",
    how: [
      { title: "1. Define the policy", body: "Ordered steps, each with a required role, a minimum number of approvals, and a value condition you choose." },
      { title: "2. Raise the request", body: "A contract enters the chain. Steps activate in order; each carries a deadline if the policy sets one." },
      { title: "3. Decide", body: "Approve or reject with a note. Who decided, when and why is recorded. Cancel the request to stop it." },
    ],
    capabilities: [
      "Ordered approval steps with a required role and a minimum approval count per step",
      "Value-conditioned routing on thresholds you set — none is a default",
      "Decision, decider, timestamp and note recorded on every step",
      "Cancel a request in flight",
      "AI review — hold AI output of a given kind above a risk floor for human approval, off by default",
      "Edit proposals give a single-reviewer approve, reject or withdraw gate on document changes",
    ],
    faq: [
      { q: "What happens if a step misses its deadline?", a: "Nothing automatic. The deadline is stamped and displayed; there is no job that auto-approves, auto-rejects or escalates." },
      { q: "Can I put an approval gate in the middle of an agent?", a: "No. A human-approval step type exists in the agent schema and the engine does not implement it — a run that reaches one fails. Approvals live on contracts and on AI output, not inside agent runs." },
      { q: "Can an approver leave a comment?", a: "Yes. Every decision carries a note, and it is part of the record." },
    ],
  },

  // ── AI Controls & Guardrails ──────────────────────────────────
  {
    // Backed by `app/api/v1/copilot_citations.py` (the three verdicts, the
    // global-org routing to the vault, and "No feature gate … Auth still
    // required") and `app/services/vault_citations.py` (containment, the
    // ambiguous cap on a title match, MIN_QUOTE_CHARS). The backend's own
    // edition manifest draws the line this entry sits on: citation
    // intelligence over the judgment corpus is India-only, "distinct from
    // POST /copilot/citations/preview, which serves both editions because the
    // vault resolver reads the org's own documents" (`core/nav_manifest.py`).
    slug: "citation-verification",
    title: "Citation verification",
    oneLine: "Every citation comes back with a verdict — verified, ambiguous or unresolved — and a match on title alone is never verified.",
    long: [
      "A citation that looks professionally formatted is not necessarily correct, so every source LexVio cites is resolved before you rely on it and the answer is one of three words. Verified means the source itself was checked and contains what was cited. Ambiguous means something by that name was found but nothing confirms the substance of the claim — a lead, not a source. Unresolved means nothing matched, which is not the same as wrong.",
      "Your own record is the corpus that can be checked anywhere, so that is where citations resolve: your executed contracts, prior matters and playbook. A quoted clause is confirmed by containment — the words are in your document or they are not — and deliberately never by similarity, because similarity lets an invented clause verify against a genuine one on the same subject. LexVio holds no case-law or statute corpus for the jurisdictions this product serves; when a citation cannot be resolved it says so and sends you to the official source rather than dressing a near-match up as a confirmation.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Ask, and get sources back", body: "Citations render as chips on the answer. Resolving one is a single indexed lookup — no second model call, no embedding round-trip." },
      { title: "2. The source is checked, not resembled", body: "A quotation is matched by containment against the text of your own documents and returns the document, the page and the surrounding context. A reference to a document by name is matched on title only, and is labelled as such." },
      { title: "3. Read the verdict and the reason", body: "Verified, ambiguous or unresolved, with a plain-English caution whenever it is not verified. A document existing under that name caps the verdict at ambiguous." },
    ],
    capabilities: [
      "Three verdicts on every citation — verified, ambiguous, unresolved — never a bare link",
      "Quoted clauses confirmed by containment against your own documents, with the page and surrounding text",
      "Similarity matching is deliberately not used to verify a quotation",
      "A match on title alone caps at ambiguous, however close the title",
      "A plain-English reason accompanies every verdict that is not verified",
      "Verdicts are recorded, so what you were shown at the time you relied on it stays on the record",
      "No plan gate — it runs on every answer, on every account",
    ],
    faq: [
      { q: "Does \"unresolved\" mean the citation is false?", a: "No. It means nothing we could check matched it — a document may simply never have been uploaded. The verdict is a statement about what could be checked, not about the citation's truth." },
      { q: "Can a case or a statute come back verified?", a: "Not from us. LexVio holds no case-law or statute corpus for the jurisdictions this product serves, and rather than return a near-match it says so and points you at the official source. A citation to a document in your own vault can be verified, because the source itself is there to check." },
      { q: "Why isn't a title match enough?", a: "Because a document by that name existing is not that document saying what was claimed. Treating the two as equivalent is precisely how a fabricated citation comes to rest on a real source." },
    ],
  },
  {
    // Backed by `app/jurisdiction/adapters/` (nine registered in
    // `adapters/__init__.py::register_default_adapters`, each module carrying
    // the licence it relies on), `app/jurisdiction/registry.py` (has_adapter
    // is True for eight; NL is False and says why), and
    // `services/verification.py` (the verdict vocabulary, "unverified is the
    // default and it is not a failure", and `publication_blockers`).
    //
    // STATUS IS `soon` ON PURPOSE, and it is the honest one. The machinery
    // runs — profiles have been generated and verified against these sources
    // — but publication is a named human signing off, and no profile has been
    // signed off yet, so `GET /jurisdictions` returns nothing to a customer
    // (`docs/GLOBAL_PERSPECTIVE_AUDIT_2026-09-02.md`: "17 profiles generated
    // + verified on live, zero published"; WORKPLAN: "The five corpus
    // publishes … still need a human with production credentials"). Marking
    // this `live` would be a new false claim of exactly the class this file's
    // header exists to prevent. Move it to `live` in the same change that
    // records the publication, not before.
    slug: "statute-verification",
    title: "Statute verification against official sources",
    oneLine: "Each legal claim we hold about a jurisdiction is checked against that government's own legislation service — and where it cannot be, the answer is \"unverified\", not silence.",
    long: [
      "A model can produce a section number that reads perfectly and does not exist. So every legal assertion LexVio holds about a jurisdiction is written as its own claim with its own citation, and each claim is checked against the official legislation service for that jurisdiction before it can reach the product. Nine sources are wired: legislation.gov.uk, the Irish Statute Book, CELLAR for EU instruments, leginfo for California, gesetze-im-internet for the German BGB, the Federal Register of Legislation for Commonwealth Australia, the Department of Justice's own published datasets for Hong Kong, e-Laws for Ontario, and wetten.overheid.nl for the Netherlands.",
      "Eight of those can return a verdict of verified. The Netherlands adapter cannot, and is not meant to: no official English text of the Burgerlijk Wetboek exists to compare wording against, so it confirms a cited article exists and stops there. Everywhere else the verdict is unverified — an honest answer about what could be checked, not a finding that the claim is false. Publication is a separate step and a human one: a named person signs off what the product asserts about a country's law, because a job that published its own output would remove the only link in the chain that is not a model.",
    ],
    group: "ai-controls",
    status: "soon",
    how: [
      { title: "1. Each assertion is its own claim", body: "A jurisdiction's profile is written as atomic claims, each carrying its own citation, so a verdict can land on one assertion rather than on a whole country at once." },
      { title: "2. The source is queried, per claim", body: "A targeted lookup at the official legislation service — never a bulk crawl, and only where the licence permits automated access at all. The provision text has to come back, and the claim's substantive terms have to be in it." },
      { title: "3. The verdict is kept, including the bad news", body: "Locating the Act but not the wording caps at unverified. An unreachable source is unverified, never contradicted. A claim a source actually disagrees with is treated as a fabrication and blocks publication until it is removed or corrected." },
    ],
    capabilities: [
      "Nine official legislation sources wired, each behind a licence read before the adapter was written",
      "Eight jurisdictions where a claim can reach verified — the UK, Ireland, EU instruments, California, Germany (BGB), Commonwealth Australia, Hong Kong and Ontario",
      "The Netherlands adapter confirms a cited article exists and by design never reaches verified — there is no official English text of the BW to check wording against",
      "Locating the instrument is not confirming the claim: verified needs the provision text and the claim's substantive terms in it",
      "An unreachable source returns unverified, never contradicted — an outage cannot manufacture a fabrication",
      "Coverage is stated per jurisdiction rather than implied: the public catalogue names which ones have a live verifier",
      "Confidence is the verified proportion of claims — not the model's own confidence, which measures fluency",
      "Air-gapped deployments register no adapter at all, since an adapter reaches a third-party host, and every claim then reads unverified",
    ],
    faq: [
      { q: "Does \"unverified\" mean the claim is wrong?", a: "No. It means no source we can lawfully query settled it. Most jurisdictions publish no free machine-readable corpus, and saying so is more useful than downgrading the claim to false or quietly promoting it to true." },
      { q: "Why only these jurisdictions?", a: "Terms of use, not effort. Several legal-information providers forbid programmatic access outright. One of them is recorded in our own codebase as a deliberate refusal rather than a gap — that row moves when a licence does, not when someone writes more code." },
      { q: "Who decides a jurisdiction is ready?", a: "A named person. Verification produces the evidence; it does not grant permission. There is no auto-publish, and a claim that a source contradicted must be removed or corrected before anything ships." },
    ],
  },
  {
    // 2026-10-08: the earlier copy described a per-organisation opt-in toggle
    // and "future training runs". Neither exists: LexVio trains no model, and
    // the backend has no training pipeline for customer content to feed.
    slug: "no-training-on-your-data",
    title: "No model training on your data",
    oneLine: "We do not train models on customer content. There is no opt-in, because there is no training programme.",
    long: [
      "Your documents are not used to train or fine-tune any model. That is not a setting you have to find and switch off — there is no training pipeline in the product, and no per-organisation opt-in toggle. LexVio trains no model of its own: the governing-law context an analysis needs is supplied to the model with each request, not learnt from anyone's documents.",
      "The models we send content to are third-party APIs — Anthropic for the main analysis path, OpenAI for embeddings, speech and part of the multi-agent path, and Google for the other part. Their API terms, not their consumer terms, govern that content. The current list is published on our privacy policy page, which is generated from live configuration rather than maintained by hand.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Nothing to configure", body: "There is no training toggle, because there is no training on your content." },
      { title: "2. Know who sees it", body: "Three model vendors receive content on different paths. All three are disclosed and all three are disabled outright in air-gapped deployments." },
      { title: "3. Check the list yourself", body: "The sub-processor list is unauthenticated and derived from live configuration — read it rather than take our word for it." },
    ],
    capabilities: [
      "No model training or fine-tuning on customer content",
      "Three model vendors, all disclosed: Anthropic, OpenAI (embeddings, speech and multi-agent), Google (multi-agent)",
      "Sub-processor list generated from live configuration, published unauthenticated",
      "Every model vendor hard-disabled when the deployment is air-gapped",
      "No per-organisation training toggle — there is nothing for one to control",
    ],
    faq: [
      { q: "Is there an opt-in I could turn on?", a: "No. If we ever build one, it will appear here and in the DPA before it appears in the product." },
      { q: "What about telemetry?", a: "Error monitoring and product analytics are gated behind a single flag that is off in air-gapped deployments. Model spend and token counts are logged per request against your organisation, which is your data about your usage, not your content." },
    ],
  },
  {
    slug: "prompt-injection-defence",
    title: "Prompt injection defence",
    oneLine: "User-pasted text is sanitised before reaching the LLM.",
    long: [
      "Prompt injection — where adversarial text in a document tries to hijack the AI ('ignore previous instructions and …') — is a real attack class. LexVio sanitises user-pasted text and uploaded document content through a hardened input layer before it reaches the LLM.",
      "Document content is also clearly labelled to the model as 'untrusted input', so the model treats it as data, not instructions.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Document arrives", body: "Upload or paste. Text extraction kicks off." },
      { title: "2. Sanitisation pass", body: "Known injection patterns are neutralised. Suspicious patterns are flagged." },
      { title: "3. Labelled to the LLM", body: "Document content is wrapped in clear delimiters and labelled as untrusted input. The model knows not to follow instructions inside it." },
    ],
    capabilities: [
      "Thirteen injection patterns, graded medium, high and critical",
      "High and critical spans are defanged in place and relabelled as quoted text, not an instruction",
      "Control characters and zero-width and bidirectional Unicode stripped",
      "Document content wrapped in explicit delimiters with an untrusted-content rule appended to the system prompt, on the paths that separate it out — the contract pipeline and the public scanner among them",
      "Detections recorded server-side for our own monitoring",
    ],
    faq: [
      { q: "Is this provably injection-proof?", a: "No defence is perfect, and this one has a limitation we will name: every pattern is English. An injection written in another language will not match them, though the delimiter-and-labelling layer still applies." },
      { q: "Can I see attempts against my own documents?", a: "Yes, since October 2026. AI Administration › AI Security lists prompt-injection detections, blocked requests and refusals with a kind and a label. The prompt itself is never stored — only a fingerprint of it." },
      { q: "Is every model call wrapped as untrusted?", a: "Sanitisation runs on user text on every call. The explicit untrusted-content wrapper applies where a caller separates document content from the instruction — the contract pipeline and the public scanner do; not every call site does." },
    ],
  },
  {
    slug: "cost-guardrails",
    title: "Per-org cost guardrails",
    oneLine: "A monthly AI token quota per plan, an 80% warning by email and in-app, and a hard stop at 100%.",
    long: [
      "Your plan carries a monthly AI token allowance — 500,000 on Starter, 2.5 million on Pro, 7 million on Business, 50 million on Enterprise. Usage counts input, output and cache-creation tokens in full and cache reads at a tenth, so caching genuinely stretches the allowance. Cross 80% and owners and admins get an email and an in-app notification, once per calendar month rather than every day. Reach 100% and further AI calls are refused with a clear payment-required response until the month rolls over or the allowance is raised.",
      "Two corrections to what this page used to say. The allowance is a token quota, not a money ceiling — a daily spend cap is a separate, optional setting an admin can add in AI Administration, and the platform keeps its own daily cap on trial accounts with no plan, as runaway-spend protection. And there is no overage mode: 100% is a stop, not a meter.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. The allowance comes with the plan", body: "Nothing to configure. Per-organisation increases are possible by arrangement." },
      { title: "2. Warning at 80%", body: "Email and in-app to owners and admins, once per calendar month." },
      { title: "3. Hard stop at 100%", body: "Further AI calls are refused with a payment-required response naming the quota, until the month resets." },
    ],
    capabilities: [
      "Monthly token allowance per plan, with per-organisation overrides available",
      "Cache reads counted at a tenth, so prompt caching stretches the allowance",
      "One 80% warning per calendar month, by email and in-app, to owners and admins",
      "Hard stop at 100% with an explicit response, not a silent failure",
      "Race-safe reservation, so concurrent requests cannot both slip past the ceiling",
      "A platform daily cap that applies to trial accounts without a plan",
    ],
    faq: [
      { q: "Can I set my own spending ceiling?", a: "Yes, since October 2026. An admin can set a daily AI spend cap, in your billing currency, in AI Administration; once today's spend reaches it, further AI calls are refused until the next day (UTC). Speech transcription is a separate path and is not covered by the cap. See AI Administration." },
      { q: "Is there an overage mode?", a: "No. At 100% the answer is a stop. There is no pay-past-the-cap setting." },
      { q: "Do warnings go to Slack?", a: "No — email and in-app only. Nothing in the product posts to Slack." },
    ],
  },
  {
    slug: "prompt-caching",
    title: "Prompt caching",
    oneLine: "Stable prompt prefixes are cached. Faster responses, lower bill, passed to you.",
    long: [
      "We cache the stable prefix of our prompts — system instructions, jurisdiction context, firm style — against Anthropic's prompt cache, sending the stable half marked cacheable and the variable half unmarked. A cache read is billed by Anthropic at a fraction of a fresh input token, and it counts against your monthly allowance at a tenth.",
      "Your usage breakdown shows cache-read and cache-creation tokens separately from ordinary input and output, so you can see the caching working. Be aware of one limit in the cost figure: the cost estimate is computed from input and output tokens and does not currently price cache reads or cache writes, so the saving shows up honestly in the token counts and approximately in the money.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. First call", body: "Cache miss — full cost, full latency." },
      { title: "2. Subsequent calls", body: "Cache hit on the stable prefix — ~10% cost, ~half latency." },
      { title: "3. See it in your usage log", body: "Cached vs uncached tokens broken out per request, so you can verify the savings." },
    ],
    capabilities: [
      "Stable-prefix caching against Anthropic's prompt cache",
      "Cache-read and cache-creation tokens broken out in your usage figures",
      "Cache reads counted at a tenth against your monthly token allowance",
      "The cache lifetime is Anthropic's ephemeral default, not a LexVio setting",
    ],
    faq: [
      { q: "Does my document content get cached?", a: "No — only the stable prefix (system instructions, jurisdiction context, firm style). Your document content is in the variable half and is not marked cacheable." },
      { q: "Does the cost figure reflect the caching saving?", a: "Not precisely. The cost estimate is derived from input and output tokens and does not price cache reads or writes. The token breakdown is the accurate view of what caching saved you." },
    ],
  },
  {
    slug: "token-caps",
    title: "Token cap per request",
    oneLine: "A 200,000-token ceiling on any single model call, and a 150,000-character ceiling on the text sent for contract analysis.",
    long: [
      "No single model call may exceed 200,000 input tokens. A call estimated over that is refused rather than silently trimmed, which keeps a runaway input from becoming a runaway bill. Output is bounded separately at the model's own wire limit.",
      "Contract analysis has its own ceiling: the text sent for analysis is capped at 150,000 characters, and the cut is a plain head slice — the first 150,000 characters, not a section-aware selection. On a very long contract that means late-document clauses can fall outside the analysed text, and governing law and dispute resolution are exactly the clauses that tend to live at the end. The truncation is logged on our side and is not currently reported back in the response. We would rather you knew that than assumed the whole document was read.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Input checked", body: "A call estimated above 200,000 input tokens is refused, not trimmed." },
      { title: "2. Analysis text capped", body: "Contract analysis sends the first 150,000 characters of the extracted text." },
      { title: "3. Split long documents deliberately", body: "For a very long contract, split it into logical parts and analyse each, so nothing important is silently outside the window." },
    ],
    capabilities: [
      "200,000-token ceiling on any single model call, enforced by refusal",
      "150,000-character ceiling on the text sent for contract analysis",
      "Head-slice truncation — not section-aware, and not reported in the response",
      "Output bounded at the model's wire limit",
    ],
    faq: [
      { q: "What happens to a 1,000-page contract?", a: "The first 150,000 characters are analysed and the rest is not, without a notice on the report. Split it into parts, or use bulk analysis with the document already divided, so that every section is inside a window." },
      { q: "Can I raise the caps for my organisation?", a: "No. These are product-wide constants, not per-organisation settings. Your monthly token allowance is the thing that can be raised." },
    ],
  },
  {
    // Rewritten 2026-09-18 against app/api/v1/billing/usage.py (current
    // month, by request_type and model, 30-day series, `cost {amount,
    // currency}` by `currency_for_org`, `cost_unpriced_requests`),
    // app/core/ai/pricing.py (USD-native), the cost writers in
    // app/core/ai/{claude,openai_chat,gemini_chat}.py, and the two paths
    // that write no row: app/core/ai/embeddings.py and
    // app/services/meeting_transcription.py. The previous copy — CSV export,
    // per-user filters, a usage webhook, an Intelligence API /usage endpoint
    // — described nothing in the codebase.
    slug: "cost-transparency",
    title: "Cost transparency",
    oneLine: "A usage view for the current month, broken down by feature and by model, with a 30-day daily series.",
    long: [
      "Every model call that is priced per token — Anthropic on every path, OpenAI and Google on the multi-agent path — is written to a per-request log with input, output, cache-read and cache-creation tokens, the model that actually answered, and the user, matter and artefact it belongs to. Each row carries its cost computed from one US-dollar price list at the time of the call and never converted afterwards; the usage view shows the figure in your billing currency (US dollars on this product), and if any request in the period has no figure in that currency it says so rather than showing a partial sum. The usage endpoint reads the current calendar month and returns totals broken down by request type and by model, a 30-day daily series, storage, seats, and where you stand against your token allowance.",
      "Two things are outside the token meter, by their nature rather than by omission. Embeddings — every chunk of every uploaded contract — write no cost row. Meeting transcription is priced per minute of audio, not per token, so it is not in this log either; its record is the meeting itself, which carries the recorded duration and the transcription model used. And the log is richer than the view over it: user, matter and artefact are recorded per request but the endpoint does not yet group by them, so there is no per-user chargeback view.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Work normally", body: "Every per-token model call is logged automatically with its tokens, the model that answered, and its cost." },
      { title: "2. Read the month", body: "Totals by request type and by model, a 30-day daily series, storage and seats, and progress against your token allowance — in your billing currency." },
      { title: "3. Know the gaps", body: "Embeddings and meeting audio are outside this log, and there is no per-user breakdown yet." },
    ],
    capabilities: [
      "Per-request log — input, output, cache-read and cache-creation tokens, the model that actually answered, estimated cost",
      "Cost in US dollars on every row, from one price list, never converted at read time",
      "Anthropic, OpenAI and Google calls on the per-token paths all write a row",
      "User, matter and artefact recorded on each request",
      "Current-month totals by request type and by model, plus a 30-day daily series",
      "Storage, seats and token-allowance position in the same view",
      "A period with any unpriced request shows no total and says how many, rather than a partial sum",
      "No date, user or feature filters on the endpoint — it returns the current month",
      "Daily spend cap and AI audit history in AI Administration",
    ],
    faq: [
      { q: "Can I export to CSV?", a: "No. There is no CSV export on the usage endpoint and no outbound webhook carrying usage events." },
      { q: "Is there a usage API?", a: "Not as a separate product. Usage is read in the app, by a signed-in user." },
      { q: "Does the cost figure cover all AI spend?", a: "No. It covers every call priced per token. Embeddings are not logged, and meeting audio is priced per minute and recorded as duration and model on the meeting instead — so total model spend is higher than the figure shown, by those two amounts." },
    ],
  },

  {
    // Added 2026-10-08. Verified: services/ai_org_policy.py (ENFORCEMENT
    // table), services/ai_training.py, api/v1/admin/ai_admin.py (migration
    // 243). Custom training is gated on `custom_model_training`, off on every
    // global_* plan.
    slug: "ai-administration",
    title: "AI Administration",
    oneLine: "Lower the model ceiling, switch AI features off, cap daily spend, and read the AI audit history and security events.",
    long: [
      "An organisation can only lower what the platform would spend: set a model ceiling, switch off the second-opinion vendors used by Deep Audit, switch off individual AI features or request types, and set a daily spend cap in your billing currency. Each is checked before any model is called; a blocked request is refused with a reason, and a policy that fails to load does not stop work.",
      "AI audit history lists every AI request — type, user, model, time — with no content. AI security lists injection detections and blocked requests, storing a fingerprint, never the prompt. The page states which settings are enforced and which are only recorded: the client-data rule, redaction of personal identifiers, the human-review requirement and the policy statement are stored but not enforced. The prompt library stores prompts, which are not yet offered in Vio or drafting.",
    ],
    planNote: "The administration pages are on every plan. Custom training — which reads your documents into staged house-style preferences and trains no model — is gated on a separate feature, and " + NOT_ON_PLAN,
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Set the ceiling", body: "Choose the model ceiling and switch off any AI feature you do not want used." },
      { title: "2. Cap the spend", body: "Set a daily cap; calls past it are refused until the next UTC day." },
      { title: "3. Review", body: "Read the AI audit history and the security events." },
    ],
    capabilities: [
      "Model ceiling that only lowers the tier",
      "Switch off Deep Audit's OpenAI and Google readers",
      "Per-feature and per-request-type switches",
      "Daily spend cap in the organisation's billing currency",
      "AI audit history without content",
      "AI security events stored as fingerprints",
      "A stated table of what is enforced and what is only recorded",
      "Prompt library (not yet used by Vio or drafting)",
    ],
    faq: [
      { q: "Can we raise the model tier above the default?", a: "No. The policy can only lower the ceiling." },
      { q: "Does the cap cover speech transcription?", a: "No. Speech goes to a different vendor path and is not covered." },
    ],
  },
  {
    // Added 2026-10-08. contracts_evidence_packs_router, mounted bare.
    slug: "evidence-packs",
    title: "Evidence packs",
    oneLine: "Freeze a risk report and hash it, so you can prove later what the product said at the time.",
    long: [
      "An evidence pack is a frozen copy of a contract's risk report at one moment, with a hash over its contents. Later, a verify call re-hashes the stored payload and tells you whether it still matches — so a pack is a record you can stand behind rather than a screenshot.",
      "There is no way to edit or delete a pack, and that is the feature rather than an omission: a record you can change after the fact answers nothing. A corrected analysis is a new pack, and both remain. Freezing the same report twice is allowed on purpose — the second pack proves the first was not altered in between.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Freeze the report", body: "Create a pack from a contract's current risk report." },
      { title: "2. Keep the history", body: "Packs are listed newest first. Nothing overwrites an earlier one." },
      { title: "3. Verify when it matters", body: "A verify call re-hashes the stored payload and reports whether it still matches." },
    ],
    capabilities: [
      "Frozen risk-report snapshot with a content hash",
      "Verification that re-hashes and compares",
      "No edit and no delete — by design, not by omission",
      "Repeat freezes allowed, so a later pack can prove an earlier one is unchanged",
      "Sits alongside the append-only audit log, which is enforced by database triggers",
    ],
    faq: [
      { q: "Why can't I delete a pack?", a: "Because a record you can delete is not evidence. If an analysis was wrong, freeze the corrected one — both will be there." },
      { q: "Does this prove the analysis was right?", a: "No. It proves what the analysis said and that it has not been altered since. Those are different claims." },
    ],
  },
  {
    // Added 2026-10-08. ai_review_router, mounted bare; off by default.
    slug: "ai-review",
    title: "Human review of AI output",
    oneLine: "Hold AI work product above a risk threshold until a named person approves it — a gate at the point it leaves, not at the point it is generated.",
    long: [
      "Set a policy per kind of AI output: whether it needs review at all, and above what risk level. Anything crossing that line enters an approval queue and waits for a person to approve or reject it. The gate sits at egress — where the work product would go to a counterparty — rather than at generation, so ordinary internal use is not slowed down by it.",
      "Deciding is deliberately a high permission: approval requires delete on the role matrix, which the seeded roles give to owners and company admins only. The whole thing is off by default.",
    ],
    group: "ai-controls",
    status: "live",
    how: [
      { title: "1. Set the policy", body: "Per artefact kind: on or off, and the risk floor above which review is required. Default is off." },
      { title: "2. Work queues up", body: "Output crossing the threshold opens a review request and waits." },
      { title: "3. Approve or reject", body: "Owners and company admins decide. Managers and members cannot — that is the permission boundary doing its job." },
    ],
    capabilities: [
      "Per-artefact-kind review policy with a risk floor",
      "Approval queue with approve and reject decisions",
      "Deciding bound to delete on the role matrix — owners and company admins only",
      "Off by default, so it is a control you turn on rather than a workflow you inherit",
      "The gate is at egress, not at generation",
    ],
    faq: [
      { q: "Does it stop the AI from running?", a: "No. It stops the output from going out unreviewed. Generation is unaffected." },
      { q: "Can a manager approve?", a: "No, unless you give a custom role delete on the relevant capability. The seeded Manager and Member roles cannot approve AI output." },
    ],
  },

  // ── Platform & Deployment ─────────────────────────────────────
  {
    // Renamed from `india-residency` 2026-09-05 (old path redirects in
    // next.config.ts). 2026-10-08: "encrypted backups replicated to a
    // secondary region", "region pinning on enterprise plans" and
    // "contractual data-localisation guarantees" removed — /legal/privacy
    // says "There is no per-organisation region routing", and the DPA says
    // backups are a per-deployment job written to ap-south-1.
    slug: "data-residency",
    title: "Data residency",
    oneLine: "Where your data is stored, stated plainly: AWS ap-south-1 (Mumbai, India), with the transfer terms in the DPA.",
    long: [
      "Uploaded documents, data exports and database backups are stored in AWS ap-south-1 (Mumbai, India). Document, prompt and meeting-audio content is also transmitted to the AI sub-processors listed in the DPA — located in the United States — to perform the analysis you request. There is no per-organisation region routing on the hosted service.",
      "India holds no EU or UK adequacy decision, so transfers from the EEA, the UK and Switzerland are made under the safeguards the DPA sets out. If your sector or jurisdiction requires data to stay on your own infrastructure, the self-hosted deployment runs where you choose and an air-gapped install disables every external model vendor.",
    ],
    group: "platform",
    status: "live",
    how: [
      { title: "1. Sign up", body: "The hosted service stores data in AWS ap-south-1 (Mumbai, India). No configuration needed — and none available." },
      { title: "2. Read the transfer terms", body: "The DPA names every sub-processor that receives content, where it is, and the safeguards for EEA, UK and Swiss data." },
      { title: "3. Or run it yourself", body: "The self-hosted deployment keeps data on your infrastructure; air-gapped mode reaches no external vendor." },
    ],
    capabilities: [
      "Documents, exports and backups stored in AWS ap-south-1 (Mumbai, India)",
      "Sub-processors that receive content named in the DPA, with their locations",
      "No adequacy claimed — transfer safeguards set out in the DPA",
      "Self-hosted and air-gapped deployment for data that must stay on your infrastructure",
    ],
    faq: [
      { q: "Can my organisation's data be kept in the EU or the US?", a: "Not on the hosted service — there is no per-organisation region routing today. A self-hosted deployment runs in the region you choose." },
      { q: "Does this meet my sector's data-localisation requirements?", a: "Confirm your specific obligations with your compliance counsel against the facts above and the DPA. We do not claim compliance with a requirement we have not assessed." },
    ],
  },
  {
    // Renamed from `gst-compliant-billing` 2026-09-05, same reason as
    // `data-residency` above: the copy was already correct for this product
    // (a commercial invoice in USD, no GST fields — `CommercialInvoice`, not
    // `GSTInvoice`), while the URL still advertised an Indian tax document.
    // Old path redirects (next.config.ts).
    slug: "billing-invoicing",
    title: "Billing & invoicing",
    oneLine: "A downloadable invoice for your subscription, in USD, every billing cycle.",
    long: [
      "Every billing cycle produces an invoice with sequential numbering, your billing details, and the charge breakdown, priced in USD.",
      "Invoices are retained for the life of your account and exportable on demand.",
    ],
    group: "platform",
    status: "live",
    how: [
      { title: "1. Add your billing details", body: "Company name, billing address, and tax ID if applicable." },
      { title: "2. Auto-invoice every cycle", body: "Sequential numbering, emailed automatically each billing cycle." },
      { title: "3. Download anytime", body: "All invoices live in Billing → Invoices. PDF export with one click." },
    ],
    capabilities: [
      "Sequential invoice numbering",
      "USD billing",
      "Emailed automatically each cycle",
      "PDF export on demand",
    ],
    faq: [
      { q: "Can I request a credit note for a partial cancellation?", a: "Yes — partial-period cancellations issue a credit note with the corresponding refund." },
    ],
  },
  {
    // Withheld 2026-10-08. What the API serves is Indian legal data —
    // judgment search, statute search, clause dispute-rate benchmarks and
    // outcome distributions computed from the Indian judgment corpus — and
    // `intelligence_api` is off on every `global_*` plan. Neither the data
    // nor the key is something a global customer can buy.
    slug: "intelligence-api",
    market: "IN",
    title: "Intelligence API",
    oneLine: "A read-only Indian legal-data API — clause benchmarks, judgment search, statute search — metered per key.",
    long: [
      "Four data endpoints behind a per-organisation API key: clause dispute-rate benchmarks with a Wilson confidence interval and sample size, outcome distributions by clause type and court, judgment search, and statute search. Plus a key-status endpoint reporting your monthly call quota and how much of it you have used.",
      "It is a data product, not a remote control for the app. There is no endpoint that scans a document, runs a Nexus query, reads your audit log or touches billing — the page used to say there was, and there is not. Alongside the data endpoints sits webhook management: create, read, update, delete a subscription, read its delivery log, and rotate its signing secret.",
    ],
    planNote: "The Intelligence API is built and the endpoints respond, but " + NOT_ON_PLAN + " In particular it is not included on Business, which this page previously claimed.",
    group: "platform",
    status: "live",
    how: [
      { title: "1. Get an API key", body: "Minted for your organisation by an administrator. The secret is shown once." },
      { title: "2. Call the API", body: "Standard REST, bearer auth, JSON out. Judgment and statute search take a query with cursor pagination." },
      { title: "3. Watch your quota", body: "The key-status endpoint reports your monthly call limit, calls used and calls remaining." },
    ],
    capabilities: [
      "Clause benchmark — dispute rate with a Wilson confidence interval and sample size, per court or aggregated",
      "Clause outcome — outcome distribution and average duration for a clause type and court",
      "Judgment search with cursor pagination and truncated headnotes, or full headnotes on request",
      "Statute search over section titles and text with excerpts",
      "Key status — monthly call limit, used and remaining",
      "Webhook subscription management with a delivery log and secret rotation",
      "Monthly call quota per key, enforced race-safely, returning too-many-requests when exhausted",
    ],
    faq: [
      { q: "Can I submit a contract for scanning through the API?", a: "No. There is no scan endpoint, and no search, audit or billing endpoint either. It exposes the legal-data corpus, not the application." },
      { q: "Are there per-key scopes?", a: "No. Keys carry no scope or permission field — every key can call every endpoint. If scoped keys matter to you, say so; it is not there." },
      { q: "Is it rate-limited?", a: "It is quota-limited rather than rate-limited: a monthly call allowance by tier, not a requests-per-second ceiling." },
      { q: "Is there an OpenAPI spec?", a: "Not served in production — the schema endpoints are disabled there. Ask us for the spec and we will send it." },
      { q: "Is there a usage endpoint?", a: "No. The key-status endpoint gives quota counters; there is no per-endpoint usage breakdown." },
    ],
  },
  {
    slug: "browser-extension",
    title: "Browser extension",
    oneLine: "Right-click any PDF or Gmail attachment → \"Scan with LexVio\".",
    long: [
      "A Chrome / Edge / Firefox extension that adds 'Scan with LexVio' to right-click menus on any PDF, Gmail attachment, or selected text. Pipes the content into your LexVio scanner without leaving the browser.",
      "Useful when documents arrive over email and you want a quick risk read before opening the conversation with the sender.",
    ],
    group: "platform",
    status: "soon",
    how: [
      { title: "1. Install the extension", body: "Chrome / Edge / Firefox web store." },
      { title: "2. Right-click anywhere", body: "PDFs, Gmail attachments, selected text on any webpage." },
      { title: "3. Pick \"Scan with LexVio\"", body: "Opens in your LexVio scanner with the content pre-loaded." },
    ],
    capabilities: [
      "Right-click integration on PDFs, Gmail, selected text",
      "Chrome + Edge + Firefox",
      "Authenticated against your LexVio session",
      "Optional auto-vault on every scan",
    ],
    faq: [
      { q: "Does the extension upload my files to LexVio's servers?", a: "Only when you explicitly trigger 'Scan with LexVio'. The extension is otherwise inert — it doesn't read or transmit anything in the background." },
    ],
  },
  {
    slug: "on-prem",
    title: "On-prem deployment",
    oneLine: "Air-gapped install via Helm chart. No outbound telemetry when AIR_GAPPED=true.",
    planNote:
      "On-premise is not part of any published plan and cannot be self-served. " +
      "None of Starter, Pro, Business or Enterprise switches it on; it is enabled " +
      "for your organisation under a written agreement covering licensing, support " +
      "and updates. Talk to us before you plan around it.",
    long: [
      "For organisations that cannot use SaaS — government, defence, regulated finance — LexVio ships as a Helm chart that runs inside your Kubernetes, with the API, the worker pool and the virus scanner as deployments. Licensing is by RS256-signed heartbeat, and the signature is verified before any database lookup.",
      "With air-gapped mode on, and it is on by default in the chart, the outbound paths are closed by hard gates in the application code, not by configuration convention: Anthropic, OpenAI, Google, the speech vendor, the document parser, the jurisdiction verification adapters and error monitoring each refuse to initialise. Worth being precise about the consequence — those gates mean no model inference at all, not model inference served locally.",
    ],
    group: "platform",
    status: "live",
    how: [
      { title: "1. Sign enterprise contract", body: "On-prem requires a written agreement covering licensing, support, and updates." },
      { title: "2. Helm install", body: "We provide the Helm chart and ship updates via signed releases. You install in your own Kubernetes." },
      { title: "3. Heartbeats for licensing", body: "Periodic RS256-signed heartbeats validate the licence. No customer data leaves." },
    ],
    capabilities: [
      "Helm chart with API, worker, virus-scanner, ingress, autoscaling and disruption-budget templates",
      "Air-gapped mode on by default, enforced by refusal in the application code at each outbound vendor",
      "RS256-signed licence heartbeats, signature verified before any lookup",
      "A backup schedule with defined daily, weekly and monthly retention in the chart",
      "Deployment, onboarding and signed updates handled by our engineering team under contract",
    ],
    faq: [
      { q: "Can we point it at our own self-hosted LLM?", a: "Not today. The Anthropic client is constructed without a configurable base URL and there is no OpenAI-compatible endpoint setting, so there is nothing to route to a local model server. The chart carries a disabled block for a local inference server that nothing in the application talks to. Do not plan on this until we tell you it is done." },
      { q: "What does air-gapped actually mean here?", a: "That every outbound integration refuses to start — model vendors, speech, document parsing, jurisdiction verification and error monitoring. It is enforced in code rather than by network policy, and the chart does not currently ship an egress network policy, so belt-and-braces network controls are yours to add." },
      { q: "Does the product still work air-gapped?", a: "The parts that do not need a model do. Anything requiring inference does not, because there is no local model path yet. That is the trade air-gapping makes today." },
    ],
  },
  {
    // Rewritten 2026-09-18 against app/api/v1/voice.py: one endpoint,
    // `POST /voice/transcribe` (25 MB, language en | hi), plus a session
    // list. The previous copy — voice queries to Vio, synthesised audio,
    // noise tolerance — described nothing in the codebase; there is no
    // text-to-speech anywhere in the product.
    slug: "voice",
    title: "Voice transcription",
    oneLine: "Dictate instead of typing — audio in, punctuated English text out. It transcribes; it does not answer.",
    long: [
      "Send an audio recording and get back a formatted, punctuated transcript in English — the dictation language this product accepts. Useful for dictating a question, a note on a contract, or a file note without typing it. Transcription sessions are kept so you can go back to what you dictated.",
      "This is speech to text and nothing more. It does not send the transcript to the AI, it does not answer your question, and there is no synthesised speech coming back — there is no text-to-speech anywhere in the product. Paste the transcript into Vio to get an answer. It is also not Meeting Intelligence: this is a dictation of one voice into a text box, while a meeting is a record — chunked live transcription, inferred speaker labels, minutes, tasks, an export, a place on the matter timeline.",
    ],
    group: "platform",
    status: "beta",
    how: [
      { title: "1. Record", body: "Upload an audio file, up to 25 MB." },
      { title: "2. Speak in English", body: "Dictation on this product is English; leave the language unset and the vendor detects it." },
      { title: "3. Get the transcript", body: "Punctuated and formatted text, with the session kept in your history." },
    ],
    capabilities: [
      "Audio-to-text with automatic punctuation and formatting",
      "English dictation",
      "25 MB per file",
      "Transcription sessions listed in your history; the audio itself is not kept, only the transcript",
      "A microphone in the phone's text boxes, and in New Contract, where speech becomes a drafting brief",
      "Disabled outright in air-gapped deployments, like every other outbound vendor",
    ],
    faq: [
      { q: "Does it answer my question out loud?", a: "No. It returns text. There is no speech synthesis in the product and no automatic hand-off from a transcript to the assistant — paste the transcript into Vio yourself." },
      { q: "Is there a model tuned to my accent?", a: "No. There is one general English speech model, so heavily accented English will transcribe as well as that model manages, and no better." },
      { q: "Are transcripts stored?", a: "Yes, as sessions in your account, under the same retention and deletion terms as your other content." },
      { q: "Should I use this to record a meeting?", a: "No — use Meeting Intelligence. Voice transcription takes one file of up to 25 MB and gives you text. A meeting is recorded in chunks as it happens or uploaded as a file of up to 300 MB, gets speaker labels (inferred, never identified), minutes, action items that become tasks, a PDF or Word export, and a place on the matter's timeline. None of that exists here." },
    ],
  },

  {
    // Added 2026-10-08. Verified: services/channel_configs.py,
    // services/channel_adapters.py (migration 244). Twilio is the SMS provider
    // for a global org (MSG91 + DLT is India-only, and DLT fields from a
    // global org are a 422). Only email changes what the product sends; SMS
    // and WhatsApp are configuration and a test send — no feature sends them.
    slug: "channel-integrations",
    title: "Email, SMS & WhatsApp settings",
    oneLine: "Use your organisation's own email sender or SMTP relay for the product's emails — and store Twilio SMS and WhatsApp Business settings, tested from the console.",
    long: [
      "An admin can configure the organisation's own email (sender name, reply-to, or your own SMTP relay), SMS through Twilio, and WhatsApp through Meta with templates submitted and synced from the console. Secrets are encrypted when saved and never shown again — only that one is set, and the last four characters of an API key.",
      "Be clear about what saving does today. An enabled email configuration changes every email LexVio sends for your organisation, falling back to LexVio's sender if your relay fails. The SMS and WhatsApp configurations can be tested from the console, but no product feature sends through them yet. Because the credentials are yours, the vendor is your processor, not ours.",
    ],
    group: "platform",
    status: "beta",
    how: [
      { title: "1. Configure", body: "Pick the channel and enter the provider details." },
      { title: "2. Test", body: "Send a test message from the console." },
      { title: "3. Email goes live", body: "An enabled email configuration takes over your organisation's emails; SMS and WhatsApp wait for features that use them." },
    ],
    capabilities: [
      "Own email sender, reply-to or SMTP relay, with fallback to LexVio's sender",
      "Twilio SMS settings, with a test send",
      "WhatsApp Business through Meta, with template sync and a test send",
      "Secrets encrypted and write-only",
      "Audit names changed fields, never values",
      "An SMTP host on a private or loopback address is refused on the hosted service",
    ],
    faq: [
      { q: "Will reminders go out by SMS or WhatsApp once I connect them?", a: "Not yet. Saving the configuration does not switch on SMS or WhatsApp sending; only the test send uses it today." },
      { q: "Can the SMTP host be on a private network?", a: "Only on an air-gapped installation, where it is your own mail server." },
    ],
  },
  {
    // Added 2026-10-08. app/seeds/release_notes.py; notes are edition-scoped
    // (`any · india · global`), so a global org never sees an India note.
    slug: "whats-new",
    title: "What's New",
    oneLine: "Release notes inside the product, written for this product, with unread counted from your last visit.",
    long: [
      "What's New lists the product's release notes. Notes are scoped per product, so what you see applies to the product you use. Unread is counted from your last visit — or from when you joined, so a new colleague is not greeted with every note since launch.",
      "Product documentation and help sit beside it.",
    ],
    group: "platform",
    status: "live",
    how: [
      { title: "1. Open Help", body: "What's New sits under help and resources on the desk." },
      { title: "2. Read", body: "Notes for this product, newest first." },
      { title: "3. Caught up", body: "The unread count resets from your visit." },
    ],
    capabilities: [
      "Release notes scoped to this product",
      "Unread counted from your last visit or your joining date",
      "Product documentation beside it",
    ],
    faq: [
      { q: "Is this the same as the public changelog?", a: "They cover the same releases; What's New is the in-product view." },
    ],
  },
  {
    // Verified 2026-09-18 against app/api/v1/blog/{me,images,media}.py,
    // app/api/v1/admin/blog.py (publish / reject / unpublish, author veto),
    // app/db/models/blog_author.py (is_visible AND NOT admin_hidden) and
    // docs/BLOG_COMMUNITY_SPEC_2026-09-02.md. Mounted bare on both surfaces
    // with no plan gate — tests/test_blog_open_to_every_plan.py pins it.
    // The public feed is edition-keyed (the phone reads editions.edition_of),
    // so a global author is published to this site, never the other one.
    slug: "community-blog",
    title: "Community blog",
    oneLine: "Every account can write for the LexVio blog — draft in the app, an editor reviews, and the byline is yours to show or hide.",
    long: [
      "Anyone with a LexVio account can write for the blog: on a trial, on an expired trial, in any role, from the desk or the phone. There is no plan gate in front of it. You write in Markdown, preview exactly the HTML readers will get, save drafts, and submit when it is ready. An editor reviews it and publishes it to the edition's site, or sends it back with a note you can act on. A submitted post can be withdrawn while it is waiting.",
      "Your byline and author profile — name, headline, firm, bio, photo, links — are yours. A visibility switch, on by default, decides whether the profile is shown; the editor holds a separate veto, and the veto wins. Images are uploaded through the app and served by the API from private storage — the bucket is never public, so an image URL is an address we control, not a bucket address. Published posts appear at /blog on this site.",
    ],
    group: "platform",
    status: "live",
    how: [
      { title: "1. Write", body: "Markdown, with a preview that renders through the same sanitiser the public page uses. Upload images from the editor. Save as many drafts as you like." },
      { title: "2. Submit", body: "Submit locks the post while an editor reads it. Withdraw takes it back to a draft. A rejected post comes back with the editor's note and can be edited and resubmitted." },
      { title: "3. Published", body: "The editor states the edition it is published to and it appears on that site's /blog, under your byline if your profile is visible. You are notified either way." },
    ],
    capabilities: [
      "Open to every account — trial, expired trial, no plan, any role, both editions, desk or phone",
      "Markdown drafts with a preview that renders exactly what readers get",
      "Submit, withdraw while pending, edit and resubmit after a rejection with the editor's note",
      "An editor reviews and publishes to the edition's site; nothing self-publishes",
      "Author profile with a visibility switch, on by default, and an editor's veto that wins",
      "Images uploaded through the app and served by the API; the storage bucket stays private",
      "A public author page once you have a published post in that edition",
    ],
    faq: [
      { q: "Can I publish directly?", a: "No. Submit is as far as an author can push a post; publication is an editor's action. That is deliberate — the blog carries the product's name." },
      { q: "Can I hide my name?", a: "Yes. Turn the profile switch off and the byline and author page are withheld. An editor can also hide a profile, and the editor's setting wins." },
      { q: "Does it cost anything or need a plan?", a: "No. Writing for the blog is open to every account; an expired trial can still draft, submit and be published." },
    ],
  },
  {
    // Status is `soon` ON PURPOSE. The app exists (E:\LexAI\dev\mobile-app,
    // Expo, `Lexvio`, ai.lexvio.app) and an Android build is with testers,
    // but it is on neither store and there is no iOS build in anyone's
    // hands. Everything below is verified against the app's own docs
    // (docs/13-SOON-BUILD-2026-09-14.md, docs/15-APP-UPDATES-AND-VERSIONS.md)
    // and source (src/api/auth.ts, src/auth/BiometricGate.tsx,
    // src/edition/manifest.ts PROFESSION_ORDER, src/offline/db.ts,
    // src/upload/capture.ts, src/push/register.ts, src/update/) plus the
    // backend's app/services/mobile_app_versions.py. No store URLs: the
    // listings do not exist. Flip to `live` when a store link is real.
    // One app serves both editions and resolves the edition after sign-in,
    // so this entry carries no `market` tag.
    slug: "mobile-app",
    title: "Lexvio mobile app",
    oneLine: "One app for both products on Android and iOS. Not on either store yet — an Android build is with testers.",
    long: [
      "Lexvio is one app for both editions: you sign in, and the product you see is decided by your organisation, not by which app you downloaded. Sign-in is a one-time code sent to your email. The home screen is a grid of tiles ordered by your profession, and since the September 2026 build every surface on the desk has a screen on the phone — the ones that make more sense at a desk say so on screen rather than dead-ending. Record a meeting from the phone's microphone, work your task list, open a matter, read your vault, photograph a contract and send it for analysis, ask Vio.",
      "It is not on the Play Store or the App Store yet. An Android build is with testers; no iOS build has been distributed, and there is no release date we would put in writing. When it ships, updates are governed by the server: an old build is nudged to update, given a grace period, and eventually blocked — and a required update never signs you out, because the sign-in and refresh paths stay open through the block.",
    ],
    group: "platform",
    status: "soon",
    how: [
      { title: "1. Sign in with a code", body: "Enter your email, type the code it sends you. Sign-up, organisation creation and plan choice stay on the web. A biometric lock can guard the app after that; it is an entry convenience, not the credential." },
      { title: "2. Your home, in your order", body: "The tile order comes from your profession. The menu is served by the server, so what is locked, not part of your edition, or not built yet is stated, never guessed." },
      { title: "3. Work from anywhere", body: "The vault list is kept on the phone so it opens offline — the list, not the files. Push notifications for tasks and meetings open the screen they are about." },
    ],
    capabilities: [
      "One codebase for Android and iOS; one app for both editions, resolved after sign-in",
      "Sign-in by email code; a biometric lock on top",
      "Home grid ordered by profession",
      "Every desk surface has a phone screen — the 2026-09-14 build",
      "Record meetings and read the transcript and minutes as they arrive",
      "Tasks, matters, the vault (list kept offline), Vio",
      "Photograph a contract or pick a file and send it through the same accept list as the desk",
      "Push notifications that open the task or meeting they are about",
      "Server-driven update gate: nudge, required-by-date, then block; a required update never signs you out; offline never blocks",
    ],
    faq: [
      { q: "Is it on the Play Store or App Store?", a: "Not yet. An Android build is being tested by hand; the iOS listing does not exist. We are not going to put a date here we cannot keep — this page will say Live when a store link is real." },
      { q: "Can I sign up from the phone?", a: "No. The app starts at the sign-in screen. Sign-up, organisation creation and plan selection stay on the web, because the edition of a new organisation is stamped from the site it signs up on." },
      { q: "Does it work offline?", a: "Partly. The vault list is stored on the phone and reads offline; the documents themselves, analysis and Vio need a connection. A failed update check never blocks the app." },
    ],
  },

  // ── Free Tools ────────────────────────────────────────────────
  {
    slug: "public-scanner",
    title: "Public scanner",
    oneLine: "Paste a contract and get a risk score and the top risks, with no account. Three scans a day and 25 a month per address.",
    long: [
      "The scanner is open to anyone. Paste contract text between 100 and 30,000 characters and you get a risk score, the top three risky clauses and a summary. It runs the real scoring and grounding layers — the same clause-grounding pass that withholds any quoted clause the model could not actually find in your text, and the same overall scoring algorithm — so it is a genuine sample rather than a demo mock-up.",
      "It is a cut-down version, deliberately. It uses a faster, cheaper model than the signed-in product, returns three clauses rather than all of them, and strips the suggested fixes. And it keeps a record: the free scan is stored with your IP address and the resulting risk report, which is how we keep it from being abused. Your document text is not stored — only a hash of it, used to cache repeat scans.",
    ],
    group: "free-tools",
    status: "live",
    how: [
      { title: "1. Open the scanner", body: "No login. Paste contract text — 100 to 30,000 characters." },
      { title: "2. Analyse", body: "Real scoring and grounding, on a faster model than the signed-in product uses." },
      { title: "3. Read the top risks", body: "Risk score, top three clauses, summary. Sign up for the whole report with the suggested fixes." },
    ],
    capabilities: [
      "No sign-up required",
      "Three scans a day and 25 a month per address",
      "Real risk scoring and clause grounding — quoted clauses that are not in your text are withheld, not shown",
      "Top three risky clauses, with suggested fixes reserved for signed-in use",
      "A faster, cheaper model than the signed-in product",
      "Your document text is not stored — only a hash, used to cache repeat scans",
    ],
    faq: [
      { q: "Is my contract stored?", a: "Not the text. The scan is recorded with your IP address, a hash of the text and the risk report that came back, which is how the free tier is kept from being abused, and the result is cached against that hash for an hour. It is not linked to any account, because there is not one — but \"not stored\" would be the wrong word and we are not going to use it." },
      { q: "Is it the same analysis I get after signing up?", a: "No. Same scoring and grounding layers, smaller model, three clauses instead of the full set, and no suggested fixes. Treat it as a real sample of the method, not the full product." },
      { q: "Can I share the result?", a: "Sharing a scan result requires an account — an anonymous scan cannot generate its own share link." },
    ],
  },
  {
    slug: "nda-generator-tool",
    title: "NDA generator",
    market: "IN",
    oneLine: "Mutual / one-way / employment / vendor / IP-heavy templates. Print to PDF.",
    long: [
      "A free NDA generator with five Indian-law-aligned templates. Pick a variant, fill the parties, choose the term, and export. The generator runs entirely in the browser — nothing is uploaded.",
      "Outputs: copy to clipboard, download .txt, or print to PDF.",
    ],
    group: "free-tools",
    status: "live",
    how: [
      { title: "1. Pick a template", body: "Mutual, one-way, employment, vendor, IP-heavy." },
      { title: "2. Fill the parties + term", body: "Inputs are in-browser only — nothing leaves your machine." },
      { title: "3. Export", body: "Copy, download .txt, or print to PDF." },
    ],
    capabilities: [
      "5 template variants",
      "All client-side — no uploads",
      "Copy / download / print outputs",
      "Indian Contract Act + Arbitration Act 1996 aligned",
    ],
    faq: [
      { q: "Is the output legal-grade?", a: "It's a starting-point template. For high-stakes deals, have an advocate review before signing." },
    ],
  },
  {
    slug: "gst-calculator-tool",
    title: "GST calculator",
    market: "IN",
    oneLine: "Add / remove GST, with intra-state CGST+SGST or inter-state IGST split.",
    long: [
      "Compute GST forward (base → gross) or reverse (gross → base) across all standard rates. Picks CGST+SGST split for intra-state supply and IGST for inter-state automatically.",
      "Includes a reference card of common Indian GST rates by goods/services category.",
    ],
    group: "free-tools",
    status: "live",
    how: [
      { title: "1. Pick direction", body: "Add GST or strip it out." },
      { title: "2. Enter base + rate + states", body: "Seller and buyer state drive the intra-state vs inter-state determination." },
      { title: "3. See the breakdown", body: "Net, CGST/SGST or IGST, total — split out per CGST Act §9 / IGST Act §5." },
    ],
    capabilities: [
      "Forward + reverse computation",
      "All 7 standard rates",
      "Auto CGST+SGST vs IGST switch",
      "Reference card of common rates",
    ],
    faq: [
      { q: "Does it handle composite or mixed supply?", a: "Not in the free tool — those require AI reasoning. Tax AI handles them." },
    ],
  },
  {
    slug: "tds-rate-finder-tool",
    title: "TDS rate finder",
    market: "IN",
    oneLine: "Section-wise TDS rates for FY 2025-26 across 30+ payment types.",
    long: [
      "Searchable table of TDS rates by section — salary, rent, professional fees, contractors, commission, crypto, and more. Filter by payee type (resident / non-resident / company).",
      "Includes threshold values, individual and company rates, and important notes on §206AA / §206AB higher-rate scenarios.",
    ],
    group: "free-tools",
    status: "live",
    how: [
      { title: "1. Search by section or keyword", body: "\"194J\", \"rent\", \"crypto\" — any term matches." },
      { title: "2. Filter by payee", body: "Resident, non-resident, or company." },
      { title: "3. See the rate", body: "Section, threshold, individual rate, company rate, applicable notes." },
    ],
    capabilities: [
      "32 sections covering all common payments",
      "Threshold + rate columns",
      "Resident vs non-resident filter",
      "§206AA + §206AB higher-rate notes",
    ],
    faq: [
      { q: "Is this current for FY 2025-26?", a: "Yes — current for assessment year 2026-27 / financial year 2025-26 as of last update." },
    ],
  },
  {
    slug: "stamp-duty-calculator-tool",
    title: "Stamp duty calculator",
    market: "IN",
    oneLine: "State-wise duty for sale deeds, gift deeds, leases, and loan agreements.",
    long: [
      "Indicative stamp duty across 22 Indian states for the most common document types. Female-buyer rebate applied automatically where the state offers one.",
      "Indicative only — always confirm the final number with your state's Registration & Stamps Department.",
    ],
    group: "free-tools",
    status: "live",
    how: [
      { title: "1. Pick state + document", body: "22 states; sale deed / gift deed / lease / loan / rent / LOA." },
      { title: "2. Enter consideration", body: "Market value or transaction value." },
      { title: "3. See the duty", body: "Indicative duty + applicable rate. Female-buyer rebate applied where applicable." },
    ],
    capabilities: [
      "22 state coverage",
      "6 document types per state",
      "Female-buyer rebate auto-applied",
      "Inline notes on family-member exceptions",
    ],
    faq: [
      { q: "Why \"indicative\"?", a: "Stamp duty changes via state notifications more often than tax law. Always confirm with your sub-registrar before execution." },
    ],
  },
  {
    slug: "hsn-sac-finder-tool",
    title: "HSN / SAC finder",
    market: "IN",
    oneLine: "Search GST codes for goods and services, with rate.",
    long: [
      "Curated index of the most-used HSN (goods) and SAC (services) codes across IT, F&B, retail, professional services, and more. Each entry shows the GST rate and category.",
      "Use for invoicing, e-invoicing schema validation, and rate verification.",
    ],
    group: "free-tools",
    status: "live",
    how: [
      { title: "1. Search by product or service", body: "\"software\", \"restaurant\", \"laptop\", \"legal services\" — natural-language search." },
      { title: "2. Filter by HSN / SAC / category", body: "Narrow down to goods vs services or a specific category." },
      { title: "3. See the code + rate", body: "Code, description, category, applicable GST rate." },
    ],
    capabilities: [
      "Curated catalogue of ~47 most-used codes",
      "HSN vs SAC filter",
      "Category filter (IT, F&B, Real Estate, etc.)",
      "Rate column for quick reference",
    ],
    faq: [
      { q: "Is this the full HSN/SAC corpus?", a: "No — the full corpus is 12,000+ entries. We curate the most-used ones for the free tool. Tax AI handles the full corpus and composite-supply edge cases." },
    ],
  },
  {
    slug: "gstin-validator-tool",
    title: "GSTIN validator",
    market: "IN",
    oneLine: "Validate any GSTIN's checksum + decode state and entity type.",
    long: [
      "Implements the official 36-base GSTIN checksum (CGST Notification 12/2017). Decodes the state code, embedded PAN, and entity type from any well-formed GSTIN.",
      "All client-side — your input never leaves the browser. Sample GSTINs included for testing.",
    ],
    group: "free-tools",
    status: "live",
    how: [
      { title: "1. Paste a GSTIN", body: "15 characters. Automatically uppercased." },
      { title: "2. Format check", body: "Validates the format against the official pattern: 2-digit state + 10-char PAN + entity + Z + checksum." },
      { title: "3. Checksum verify + decode", body: "Computes the expected checksum character; decodes state and entity type if format is valid." },
    ],
    capabilities: [
      "Official 36-base checksum implementation",
      "State code decode (all 38 codes)",
      "Entity type decode (10 entity codes)",
      "Sample GSTINs for testing",
      "Runs entirely client-side",
    ],
    faq: [
      { q: "Does this check if a GSTIN is currently active?", a: "No — only format + checksum. For live status (active / suspended / cancelled), use the official GST portal at gst.gov.in." },
    ],
  },
];

/**
 * What this site may show: the catalogue minus everything scoped to one
 * jurisdiction's law.
 *
 * Default-deny on purpose. The predicate is "carries no market tag", not
 * "carries a tag other than IN", so a future `market: "US"` is withheld too —
 * this site sells into many jurisdictions and a capability that exists in one
 * of them needs a deliberate decision about how to say so, not an implicit
 * pass. Widening it is a product decision; narrowing it is free.
 */
export const FEATURES: ReadonlyArray<Feature> = ALL_FEATURES.filter(
  (f) => f.market === undefined,
);

/**
 * Resolve a slug — deliberately over `FEATURES`, not `ALL_FEATURES`.
 *
 * A withheld feature therefore returns undefined, and `/features/[slug]`
 * turns that into `notFound()`. That is the same answer the backend gives
 * (`require_india_edition` is 404, not 403: the surface does not exist for
 * this product) and the same answer `/library` and `/glossary` now give in
 * `middleware.ts`.
 */
export function featureBySlug(slug: string): Feature | undefined {
  return FEATURES.find((f) => f.slug === slug);
}

export function featuresByGroup(): Array<{ group: Group; features: ReadonlyArray<Feature> }> {
  return (Object.keys(GROUPS) as Group[]).map((g) => ({
    group: g,
    features: FEATURES.filter((f) => f.group === g),
  }));
}

export function relatedFeatures(slug: string, limit = 4): ReadonlyArray<Feature> {
  const current = featureBySlug(slug);
  if (!current) return [];
  return FEATURES.filter((f) => f.group === current.group && f.slug !== slug).slice(0, limit);
}
